/*
  Interactive article tools — blade finder, teeth explorer, bit length finder, drill speed
  calculator, band saw blade finder, SDS shank chooser, versus card, hole explorer, speed fit chart, and the four
  Silver & Deming tools (bolt-to-bit finder, price ladder, speed dial, kit builder), the chart
  from table, and the three bell hanger tools (route planner, bit rail, plate check). See
  docs/blog/ARTICLE-TOOLS.md.

  Loaded by et-article.liquid only when article.content contains 'et-tool', and only mounts into
  <div class="et-tool" data-tool="<id>"></div> placeholders (lib/blog/article-tools.ts's id
  allowlist; lib/blog/sanitize-html.ts strips any other value before it ever reaches the theme).
  Every fact the tools use (TPI, blade length, pipe sizing, price-per-blade math) is expected to
  also live in a plain static table right next to the placeholder in the article body — rule T6,
  "interactive, but crawlable": if this script fails to load or throws, the placeholder is simply
  left empty (same graceful-degradation contract as the inline product card in et-article.liquid),
  and every fact the tool would have shown is still on the page as ordinary HTML.

  To add a new tool: add a `mount()` function below and register it in the TOOLS map at the
  bottom. Also add the id + label to lib/blog/article-tools.ts (the portal's sanitizer/editor
  allowlist) — a tool that isn't in both places either can't be inserted from the editor, or gets
  silently stripped on save.
*/
(function () {
  "use strict";

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function usd(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  var productCache = Object.create(null);
  /** Fetches /products/<handle>.js once per handle per page load; caches the promise (not just
   * the result) so concurrent callers for the same SKU share one request. Never throws — callers
   * always get either the parsed product or null. */
  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    var p = fetch("/products/" + handle + ".js", { headers: { accept: "application/json" } })
      .then(function (r) {
        if (!r.ok) throw new Error("product " + handle + " " + r.status);
        return r.json();
      })
      .catch(function (e) {
        console.warn("[et-article-tools] product fetch failed for", handle, e);
        return null;
      });
    productCache[handle] = p;
    return p;
  }

  /**
   * Per-blade price for one SKU, derived from the live product JSON rather than a hardcoded
   * number (task requirement — prices drift, specs don't).
   *
   * Most recip-blade variants sell the whole pack at one price (quantity_rule.min is 1 or
   * absent) — e.g. CT9 sells as one $109.22 variant covering a 5-blade pack ("9\" … (5 pack)" —
   * the 9 is the length), verified live 2026-09-27 against /products/ct9.js → $21.84 each. Divide by
   * the documented pack size for the per-blade price.
   *
   * Some product lines have moved to "unit price + minimum order quantity" instead (see
   * memory: pack-migration-is-unit-true-with-min-qty.md) — quantity_rule.min > 1 there. In that
   * shape `variant.price` is already the PER-UNIT price, so it must NOT be divided by the spec's
   * pack size a second time; dividing again would silently understate the price by ~5-25x.
   * quantity_rule.min is the live, authoritative signal for which shape a given SKU is in today,
   * so this checks it every time rather than assuming from the spec table alone.
   *
   * Returns null (never throws) when the product has no usable variant/price — callers hide the
   * price for that pick and keep the SKU/link, per the graceful-fallback requirement.
   */
  function perBladePrice(product, specPack) {
    if (!product || !product.variants || !product.variants[0]) return null;
    var v = product.variants[0];
    if (typeof v.price !== "number") return null;
    var rule = v.quantity_rule;
    var minQty = rule && typeof rule.min === "number" ? rule.min : 1;
    if (minQty > 1) {
      // Unit-priced with a minimum: v.price is already per unit (per blade).
      return { cents: v.price, label: usd(v.price) + " each (min " + minQty + ")", available: v.available };
    }
    var pack = specPack > 0 ? specPack : 1;
    var each = v.price / pack;
    return {
      cents: each,
      label: usd(v.price) + " / " + pack + " &middot; " + usd(each) + " each",
      available: v.available,
    };
  }

  // ---------------------------------------------------------------------
  // Blade finder
  // ---------------------------------------------------------------------

  // Static spec table: sku, family key, TPI ("10/14" for variable pitch), length (in.),
  // thickness (in.), pack size. No prices here — those come from fetchProduct() at render time.
  // Verified against the live catalog 2026-09-27 (34 of the 72 SKUs the source research covered;
  // add rows here as new blade SKUs ship — this list doesn't need to be exhaustive to be useful).
  var BLADE_SPEC = [
    ["W636", "wood", 6, 6, 0.05, 5],
    ["W956", "wood", 6, 9, 0.05, 5],
    ["W125", "wood", 6, 12, 0.05, 5],
    ["W656S", "curve", 6, 6, 0.05, 5],
    ["DM606", "demo", 6, 6, 0.062, 3],
    ["DM906", "demo", 6, 9, 0.062, 3],
    ["DM1206", "demo", 6, 12, 0.062, 3],
    ["DM610", "demo", 10, 6, 0.062, 3],
    ["DM910", "demo", 10, 9, 0.062, 3],
    ["DM1210", "demo", 10, 12, 0.062, 3],
    ["M610", "metal", 10, 6, 0.035, 5],
    ["M810", "metal", 10, 8, 0.035, 5],
    ["M1010", "metal", 10, 10, 0.035, 5],
    ["M414", "metal", 14, 4, 0.035, 5],
    ["M614", "metal", 14, 6, 0.035, 5],
    ["M814", "metal", 14, 8, 0.035, 5],
    ["M418", "metal", 18, 4, 0.035, 5],
    ["M618", "metal", 18, 6, 0.035, 5],
    ["M818", "metal", 18, 8, 0.035, 5],
    ["M1018", "metal", 18, 10, 0.035, 5],
    ["M1218", "metal", 18, 12, 0.035, 5],
    ["M424", "metal", 24, 4, 0.035, 5],
    ["M624", "metal", 24, 6, 0.035, 5],
    ["HM610", "hmetal", 10, 6, 0.042, 5],
    ["HM910", "hmetal", 10, 9, 0.042, 5],
    ["HM614", "hmetal", 14, 6, 0.042, 5],
    ["HM914", "hmetal", 14, 9, 0.042, 5],
    ["HM1214", "hmetal", 14, 12, 0.042, 5],
    ["HM618", "hmetal", 18, 6, 0.042, 5],
    ["HM918", "hmetal", 18, 9, 0.042, 5],
    ["HM1218", "hmetal", 18, 12, 0.042, 5],
    ["HD610", "hd", "10/14", 6, 0.05, 5],
    ["HD810", "hd", "10/14", 8, 0.05, 5],
    ["HD120", "hd", "10/14", 12, 0.05, 5],
    ["CT6", "carbide", 8, 6, 0.05, 5],
    ["CT9", "carbide", 8, 9, 0.05, 5],
  ].map(function (r) {
    return { sku: r[0], fam: r[1], tpi: r[2], len: r[3], th: r[4], pack: r[5] };
  });
  var FAMILY_LABEL = {
    wood: "Wood & Nail",
    curve: "Wood & Nail Scroll",
    demo: "Demolition",
    metal: "Metal",
    hmetal: "Heavy-Metal",
    hd: "Heavy-Duty variable pitch",
    carbide: "MaxMetal Carbide",
  };
  var PIPES = [
    ['1/2"', 0.84, 0.109],
    ['3/4"', 1.05, 0.113],
    ['1"', 1.315, 0.133],
    ['1-1/2"', 1.9, 0.145],
    ['2"', 2.375, 0.154],
    ['3"', 3.5, 0.216],
    ['4"', 4.5, 0.237],
    ['6"', 6.625, 0.28],
  ];
  var TPI_OPTIONS = [6, 8, 10, 14, 18, 24];
  var LENGTHS = [4, 6, 8, 9, 10, 12];
  var MATERIALS = [
    { k: "wood", label: "Wood", size: "depth", def: 3.5, fams: ["wood"], why: "6 TPI clears chips fast in wood. Wood & Nail teeth are bi-metal, so a hidden nail won't ruin the blade." },
    { k: "nails", label: "Wood with nails", size: "depth", def: 3.5, fams: ["demo"], tpi: 6, why: "Demolition blades are .062 in. thick, so they don't bend when you hit a nail or pry." },
    { k: "mixed", label: "Mixed demolition", size: "depth", def: 3.5, fams: ["demo"], tpi: 10, why: "10 TPI handles wood and metal in the same cut; the .062 in. body survives abuse." },
    { k: "pvc", label: "PVC / ABS pipe", size: "pipe", def: 4, fams: ["metal"], tpi: 10, why: "10 TPI cuts plastic fast with a cleaner edge than a wood blade." },
    { k: "sheet", label: "Sheet metal / conduit", size: "wall", def: 0.0625, fams: ["metal"], why: "Thin metal needs fine teeth so at least three stay in the cut." },
    { k: "pipe", label: "Steel pipe", size: "pipe", def: 4, fams: ["metal", "hmetal"], why: "TPI comes from the wall thickness; length comes from the outside diameter." },
    { k: "thick", label: "Solid bar / plate", size: "wall", def: 0.5, fams: ["hmetal", "metal"], why: "Thick steel needs coarser teeth so the gullets don't clog." },
    { k: "unknown", label: "Metal, not sure", size: "depth", def: 2, fams: ["hd"], why: "Variable 10/14 pitch covers a wide range of thicknesses with one blade." },
    { k: "cast", label: "Cast iron / rebar", size: "pipe", def: 4, fams: ["carbide"], why: "Carbide teeth outlast bi-metal in abrasive, hard material by a wide margin." },
  ];

  function minTpiFor(thickness) {
    return 3 / thickness;
  }
  function pickTpi(thickness) {
    var need = minTpiFor(thickness);
    for (var i = 0; i < TPI_OPTIONS.length; i++) {
      if (TPI_OPTIONS[i] >= need) return TPI_OPTIONS[i];
    }
    return 24;
  }

  function mountBladeFinder(root) {
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--blade-finder");
    root.innerHTML =
      '<div class="et-tool__finder">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">Blade finder</div>' +
      '<span class="et-tool__meta">' + BLADE_SPEC.length + " recip blade specs &middot; made in the USA by ITM</span></div>" +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-mat">What are you cutting?</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-mat" data-chips></div></div>' +
      '<div class="et-tool__row" data-size-row></div>' +
      '<div class="et-tool__result" aria-live="polite" data-result></div>' +
      "</div></div>";

    var chipsEl = root.querySelector("[data-chips]");
    var sizeRowEl = root.querySelector("[data-size-row]");
    var resultEl = root.querySelector("[data-result]");

    var state = { mat: MATERIALS.find(function (m) { return m.k === "cast"; }), pipeIdx: 6, wall: 0.5, depth: 3.5 };
    state.wall = state.mat.size === "wall" ? state.mat.def : 0.5;
    state.depth = state.mat.size === "depth" ? state.mat.def : 3.5;

    MATERIALS.forEach(function (m) {
      var b = document.createElement("button");
      b.type = "button";
      b.className = "et-tool__chip";
      b.textContent = m.label;
      b.setAttribute("aria-pressed", m === state.mat ? "true" : "false");
      b.addEventListener("click", function () {
        state.mat = m;
        if (m.size === "wall") state.wall = m.def;
        if (m.size === "depth") state.depth = m.def;
        render();
      });
      chipsEl.appendChild(b);
    });

    function render() {
      Array.prototype.forEach.call(chipsEl.querySelectorAll(".et-tool__chip"), function (c, i) {
        c.setAttribute("aria-pressed", MATERIALS[i] === state.mat ? "true" : "false");
      });

      sizeRowEl.innerHTML = "";
      var mat = state.mat;
      var cutDepth, wallThickness = null;
      if (mat.size === "pipe") {
        var label = document.createElement("label");
        var span = document.createElement("span");
        span.textContent = "Pipe size";
        var select = document.createElement("select");
        select.setAttribute("aria-label", "Pipe size");
        PIPES.forEach(function (p, i) {
          var opt = document.createElement("option");
          opt.value = String(i);
          opt.textContent = p[0] + " pipe (" + p[1].toFixed(2) + " in. OD)";
          if (i === state.pipeIdx) opt.selected = true;
          select.appendChild(opt);
        });
        select.addEventListener("change", function () {
          state.pipeIdx = Number(select.value);
          render();
        });
        label.appendChild(span);
        label.appendChild(select);
        sizeRowEl.appendChild(label);
        var pipe = PIPES[state.pipeIdx];
        cutDepth = pipe[1];
        wallThickness = pipe[2];
      } else if (mat.size === "wall") {
        var wlabel = document.createElement("label");
        var wspan = document.createElement("span");
        wspan.textContent = "Metal thickness (in.)";
        var winput = document.createElement("input");
        winput.type = "number";
        winput.min = "0.02";
        winput.max = "2";
        winput.step = "0.0625";
        winput.value = String(state.wall);
        winput.setAttribute("aria-label", "Metal thickness in inches");
        winput.addEventListener("change", function () {
          var v = parseFloat(winput.value);
          if (v > 0) {
            state.wall = v;
            render();
          }
        });
        wlabel.appendChild(wspan);
        wlabel.appendChild(winput);
        sizeRowEl.appendChild(wlabel);
        cutDepth = state.wall;
        wallThickness = state.wall;
      } else {
        var dlabel = document.createElement("label");
        var dspan = document.createElement("span");
        dspan.textContent = "Thickest part of the cut (in.)";
        var dinput = document.createElement("input");
        dinput.type = "number";
        dinput.min = "0.25";
        dinput.max = "10";
        dinput.step = "0.25";
        dinput.value = String(state.depth);
        dinput.setAttribute("aria-label", "Thickest part of the cut, in inches");
        dinput.addEventListener("change", function () {
          var v = parseFloat(dinput.value);
          if (v > 0) {
            state.depth = v;
            render();
          }
        });
        dlabel.appendChild(dspan);
        dlabel.appendChild(dinput);
        sizeRowEl.appendChild(dlabel);
        cutDepth = state.depth;
        if (mat.k === "wood") {
          var hint = document.createElement("span");
          hint.className = "et-tool__hint";
          hint.textContent = "A 2x4 is 3.5 in. deep.";
          sizeRowEl.appendChild(hint);
        }
      }

      var tpi;
      if (mat.tpi) tpi = mat.tpi;
      else if (mat.fams[0] === "wood" || mat.k === "curve") tpi = 6;
      else if (mat.k === "cast") tpi = 8;
      else if (mat.k === "unknown") tpi = "10/14";
      else tpi = pickTpi(wallThickness);

      var needLen = cutDepth + 3;
      var lenPick = LENGTHS.find(function (L) { return L >= needLen; }) || 12;
      var tooLong = needLen > 12;

      var picks = [];
      mat.fams.forEach(function (fam) {
        var pool = BLADE_SPEC.filter(function (c) { return c.fam === fam && String(c.tpi) === String(tpi); });
        if (!pool.length) return;
        var fit = pool.filter(function (c) { return c.len >= needLen; }).sort(function (a, b) { return a.len - b.len; });
        picks.push(fit.length ? fit[0] : pool.slice().sort(function (a, b) { return b.len - a.len; })[0]);
      });
      if (mat.k === "wood" && cutDepth <= 4) {
        var scroll = BLADE_SPEC.find(function (c) { return c.sku === "W656S"; });
        if (scroll) picks.push(scroll);
      }

      // Show the length of the blade we actually recommend (the next size up we stock, e.g. 9 in.
      // when the rule says 7.5 in. and carbide comes in 6 or 9), not the bare rule-of-thumb size.
      var teethLine = "";
      if (wallThickness && typeof tpi === "number") {
        var teethCount = wallThickness * tpi;
        teethLine =
          '<p class="et-tool__why">' +
          wallThickness.toFixed(3) +
          " in. wall &times; " +
          tpi +
          " TPI = <b>" +
          teethCount.toFixed(1) +
          " teeth</b> in the cut" +
          (teethCount < 3 ? " (under 3: tilt the saw so the blade enters at an angle)" : "") +
          "</p>";
      }

      resultEl.innerHTML =
        '<div class="et-tool__rec">' +
        "<div><small>Teeth per inch</small><b>" + tpi + (mat.k === "cast" ? " carbide" : "") + "</b></div>" +
        "<div><small>Blade length</small><b>" + (tooLong ? "12 in. max" : (picks.length ? picks[0].len : lenPick) + " in.") + "</b></div>" +
        "<div><small>Blade family</small><b>" + esc(FAMILY_LABEL[mat.fams[0]]) + "</b></div>" +
        "</div>" +
        '<p class="et-tool__why">' +
        esc(mat.why) +
        " " +
        (tooLong
          ? "This cut is deeper than a 12 in. blade can clear, so cut from both sides."
          : "Length = " + cutDepth.toFixed(2).replace(/\.00$/, "") + " in. cut + 3 in. clearance.") +
        "</p>" +
        teethLine +
        '<div class="et-tool__picks" data-picks></div>';

      var picksEl = resultEl.querySelector("[data-picks]");
      picks.forEach(function (p) {
        var a = document.createElement("a");
        a.className = "et-tool__pick";
        a.href = "/products/" + p.sku.toLowerCase();
        a.innerHTML =
          '<span><span class="et-tool__pick-sku">' +
          esc(p.sku) +
          "</span> &middot; " +
          p.len +
          " in. " +
          esc(FAMILY_LABEL[p.fam]) +
          " " +
          p.tpi +
          "T</span>" +
          '<span class="et-tool__pick-price" hidden data-price></span>';
        picksEl.appendChild(a);

        var priceEl = a.querySelector("[data-price]");
        fetchProduct(p.sku.toLowerCase()).then(function (product) {
          var priced = perBladePrice(product, p.pack);
          if (!priced) return; // fetch failed or product has no usable price — leave hidden
          priceEl.innerHTML = priced.label + (priced.available ? "" : " &middot; backorder");
          priceEl.hidden = false;
        });
      });
    }

    render();
  }

  // ---------------------------------------------------------------------
  // Teeth explorer
  // ---------------------------------------------------------------------

  var TEETH_TPI = [6, 10, 14, 18, 24];
  // Variable pitch: [coarse, fine] tooth counts on one blade. 10/14 is what we stock (HD610,
  // HD810, HD120); the others are the common VP sizes, shown so the idea is easy to compare.
  var TEETH_VP = [[6, 10], [8, 12], [10, 14], [14, 18]];
  var VP_DEFAULT = 2;

  function mountTeethExplorer(root) {
    root.classList.add("et-tool--teeth-explorer");
    root.innerHTML =
      '<div class="et-tool__teeth">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">Teeth explorer</div></div>' +
      '<div class="et-tool__toggle-row"><button type="button" class="et-tool__chip et-tool__vp" ' +
      'aria-pressed="false" data-vp>Variable pitch</button>' +
      '<span class="et-tool__hint" data-vp-hint>Mixed tooth spacing on one blade</span></div>' +
      '<svg class="et-tool__svg" viewBox="0 0 640 206" tabindex="0" role="slider" ' +
      'aria-label="Material position along the blade. Drag, or use the arrow keys." ' +
      'aria-valuemin="0" aria-valuemax="100" aria-valuenow="50" data-svg></svg>' +
      '<p class="et-tool__touch" aria-live="polite" data-touch></p>' +
      '<p class="et-tool__hint et-tool__drag-hint">Drag the material along the blade to see which teeth touch.</p>' +
      '<div class="et-tool__sliders">' +
      '<label class="et-tool__slider-label">Material thickness <output data-out-thick>0.125 in.</output>' +
      '<input type="range" data-thick min="0.03" max="0.75" step="0.005" value="0.125" ' +
      'aria-label="Material thickness in inches"></label>' +
      '<label class="et-tool__slider-label">Blade TPI <output data-out-tpi>24</output>' +
      '<input type="range" data-tpi min="0" max="4" step="1" value="4" ' +
      'aria-label="Blade teeth per inch, from 6 to 24"></label>' +
      "</div>" +
      '<p class="et-tool__verdict" aria-live="polite" data-verdict></p>' +
      "</div>";

    var svg = root.querySelector("[data-svg]");
    var thickInput = root.querySelector("[data-thick]");
    var tpiInput = root.querySelector("[data-tpi]");
    var outThick = root.querySelector("[data-out-thick]");
    var outTpi = root.querySelector("[data-out-tpi]");
    var verdictEl = root.querySelector("[data-verdict]");
    var vpBtn = root.querySelector("[data-vp]");
    var touchEl = root.querySelector("[data-touch]");
    var vpOn = false;
    var stdIndex = Number(tpiInput.value);
    // Where the material sits along the blade, as the position of its centre from 0 (left end)
    // to 1 (right end). Kept as a fraction so changing the thickness doesn't jump it around.
    var pos = 0.5;
    var dragging = false;
    var dragOffset = 0;
    var geom = { W: 640, left: 20, right: 620, matW: 0 };

    function cssVar(name) {
      return getComputedStyle(root).getPropertyValue(name).trim();
    }

    function draw() {
      var thickness = parseFloat(thickInput.value);
      var idx = Number(tpiInput.value);
      var lo, hi, tpiLabel;
      if (vpOn) {
        lo = TEETH_VP[idx][0];
        hi = TEETH_VP[idx][1];
        tpiLabel = lo + "/" + hi;
      } else {
        lo = hi = TEETH_TPI[idx];
        tpiLabel = String(lo);
      }
      var tpi = (lo + hi) / 2;
      outThick.textContent = thickness.toFixed(3) + " in.";
      outTpi.textContent = tpiLabel + (vpOn ? " variable" : "");

      var W = 640,
        scale = 520,
        bladeY = 40;
      // Tooth spacing sequence. Constant for a standard blade; for variable pitch it swings from
      // coarse to fine and back, the way real VP blades vary along their length.
      var pC = scale / lo,
        pF = scale / hi,
        pM = (pC + pF) / 2;
      var pitches = vpOn ? [pC, pM, pF, pF, pM] : [pC];
      var left = 20,
        right = W - 20;
      var matW = Math.min(thickness * scale, right - left);
      var travel = right - left - matW;
      var matX = left + travel * pos;
      geom = { W: W, left: left, right: right, matW: matW };
      var ink = cssVar("--ett-ink") || "#161A20";
      var sunk = cssVar("--ett-chip") || "#F3ECDD";
      var paper = cssVar("--ett-cream") || "#F4F4F4";
      var rust = cssVar("--ett-rust") || "#C9461D";
      var fineTone = cssVar("--ett-blue") || "#2E6FB0";
      var muted = cssVar("--ett-muted") || "#5B6573";

      var s = '<rect x="0" y="0" width="' + W + '" height="206" fill="' + paper + '"></rect>';
      s +=
        '<rect data-mat x="' + matX.toFixed(1) + '" y="' + (bladeY + 22) + '" width="' + matW.toFixed(1) +
        '" height="80" rx="2" fill="' + sunk + '" stroke="' + ink + '" stroke-width="1" ' +
        'style="cursor:' + (dragging ? "grabbing" : "grab") + '"></rect>';
      // grip marks so the box reads as draggable
      var gx = matX + matW / 2;
      s += '<g stroke="' + muted + '" stroke-width="1.5" stroke-linecap="round" pointer-events="none">' +
        '<line x1="' + (gx - 5) + '" y1="' + (bladeY + 58) + '" x2="' + (gx - 5) + '" y2="' + (bladeY + 70) + '"></line>' +
        '<line x1="' + gx + '" y1="' + (bladeY + 58) + '" x2="' + gx + '" y2="' + (bladeY + 70) + '"></line>' +
        '<line x1="' + (gx + 5) + '" y1="' + (bladeY + 58) + '" x2="' + (gx + 5) + '" y2="' + (bladeY + 70) + '"></line></g>';
      s += '<rect x="' + left + '" y="' + (bladeY - 4) + '" width="' + (right - left) + '" height="20" fill="' + ink + '"></rect>';

      // One polygon per tooth so coarse and fine stretches can be shaded apart. Coarse = the
      // wider spacings (coarse and middle), fine = the tightest spacing.
      var teethSvg = "";
      var dotsSvg = "";
      var touchCoarse = 0,
        touchFine = 0;
      var i = 0;
      for (var x = left; x < right; i++) {
        var pitch = pitches[i % pitches.length];
        var isFine = vpOn && pitch < pM - 0.01;
        var tipX = x + pitch * 0.35,
          tipY = bladeY + 28,
          endX = Math.min(x + pitch, right);
        teethSvg +=
          '<path d="M' + x.toFixed(1) + " " + (bladeY + 16) + " L" + tipX.toFixed(1) + " " + tipY +
          " L" + endX.toFixed(1) + " " + (bladeY + 16) + ' Z" fill="' + (isFine ? fineTone : ink) + '"></path>';
        if (tipX >= matX && tipX <= matX + matW) {
          if (isFine) touchFine++;
          else touchCoarse++;
          dotsSvg += '<circle cx="' + tipX.toFixed(1) + '" cy="' + tipY + '" r="3.4" fill="' + rust + '" pointer-events="none"></circle>';
        }
        x += pitch;
      }
      s += teethSvg;
      s += dotsSvg;
      var touching = touchCoarse + touchFine;
      if (vpOn) {
        // legend
        s += '<g font-family="Spline Sans Mono, monospace" font-size="12" fill="' + ink + '">' +
          '<rect x="' + (W - 196) + '" y="' + (bladeY - 24) + '" width="11" height="11" fill="' + ink + '"></rect>' +
          '<text x="' + (W - 180) + '" y="' + (bladeY - 14) + '">coarse ' + lo + '</text>' +
          '<rect x="' + (W - 100) + '" y="' + (bladeY - 24) + '" width="11" height="11" fill="' + fineTone + '"></rect>' +
          '<text x="' + (W - 84) + '" y="' + (bladeY - 14) + '">fine ' + hi + '</text></g>';
      }
      // Ruler: the drawing is to scale (scale = SVG units per inch), so one real inch runs from
      // the blade's left end. Ticks every 1/16 in., longer at 1/8, 1/4 and 1/2.
      var rY = 178;
      var ruler = '<g stroke="' + muted + '" stroke-width="1" pointer-events="none">' +
        '<line x1="' + left + '" y1="' + rY + '" x2="' + (left + scale) + '" y2="' + rY + '"></line>';
      for (var k = 0; k <= 16; k++) {
        var tx = left + (scale * k) / 16;
        var tl = k % 16 === 0 ? 12 : k % 8 === 0 ? 10 : k % 4 === 0 ? 8 : k % 2 === 0 ? 5 : 3;
        ruler += '<line x1="' + tx.toFixed(1) + '" y1="' + (rY - tl) + '" x2="' + tx.toFixed(1) + '" y2="' + rY + '"></line>';
      }
      ruler += "</g>";
      var rLabels = [[0, "0"], [4, "1/4"], [8, "1/2"], [12, "3/4"], [16, "1 in."]];
      ruler += '<g font-family="Spline Sans Mono, monospace" font-size="12" fill="' + muted + '" pointer-events="none">';
      for (var q = 0; q < rLabels.length; q++) {
        var lx = left + (scale * rLabels[q][0]) / 16;
        ruler += '<text x="' + lx.toFixed(1) + '" y="' + (rY + 16) + '" text-anchor="' +
          (q === 0 ? "start" : q === rLabels.length - 1 ? "end" : "middle") + '">' + rLabels[q][1] + "</text>";
      }
      ruler += "</g>";
      s += ruler;

      touchEl.textContent =
        touching + (touching === 1 ? " tooth" : " teeth") + " touching at this spot" +
        (vpOn ? " (" + touchCoarse + " coarse, " + touchFine + " fine)" : "") + ".";
      svg.setAttribute("aria-valuenow", String(Math.round(pos * 100)));
      svg.setAttribute("aria-valuetext", touching + " teeth touching" + (vpOn ? ", " + touchCoarse + " coarse and " + touchFine + " fine" : ""));
      s +=
        '<text x="' + W / 2 + '" y="' + (bladeY + 122) + '" text-anchor="middle" ' +
        'font-family="Spline Sans Mono, monospace" font-size="13" fill="' + ink + '">material ' +
        thickness.toFixed(3) + " in. thick (enlarged)</text>";
      s +=
        '<text x="22" y="' + (bladeY - 12) + '" font-family="Spline Sans Mono, monospace" ' +
        'font-size="13" fill="' + ink + '">blade &middot; ' + tpiLabel + " TPI" + (vpOn ? " variable pitch" : "") + "</text>";
      svg.innerHTML = s;

      var teethInCut = thickness * tpi;
      var cls, msg;
      if (vpOn) {
        var few = thickness * lo,
          many = thickness * hi;
        var range = few.toFixed(1) + " to " + many.toFixed(1) + " teeth in the cut";
        var ours = lo === 10 && hi === 14
          ? " Ours: the 10/14 Heavy-Duty blades (HD610, HD810, HD120)."
          : "";
        if (many < 2) {
          cls = "bad";
          msg = "About " + range + ". Too coarse even on the fine stretches. Use a finer blade.";
        } else if (many < 3) {
          cls = "ok";
          msg = "About " + range + ". Workable if you tilt the saw; a finer pitch is safer." + ours;
        } else if (few > 20) {
          cls = "ok";
          msg = "About " + range + ". Chips will clog the gaps. Use a coarser blade." + ours;
        } else if (few < 3) {
          cls = "good";
          msg = "About " + range + ". The fine stretches keep 3+ teeth in the thin spots, and the uneven spacing cuts vibration. This is where variable pitch shines." + ours;
        } else {
          cls = "good";
          msg = "About " + range + ". Good: smooth cut, and the uneven spacing cuts vibration and noise." + ours;
        }
        verdictEl.className = "et-tool__verdict et-tool__verdict--" + cls;
        verdictEl.textContent = msg;
        return;
      }
      if (teethInCut < 2) {
        cls = "bad";
        msg = "About " + teethInCut.toFixed(1) + " teeth in the cut. Teeth will snag and strip. Use a finer blade.";
      } else if (teethInCut < 3) {
        cls = "ok";
        msg = "About " + teethInCut.toFixed(1) + " teeth in the cut. Workable if you tilt the saw; a finer blade is safer.";
      } else if (teethInCut > 20) {
        cls = "ok";
        msg = "About " + teethInCut.toFixed(0) + " teeth in the cut. Chips will clog the gaps and the cut slows down. Use a coarser blade.";
      } else {
        cls = "good";
        msg = "About " + teethInCut.toFixed(1) + " teeth in the cut. Good: smooth cut, no snagging.";
      }
      verdictEl.className = "et-tool__verdict et-tool__verdict--" + cls;
      verdictEl.textContent = msg;
    }

    vpBtn.addEventListener("click", function () {
      vpOn = !vpOn;
      vpBtn.setAttribute("aria-pressed", vpOn ? "true" : "false");
      if (vpOn) {
        stdIndex = Number(tpiInput.value);
        tpiInput.max = String(TEETH_VP.length - 1);
        tpiInput.value = String(VP_DEFAULT);
        tpiInput.setAttribute("aria-label", "Variable pitch blade, from 6/10 to 14/18 teeth per inch");
      } else {
        tpiInput.max = String(TEETH_TPI.length - 1);
        tpiInput.value = String(stdIndex);
        tpiInput.setAttribute("aria-label", "Blade teeth per inch, from 6 to 24");
      }
      draw();
    });
    function svgX(evt) {
      var r = svg.getBoundingClientRect();
      return ((evt.clientX - r.left) / r.width) * geom.W;
    }
    function setPosFromLeft(leftX) {
      var travel = geom.right - geom.left - geom.matW;
      pos = travel > 0 ? Math.max(0, Math.min(1, (leftX - geom.left) / travel)) : 0.5;
      draw();
    }
    svg.addEventListener("pointerdown", function (evt) {
      if (!evt.target || !evt.target.hasAttribute || !evt.target.hasAttribute("data-mat")) return;
      var travel = geom.right - geom.left - geom.matW;
      dragging = true;
      dragOffset = svgX(evt) - (geom.left + travel * pos);
      try { svg.setPointerCapture(evt.pointerId); } catch (e) { /* older browsers */ }
      evt.preventDefault();
      draw();
    });
    svg.addEventListener("pointermove", function (evt) {
      if (!dragging) return;
      setPosFromLeft(svgX(evt) - dragOffset);
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      draw();
    }
    svg.addEventListener("pointerup", endDrag);
    svg.addEventListener("pointercancel", endDrag);
    svg.addEventListener("keydown", function (evt) {
      var step = evt.shiftKey ? 0.1 : 0.01;
      if (evt.key === "ArrowLeft" || evt.key === "ArrowDown") pos = Math.max(0, pos - step);
      else if (evt.key === "ArrowRight" || evt.key === "ArrowUp") pos = Math.min(1, pos + step);
      else if (evt.key === "Home") pos = 0;
      else if (evt.key === "End") pos = 1;
      else return;
      evt.preventDefault();
      draw();
    });
    thickInput.addEventListener("input", draw);
    tpiInput.addEventListener("input", draw);
    draw();

    // Redraw on light/dark theme change so the SVG keeps using the live token values.
    try {
      window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", draw);
    } catch (e) {
      /* older Safari: matchMedia listener API differs, not worth polyfilling for a redraw-only tool */
    }
  }

  // ---------------------------------------------------------------------
  // Bit length finder
  // ---------------------------------------------------------------------

  // The flute rule (David Castro): leave about two bit diameters of flute OUTSIDE the hole so
  // chips, dust and heat can get out. So the deepest hole a bit should drill = flute - 2 x dia.
  // Flute lengths are our own Triumph specs (T4HD stubby, T1D/T2HD+ jobber, T1290 long-flute
  // aircraft), read from the product descriptions 2026-09-27. The 6 in. (T6HH) and 12 in. (T12HH)
  // HD extension bits keep a JOBBER flute on a longer shank: they add reach, not depth.
  //
  // Drill bits are priced PER PIECE and sold by the sleeve. The storefront's quantity_rule reports
  // min 1 (Minmaxify enforces the real minimum), so the sleeve size is baked in here from
  // pim.products.order_multiple instead of read live. Handles are stored, not derived from the
  // SKU: most of these drop the SKU's leading zero (012516 -> /products/12516).
  var BIT_SIZES = [
    // label, diameter, SKU size code (64ths)
    ['1/8"', 0.125, "08"],
    ['3/16"', 0.1875, "12"],
    ['1/4"', 0.25, "16"],
    ['5/16"', 0.3125, "20"],
    ['3/8"', 0.375, "24"],
    ['7/16"', 0.4375, "28"],
    ['1/2"', 0.5, "32"],
  ];
  var JOBBER_FLUTE = [1.625, 2.3125, 2.75, 3.1875, 3.625, 4.0625, 4.5];
  // Each length: flute per size index (null = not stocked), handle/sku builders, sleeve per size.
  var BIT_LENGTHS = [
    {
      k: "stubby", label: "Stubby", oal: [1.875, 2.1875, 2.5, 2.8125, 3.125, 3.4375, 3.75],
      flute: [0.875, 1.125, 1.375, 1.625, 1.8125, 2.0625, 2.25],
      sku: "0604", handle: "604", sleeve: [12, 12, 12, 6, 6, 6, 6], name: "135° HD Stubby",
    },
    {
      k: "jobber", label: "Jobber", oal: [2.75, 3.5, 4, 4.5, 5, 5.5, 6], flute: JOBBER_FLUTE,
      sku: "0125", handle: "125", sleeve: [12, 12, 12, 6, 6, 6, 6], name: "Heavy-Duty ThunderBit",
    },
    {
      k: "long12", label: '12" long-flute aircraft', oal: [12, 12, 12, 12, 12, 12, 12],
      flute: [9, 9, 9, 9, 9, 9, 9], sku: "0598", handle: "598", sleeve: [1, 1, 1, 1, 1, 1, 1],
      name: '118° x 12" Aircraft Extension',
    },
    {
      k: "long18", label: '18" long-flute aircraft', oal: [18, 18, 18, 18, 18, 18, 18],
      flute: [null, null, 13, 13, 13, 13, 13], sku: "0599", handle: "599", sleeve: [1, 1, 1, 1, 1, 1, 1],
      name: '118° x 18" Aircraft Extension',
    },
  ];
  // Reach-only options: jobber flute on a long shank.
  var BIT_REACH = [
    { k: "hd6", label: '6" HD extension', oal: 6, sku: "0575", handle: "575", sleeve: [6, 6, 6, 6, 6, 6, 6], name: '135° HD 6" Aircraft' },
    { k: "hd12", label: '12" HD extension', oal: 12, sku: "0585", handle: "585", sleeve: [6, 6, 6, 3, 3, 3, 3], name: '135° HD 12" Extension' },
  ];
  // Metal: starting cutting speed (SFM, HSS; cobalt for stainless) + a jobber-length alternative
  // made for that metal, where we stock one at that size.
  var BIT_METALS = [
    { k: "steel", label: "Mild steel", sfm: 90, why: "A 135° split-point HSS bit is the everyday choice for mild steel." },
    {
      k: "stainless", label: "Stainless steel", sfm: 40,
      alt: { sku: function (c) { return "0135" + c; }, handle: function (c) { return "0135" + c; }, sizes: [0, 1, 2, 3, 4, 5, 6], sleeve: [12, 12, 12, 6, 6, 6, 6], name: "135° HD Cobalt ThunderBit" },
      why: "Use cobalt for stainless. Run slow, push hard and use oil so the steel never work-hardens under the tip.",
    },
    {
      k: "alum", label: "Aluminum / brass", sfm: 250,
      alt: { sku: function (c) { return "JL." + CODE_DEC[c] + "BR"; }, handle: function (c) { return "jl-" + CODE_DEC[c] + "br"; }, sizes: [0, 2, 4, 6], sleeve: [12, 12, 12, 6, 6, 6, 6], name: "118° Bright Jobber" },
      why: "A bright (uncoated) 118° bit cuts soft metal fast and chips don't stick to it.",
    },
    {
      k: "cast", label: "Cast iron", sfm: 70,
      alt: { sku: function (c) { return "CTJ." + CODE_DEC[c]; }, handle: function (c) { return "ctj-" + CODE_DEC[c]; }, sizes: [0, 2, 4, 6], sleeve: [2, 2, 4, 4, 2, 2, 1], name: "118° Carbide-Tipped Jobber" },
      why: "Cast iron is abrasive. A carbide tip lasts much longer; drill it dry.",
    },
  ];
  var CODE_DEC = { "08": "125", "16": "250", "24": "375", "32": "500" };

  /** Inches to the nearest 1/16 as a mixed fraction: 2.25 -> "2-1/4 in." */
  function inches(x) {
    var s = Math.round(x * 16);
    var whole = Math.floor(s / 16),
      n = s % 16,
      d = 16;
    while (n && n % 2 === 0) {
      n /= 2;
      d /= 2;
    }
    var frac = n ? n + "/" + d : "";
    return (whole && frac ? whole + "-" + frac : whole ? String(whole) : frac || "0") + " in.";
  }

  function mountBitLengthFinder(root) {
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--bit-length-finder");
    root.innerHTML =
      '<div class="et-tool__finder">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">Bit length finder</div>' +
      '<span class="et-tool__meta">Flute rule: 2 &times; diameter outside the hole &middot; made in the USA by Triumph</span></div>' +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-metal">What are you drilling?</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-metal" data-metals></div></div>' +
      '<div class="et-tool__row">' +
      '<label><span>Bit size</span><select data-size aria-label="Bit size"></select></label>' +
      '<label><span>Hole depth (in.)</span><input type="number" data-depth min="0.0625" max="14" step="0.0625" value="2" aria-label="Hole depth in inches"></label>' +
      '<label class="et-tool__check"><input type="checkbox" data-reach> <span>Need extra reach (past a flange or through a frame)</span></label>' +
      "</div>" +
      '<svg class="et-tool__svg et-tool__bitsvg" viewBox="0 0 640 196" role="img" data-svg></svg>' +
      '<div class="et-tool__result" aria-live="polite" data-result></div>' +
      "</div></div>";

    var metalsEl = root.querySelector("[data-metals]");
    var sizeEl = root.querySelector("[data-size]");
    var depthEl = root.querySelector("[data-depth]");
    var reachEl = root.querySelector("[data-reach]");
    var svg = root.querySelector("[data-svg]");
    var resultEl = root.querySelector("[data-result]");
    var state = { metal: BIT_METALS[0], sizeIdx: 2, depth: 2, reach: false };

    BIT_SIZES.forEach(function (s, i) {
      var o = document.createElement("option");
      o.value = String(i);
      o.textContent = s[0];
      if (i === state.sizeIdx) o.selected = true;
      sizeEl.appendChild(o);
    });
    BIT_METALS.forEach(function (m) {
      var b = document.createElement("button");
      b.type = "button";
      b.className = "et-tool__chip";
      b.textContent = m.label;
      b.addEventListener("click", function () {
        state.metal = m;
        render();
      });
      metalsEl.appendChild(b);
    });
    sizeEl.addEventListener("change", function () {
      state.sizeIdx = Number(sizeEl.value);
      render();
    });
    depthEl.addEventListener("input", function () {
      var v = parseFloat(depthEl.value);
      if (v > 0) {
        state.depth = v;
        render();
      }
    });
    reachEl.addEventListener("change", function () {
      state.reach = reachEl.checked;
      render();
    });

    function cssVar(name) {
      return getComputedStyle(root).getPropertyValue(name).trim();
    }

    function addPick(listEl, sku, handle, title, sleeve, note) {
      var a = document.createElement("a");
      a.className = "et-tool__pick";
      a.href = "/products/" + handle;
      a.innerHTML =
        '<span><span class="et-tool__pick-sku">' + esc(sku) + "</span> &middot; " + esc(title) +
        (note ? ' <span class="et-tool__hint">' + esc(note) + "</span>" : "") + "</span>" +
        '<span class="et-tool__pick-price" hidden data-price></span>';
      listEl.appendChild(a);
      var priceEl = a.querySelector("[data-price]");
      fetchProduct(handle).then(function (product) {
        var v = product && product.variants && product.variants[0];
        if (!v || typeof v.price !== "number") return; // leave hidden, keep SKU + link
        priceEl.innerHTML = usd(v.price) + " each" + (sleeve > 1 ? ", sold in " + sleeve + "s" : "") +
          (v.available ? "" : " &middot; backorder");
        priceEl.hidden = false;
      });
    }

    function drawBit(len, flute, oal, dia, depth, ok) {
      var ink = cssVar("--ett-ink") || "#161A20";
      var muted = cssVar("--ett-muted") || "#5B6573";
      var chip = cssVar("--ett-chip") || "#F3ECDD";
      var good = cssVar("--ett-good") || "#2E8B57";
      var bad = cssVar("--ett-bad") || "#A81830";
      var W = 640, left = 16, right = 624, midY = 62;
      // Scale so the whole bit (or the hole, if deeper) fits.
      var span = Math.max(oal, depth + 0.5);
      var sc = (right - left) / span;
      var tipX = right - Math.max(0, span - oal) * sc; // tip sits at the hole bottom
      var faceX = tipX - Math.min(depth, oal) * sc; // workpiece face
      var fluteStart = tipX - flute * sc;
      var shankX = tipX - oal * sc;
      var h = Math.max(10, Math.min(34, dia * sc)); // drawn thickness, clamped for legibility
      var s = "";
      // workpiece
      s += '<rect x="' + faceX.toFixed(1) + '" y="' + (midY - 42) + '" width="' + (right - faceX + 16).toFixed(1) +
        '" height="84" fill="' + chip + '" stroke="' + ink + '" stroke-width="1"></rect>';
      // hole
      s += '<rect x="' + faceX.toFixed(1) + '" y="' + (midY - h / 2 - 2).toFixed(1) + '" width="' + (tipX - faceX).toFixed(1) +
        '" height="' + (h + 4).toFixed(1) + '" fill="#fff" opacity="0.9"></rect>';
      // shank
      s += '<rect x="' + shankX.toFixed(1) + '" y="' + (midY - h / 2).toFixed(1) + '" width="' + (fluteStart - shankX).toFixed(1) +
        '" height="' + h.toFixed(1) + '" fill="' + muted + '"></rect>';
      // flutes as slanted stripes
      s += '<rect x="' + fluteStart.toFixed(1) + '" y="' + (midY - h / 2).toFixed(1) + '" width="' + (tipX - fluteStart - h * 0.4).toFixed(1) +
        '" height="' + h.toFixed(1) + '" fill="' + ink + '"></rect>';
      var step = Math.max(8, h * 0.9);
      for (var x = fluteStart + 4; x < tipX - h * 0.6; x += step) {
        s += '<line x1="' + x.toFixed(1) + '" y1="' + (midY + h / 2).toFixed(1) + '" x2="' + (x + h * 0.5).toFixed(1) +
          '" y2="' + (midY - h / 2).toFixed(1) + '" stroke="' + muted + '" stroke-width="2"></line>';
      }
      s += '<path d="M' + (tipX - h * 0.4).toFixed(1) + " " + (midY - h / 2).toFixed(1) + " L" + tipX.toFixed(1) + " " + midY +
        " L" + (tipX - h * 0.4).toFixed(1) + " " + (midY + h / 2).toFixed(1) + ' Z" fill="' + ink + '"></path>';
      // exposed-flute bracket
      var exposed = Math.max(0, faceX - fluteStart);
      var col = ok ? good : bad;
      var bY = midY + 50;
      if (exposed > 0) {
        s += '<g stroke="' + col + '" stroke-width="2"><line x1="' + fluteStart.toFixed(1) + '" y1="' + bY + '" x2="' + faceX.toFixed(1) + '" y2="' + bY + '"></line>' +
          '<line x1="' + fluteStart.toFixed(1) + '" y1="' + (bY - 6) + '" x2="' + fluteStart.toFixed(1) + '" y2="' + (bY + 6) + '"></line>' +
          '<line x1="' + faceX.toFixed(1) + '" y1="' + (bY - 6) + '" x2="' + faceX.toFixed(1) + '" y2="' + (bY + 6) + '"></line></g>';
      }
      var mono = 'font-family="Spline Sans Mono, monospace" font-size="12.5"';
      s += "<text " + mono + ' fill="' + col + '" x="' + Math.max(left, Math.min(fluteStart, faceX - 220)).toFixed(1) + '" y="' + (bY + 22) + '">flute outside: ' +
        inches(Math.max(0, flute - depth)) + " (need " + inches(2 * dia) + ")</text>";
      s += "<text " + mono + ' fill="' + ink + '" x="' + (faceX + 6).toFixed(1) + '" y="' + (midY - 28) + '">hole ' + inches(depth) + "</text>";
      s += "<text " + mono + ' fill="' + muted + '" x="' + left + '" y="14">' + esc(len) + " &middot; " + inches(oal) + " overall &middot; " + inches(flute) + " flute (to scale)</text>";

      // Ruler along the bit, zero at the shank end, same scale as the drawing. Tick spacing and
      // label spacing adapt to the zoom so an 18 in. bit stays readable and a stubby gets 1/8s.
      var rY = 172;
      var tickIn = sc >= 40 ? 0.125 : sc >= 16 ? 0.25 : 0.5;
      var labelEvery = sc >= 24 ? 1 : sc >= 12 ? 2 : 3;
      var rulerLen = Math.max(oal, (faceX - shankX) / sc + depth);
      var r = '<g stroke="' + muted + '" stroke-width="1">' +
        '<line x1="' + shankX.toFixed(1) + '" y1="' + rY + '" x2="' + (shankX + rulerLen * sc).toFixed(1) + '" y2="' + rY + '"></line>';
      var labels = "";
      var nTicks = Math.round(rulerLen / tickIn);
      for (var t = 0; t <= nTicks; t++) {
        var inch = t * tickIn;
        var tx = shankX + inch * sc;
        if (tx > right + 0.5) break;
        var whole = Math.abs(inch - Math.round(inch)) < 1e-6;
        var half = Math.abs(inch * 2 - Math.round(inch * 2)) < 1e-6;
        var qtr = Math.abs(inch * 4 - Math.round(inch * 4)) < 1e-6;
        var tl = whole ? 12 : half ? 8 : qtr ? 5 : 3;
        r += '<line x1="' + tx.toFixed(1) + '" y1="' + (rY - tl) + '" x2="' + tx.toFixed(1) + '" y2="' + rY + '"></line>';
        if (whole && Math.round(inch) % labelEvery === 0) {
          labels += '<text x="' + tx.toFixed(1) + '" y="' + (rY + 15) + '" text-anchor="' + (t === 0 ? "start" : "middle") + '">' +
            Math.round(inch) + (t === 0 ? " in." : "") + "</text>";
        }
      }
      r += "</g>";
      s += r + '<g font-family="Spline Sans Mono, monospace" font-size="11.5" fill="' + muted + '">' + labels + "</g>";
      svg.innerHTML = s;
      svg.setAttribute("aria-label", len + " bit in a " + inches(depth) + " deep hole, " + inches(Math.max(0, flute - depth)) + " of flute outside the hole");
    }

    function render() {
      Array.prototype.forEach.call(metalsEl.querySelectorAll(".et-tool__chip"), function (c, i) {
        c.setAttribute("aria-pressed", BIT_METALS[i] === state.metal ? "true" : "false");
      });
      var size = BIT_SIZES[state.sizeIdx];
      var i = state.sizeIdx,
        dia = size[1],
        code = size[2],
        depth = state.depth,
        need = depth + 2 * dia;

      var pick = null;
      for (var j = 0; j < BIT_LENGTHS.length; j++) {
        var L = BIT_LENGTHS[j];
        if (L.flute[i] != null && L.flute[i] >= need) {
          pick = L;
          break;
        }
      }
      var rpm = Math.round((3.82 * state.metal.sfm) / dia / 50) * 50;

      var rows = BIT_LENGTHS.filter(function (L) { return L.flute[i] != null; }).map(function (L) {
        var max = L.flute[i] - 2 * dia;
        return "<li>" + esc(L.label) + ": up to <b>" + inches(max) + "</b> deep" + (max >= depth ? " &#10003;" : "") + "</li>";
      }).join("");

      var html = '<div class="et-tool__rec">' +
        "<div><small>Bit length</small><b>" + (pick ? esc(pick.label) : "Too deep") + "</b></div>" +
        "<div><small>Deepest hole for it</small><b>" + (pick ? inches(pick.flute[i] - 2 * dia) : "&mdash;") + "</b></div>" +
        "<div><small>Starting speed</small><b>" + rpm.toLocaleString("en-US") + " RPM</b></div>" +
        "</div>";
      if (pick) {
        html += '<p class="et-tool__why">A ' + esc(size[0]) + " bit needs " + inches(2 * dia) + " of flute outside a " + inches(depth) +
          " hole, so " + inches(need) + " of flute in all. The " + esc(pick.label.toLowerCase()) + " has " + inches(pick.flute[i]) +
          ", and it is the shortest (stiffest) length that does. " + esc(state.metal.why) + "</p>";
      } else {
        html += '<p class="et-tool__why">No twist drill we stock clears the flute rule for a ' + inches(depth) + " hole at " + esc(size[0]) +
          ". Drill from both sides, or peck: back the bit all the way out every half inch to clear chips.</p>";
      }
      html += '<ul class="et-tool__maxlist">' + rows + "</ul>" + '<div class="et-tool__picks" data-picks></div>';
      resultEl.innerHTML = html;

      var picksEl = resultEl.querySelector("[data-picks]");
      if (pick) {
        var alt = state.metal.alt;
        if (pick.k === "jobber" && alt && alt.sizes.indexOf(i) >= 0) {
          addPick(picksEl, alt.sku(code), alt.handle(code), size[0] + " " + alt.name, alt.sleeve[i], "best for " + state.metal.label.toLowerCase());
        }
        addPick(picksEl, pick.sku + code, pick.handle + code, size[0] + " " + pick.name, pick.sleeve[i],
          alt && pick.k !== "jobber" ? "standard HSS; no " + state.metal.label.toLowerCase() + " version at this length" : "");
        if (state.reach && pick.k === "jobber") {
          BIT_REACH.forEach(function (R) {
            addPick(picksEl, R.sku + code, R.handle + code, size[0] + " " + R.name, R.sleeve[i], "jobber flute, extra reach");
          });
        }
        drawBit(pick.label, pick.flute[i], pick.oal[i], dia, depth, true);
      } else {
        var longest = BIT_LENGTHS.filter(function (L) { return L.flute[i] != null; }).pop();
        drawBit(longest.label, longest.flute[i], longest.oal[i], dia, depth, false);
      }
    }

    render();
    try {
      window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", render);
    } catch (e) {
      /* redraw-only nicety */
    }
  }

  // ---------------------------------------------------------------------
  // Drill speed calculator
  // ---------------------------------------------------------------------

  // RPM = 3.82 x SFM / diameter (in.). Rounded to the nearest 50 RPM for results >= 300, nearest
  // 10 below that, so the numbers match the speed tables in the drill-bits-for-metal and drill-speed-chart articles.
  function roundRpm(raw) {
    return raw >= 300 ? Math.round(raw / 50) * 50 : Math.round(raw / 10) * 10;
  }

  // sfm is [aluminum, brass, mild steel, cast iron, stainless], in that fixed order (SPEED_METALS
  // below carries the matching index for each metal chip).
  var SPEED_TOOLS = [
    { k: "twist", label: "Twist drill (HSS)", sizes: "drill", sfm: [250, 150, 90, 70, 40] },
    {
      k: "cobalt", label: "Cobalt drill", sizes: "drill", sfm: [250, 150, 90, 70, 40],
      note: "Cobalt runs at the same starting speed as HSS. Its edge is that it survives the heat, which is why it is the pick for stainless.",
    },
    {
      k: "carbide", label: "Carbide-tipped drill", sizes: "drill", sfm: [500, 300, 180, 140, 80],
      note: "Drill press or rigid setup only. Carbide chips if the drill wobbles.",
    },
    {
      k: "step", label: "Step drill", sizes: "step", sfm: [250, 150, 90, 70, 40],
      sizeLabel: "Largest step you will cut",
      note: "Set the speed for the biggest step you will cut, then leave it there.",
    },
    { k: "holesaw", label: "Bi-metal hole saw", sizes: "holesaw", sfm: [190, 130, 80, 60, 40] },
    {
      k: "countersink", label: "Countersink", sizes: "countersink", sfm: [125, 75, 45, 35, 20],
      sizeLabel: "Countersink body diameter",
      note: "If it chatters, slow down and push a little harder.",
    },
  ];
  var SPEED_METALS = [
    { k: "aluminum", label: "Aluminum", idx: 0 },
    { k: "brass", label: "Brass", idx: 1 },
    { k: "steel", label: "Mild steel", idx: 2 },
    { k: "castiron", label: "Cast iron", idx: 3 },
    { k: "stainless", label: "Stainless steel", idx: 4 },
  ];
  // label (as printed and as shown in a pick title) + decimal inches, per tool's size family.
  var SPEED_SIZES = {
    drill: [
      ["1/16", 0.0625], ["1/8", 0.125], ["3/16", 0.1875], ["1/4", 0.25], ["5/16", 0.3125],
      ["3/8", 0.375], ["7/16", 0.4375], ["1/2", 0.5], ["5/8", 0.625], ["3/4", 0.75], ["1", 1],
    ],
    step: [
      ["1/4", 0.25], ["3/8", 0.375], ["1/2", 0.5], ["5/8", 0.625], ["3/4", 0.75], ["7/8", 0.875],
      ["1", 1], ["1-1/8", 1.125], ["1-3/8", 1.375],
    ],
    holesaw: [
      ["3/4", 0.75], ["7/8", 0.875], ["1", 1], ["1-1/8", 1.125], ["1-3/8", 1.375], ["1-1/2", 1.5],
      ["1-3/4", 1.75], ["2", 2], ["2-1/2", 2.5], ["3", 3], ["3-1/2", 3.5], ["4", 4],
    ],
    countersink: [["1/4", 0.25], ["3/8", 0.375], ["1/2", 0.5], ["5/8", 0.625], ["3/4", 0.75], ["1", 1]],
  };
  var SPEED_HEAT_CHECK =
    "Too hot if: chips turn blue or brown on steel, the tool squeals, you see smoke, or you get " +
    "dust instead of chips. Slow down, push harder, add cutting oil.";

  /** Closest of "a quarter"/"a third"/"half"/"two-thirds"/"three-quarters"/"all the way" to a
   * 0-1 trigger fraction, clamped. */
  var SPEED_TRIGGER_WORDS = [
    [0.25, "a quarter"], [1 / 3, "a third"], [0.5, "half"], [2 / 3, "two-thirds"],
    [0.75, "three-quarters"], [1, "all the way"],
  ];
  function triggerWord(frac) {
    frac = Math.max(0, Math.min(1, frac));
    var best = SPEED_TRIGGER_WORDS[0],
      bestDist = Infinity;
    SPEED_TRIGGER_WORDS.forEach(function (o) {
      var d = Math.abs(frac - o[0]);
      if (d < bestDist) {
        bestDist = d;
        best = o;
      }
    });
    return best[1];
  }
  /** Cordless-drill gear/trigger guidance for a given target RPM, plus the always-shown drill
   * press line. Two speed ranges (low gear to 550, high gear to 1,800) cover nearly every
   * cordless drill; above that, only a press or corded drill keeps up. */
  function handDrillLines(rpm) {
    var lines = [];
    if (rpm <= 550) {
      lines.push("Cordless drill: low gear (1), trigger about " + triggerWord(rpm / 550) + " of the way.");
    } else if (rpm <= 1800) {
      lines.push("Cordless drill: high gear (2), trigger about " + triggerWord(rpm / 1800) + " of the way.");
    } else {
      lines.push("Cordless drill: high gear (2), full trigger. A drill press or corded drill can go faster.");
    }
    lines.push("Drill press: pick the nearest belt step below this number.");
    return lines;
  }

  // Priced picks per tool. Drill bits and countersinks are priced PER PIECE and sold by the
  // sleeve (Minmaxify enforces the real minimum, so quantity_rule reports min 1) — shown as
  // "$X each, sold in N" when a fixed sleeve size is given, or "$X each (min N)" for a
  // single-tool SKU with its own order minimum. `dia` is the decimal inches value from
  // SPEED_SIZES; `sizeLabel` is that size's printed fraction, used in a couple of pick titles.
  var HSS_COLLECTION = { href: "/collections/high-speed-drill-bits", label: "Every size of HSS drill bit" };
  function speedPicks(toolKey, metalKey, dia, sizeLabel) {
    var picks = [],
      links = [];
    if (toolKey === "twist" || toolKey === "cobalt" || toolKey === "carbide") {
      if (dia === 0.25) {
        if (toolKey === "twist") picks.push({ sku: "012516", handle: "12516", title: "ThunderBit 1/4 in. (012516)", sleeve: 12 });
        else if (toolKey === "cobalt") picks.push({ sku: "013516", handle: "013516", title: "Cobalt ThunderBit 1/4 in. (013516)", sleeve: 12 });
        else picks.push({ sku: "CTJ.250", handle: "ctj-250", title: "CTJ.250 carbide-tipped 1/4 in.", sleeve: 4 });
      } else {
        links.push(HSS_COLLECTION);
      }
    } else if (toolKey === "step") {
      if (metalKey === "stainless") {
        picks.push({ sku: "XSTP-01", handle: "xstp-01", title: "M35 cobalt step drill 1/8–1/2 in. (XSTP-01)" });
      } else if (dia <= 0.5) {
        picks.push({ sku: "274HEX01", handle: "274hex01", title: "M2 step drill 1/8–1/2 in. (274HEX01)", min: 2 });
      } else if (dia <= 0.875) {
        picks.push({ sku: "274HEX04", handle: "274hex04", title: "M2 step drill 3/16–7/8 in. (274HEX04)", min: 1 });
      } else if (dia <= 1.125) {
        picks.push({ sku: "274HEX05", handle: "274hex05", title: "M2 step drill 7/16–1-1/8 in. (274HEX05)", min: 1 });
      } else {
        picks.push({ sku: "274MC0006", handle: "274mc0006", title: "Step drill 3/4–1-3/8 in. (274MC0006)", min: 1 });
      }
      links.push({ href: "/collections/step-drills", label: "Every step drill" });
    } else if (toolKey === "holesaw") {
      var HOLESAW_SKUS = [
        [0.875, "275HW0016", "275hw0016", 4], [1.125, "275HW0019", "275hw0019", 4],
        [1.375, "275HW0023", "275hw0023", 4], [2, "275HW0028", "275hw0028", 4],
        [2.5, "275HW0033", "275hw0033", 2], [4, "275HW0037", "275hw0037", 2],
      ];
      var row = HOLESAW_SKUS.find(function (r) { return Math.abs(r[0] - dia) < 1e-6; });
      if (row) {
        picks.push({ sku: row[1], handle: row[2], title: "ITM " + sizeLabel + " in. bi-metal hole saw (" + row[1] + ")", min: row[3] });
      } else {
        links.push({ href: "/collections/hole-saws", label: "Every size of hole saw" });
      }
    } else if (toolKey === "countersink") {
      picks.push({ sku: "CS-18", handle: "cs-18", title: "Weldon 82° countersink 3/16–17/32 in. (CS-18)", min: 10 });
      links.push({ href: "/collections/countersinks", label: "Every countersink" });
    }
    return { picks: picks, links: links };
  }

  function mountSpeedCalculator(root) {
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--speed-calculator");
    root.innerHTML =
      '<div class="et-tool__finder">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">Drill speed calculator</div>' +
      '<span class="et-tool__meta">RPM = 3.82 &times; SFM &divide; diameter</span></div>' +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-tool">Tool</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-tool" data-tools></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-metal">Metal</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-metal" data-metals></div></div>' +
      '<div class="et-tool__row"><label><span data-size-label>Diameter (in.)</span>' +
      '<select data-size aria-label="Diameter in inches"></select></label></div>' +
      '<div class="et-tool__result" aria-live="polite" data-result></div>' +
      "</div></div>";

    var toolsEl = root.querySelector("[data-tools]");
    var metalsEl = root.querySelector("[data-metals]");
    var sizeLabelEl = root.querySelector("[data-size-label]");
    var sizeEl = root.querySelector("[data-size]");
    var resultEl = root.querySelector("[data-result]");

    var state = { tool: SPEED_TOOLS[0], metal: SPEED_METALS[2], sizeIdx: 3 }; // twist, mild steel, 1/4 in.

    function addPick(listEl, sku, handle, title, sleeve, min) {
      var a = document.createElement("a");
      a.className = "et-tool__pick";
      a.href = "/products/" + handle;
      a.innerHTML =
        '<span><span class="et-tool__pick-sku">' + esc(sku) + "</span> &middot; " + esc(title) + "</span>" +
        '<span class="et-tool__pick-price" hidden data-price></span>';
      listEl.appendChild(a);
      var priceEl = a.querySelector("[data-price]");
      fetchProduct(handle).then(function (product) {
        var v = product && product.variants && product.variants[0];
        if (!v || typeof v.price !== "number") return; // leave hidden, keep SKU + link
        var label = usd(v.price) + " each";
        if (sleeve > 1) label += ", sold in " + sleeve;
        else if (min > 1) label += " (min " + min + ")";
        priceEl.innerHTML = label + (v.available ? "" : " &middot; backorder");
        priceEl.hidden = false;
      });
    }

    function rebuildToolChips() {
      toolsEl.innerHTML = "";
      SPEED_TOOLS.forEach(function (t) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "et-tool__chip";
        b.textContent = t.label;
        b.setAttribute("aria-pressed", t === state.tool ? "true" : "false");
        b.addEventListener("click", function () {
          var curDia = SPEED_SIZES[state.tool.sizes][state.sizeIdx][1];
          state.tool = t;
          var sizes = SPEED_SIZES[t.sizes];
          var match = sizes.findIndex(function (s) { return Math.abs(s[1] - curDia) < 1e-6; });
          state.sizeIdx = match >= 0 ? match : 0;
          render();
        });
        toolsEl.appendChild(b);
      });
    }
    function rebuildMetalChips() {
      metalsEl.innerHTML = "";
      SPEED_METALS.forEach(function (m) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "et-tool__chip";
        b.textContent = m.label;
        b.setAttribute("aria-pressed", m === state.metal ? "true" : "false");
        b.addEventListener("click", function () {
          state.metal = m;
          render();
        });
        metalsEl.appendChild(b);
      });
    }

    function rebuildSizeSelect() {
      sizeLabelEl.textContent = state.tool.sizeLabel || "Diameter (in.)";
      sizeEl.innerHTML = "";
      SPEED_SIZES[state.tool.sizes].forEach(function (s, i) {
        var o = document.createElement("option");
        o.value = String(i);
        o.textContent = s[0] + " in.";
        if (i === state.sizeIdx) o.selected = true;
        sizeEl.appendChild(o);
      });
    }

    sizeEl.addEventListener("change", function () {
      state.sizeIdx = Number(sizeEl.value);
      render();
    });

    function render() {
      Array.prototype.forEach.call(toolsEl.querySelectorAll(".et-tool__chip"), function (c, i) {
        c.setAttribute("aria-pressed", SPEED_TOOLS[i] === state.tool ? "true" : "false");
      });
      Array.prototype.forEach.call(metalsEl.querySelectorAll(".et-tool__chip"), function (c, i) {
        c.setAttribute("aria-pressed", SPEED_METALS[i] === state.metal ? "true" : "false");
      });
      rebuildSizeSelect();

      var tool = state.tool;
      var sizes = SPEED_SIZES[tool.sizes];
      var entry = sizes[state.sizeIdx];
      var diaLabel = entry[0],
        dia = entry[1];
      var sfm = tool.sfm[state.metal.idx];
      var raw = (3.82 * sfm) / dia;
      var rpm = roundRpm(raw);
      var mmin = Math.round(sfm * 0.3048);

      var warning =
        state.metal.k === "stainless" && (tool.k === "twist" || tool.k === "step")
          ? "Plain HSS dulls fast in stainless. Use a cobalt drill (or the cobalt step drill)."
          : null;

      var html =
        '<div class="et-tool__rec">' +
        "<div><small>Starting speed</small><b>" + rpm.toLocaleString("en-US") + " RPM</b></div>" +
        "<div><small>Metric</small><b>" + mmin + " m/min</b></div>" +
        "</div>" +
        '<p class="et-tool__why">3.82 &times; ' + sfm + " SFM &divide; " + diaLabel + " in. (" + dia + " in.) = " +
        Math.round(raw).toLocaleString("en-US") + " &rarr; about " + rpm.toLocaleString("en-US") + " RPM</p>" +
        (warning ? '<p class="et-tool__warn">' + esc(warning) + "</p>" : "") +
        (tool.note ? '<p class="et-tool__why">' + esc(tool.note) + "</p>" : "") +
        '<ul class="et-tool__hand">' +
        handDrillLines(rpm).map(function (l) { return "<li>" + esc(l) + "</li>"; }).join("") +
        "</ul>" +
        '<p class="et-tool__heat">' + esc(SPEED_HEAT_CHECK) + "</p>" +
        '<div class="et-tool__picks" data-picks></div>';
      resultEl.innerHTML = html;

      var picksEl = resultEl.querySelector("[data-picks]");
      var result = speedPicks(tool.k, state.metal.k, dia, diaLabel);
      result.picks.forEach(function (p) {
        addPick(picksEl, p.sku, p.handle, p.title, p.sleeve, p.min);
      });
      result.links.forEach(function (l) {
        var a = document.createElement("a");
        a.className = "et-tool__pick et-tool__pick--link";
        a.href = l.href;
        a.textContent = l.label;
        picksEl.appendChild(a);
      });
    }

    rebuildToolChips();
    rebuildMetalChips();
    render();
  }

  // ---------------------------------------------------------------------
  // Band saw blade finder
  // ---------------------------------------------------------------------

  // Saw -> blade length. `len` is the printed length key into BAND_PACKS. `pending: true` means the
  // length is awaiting vendor confirmation (Milwaukee lists 27 in. for the 2429), so the result
  // asks the reader to check the saw's label.
  var SAW_FIT = [
    { brand: "Milwaukee", name: "M12 Sub-Compact", models: "2429", cls: "Sub-compact", len: "27-3/16" },
    { brand: "Milwaukee", name: "M12 FUEL Compact", models: "2529-20, 2529-XC", cls: "Compact", len: "30-9/16" },
    { brand: "Milwaukee", name: "M18 / M18 FUEL Compact", models: "2629-20, 2629-22, 2829; corded 6242-6", cls: "Compact", len: "35-3/8" },
    { brand: "Milwaukee", name: "M18 FUEL Deep Cut", models: "2729, 2929; corded 6232", cls: "Deep cut", len: "44-7/8" },
    { brand: "DEWALT", name: "20V Compact / ATOMIC Compact", models: "DCS375B, DCS377B", cls: "Compact", len: "27-3/16" },
    { brand: "DEWALT", name: "20V Compact", models: "DCS370, DCS371B", cls: "Compact", len: "32-7/8" },
    { brand: "DEWALT", name: "20V Deep Cut / corded Deep Cut", models: "DCS374, DCS376, DWM120", cls: "Deep cut", len: "44-7/8" },
    { brand: "Makita", name: "18V Sub-Compact", models: "XBP05ZB", cls: "Sub-compact", len: "28-13/16" },
    { brand: "Makita", name: "18V Compact", models: "XBP03Z, XBP04Z", cls: "Compact", len: "32-7/8" },
    { brand: "Makita", name: "18V Deep Cut / corded Deep Cut", models: "XBP02, 2107F", cls: "Deep cut", len: "44-7/8" },
    { brand: "Bosch", name: "18V Compact / corded", models: "GCB18V-2N, GCB18V-LI; corded BSH180", cls: "Compact", len: "28-13/16" },
  ];
  var BAND_BRANDS = ["Milwaukee", "DEWALT", "Makita", "Bosch", "Other / not listed"];

  // Packs per length: [handle, label TPI]. Handle is the lowercase SKU. All ITM, 1/2 in. x .020 in.
  // bi-metal (8% cobalt), made in the USA, box of 3. No prices here: they come from fetchProduct().
  var BAND_PACK_SIZE = 3;
  var BAND_PACKS = {
    "27-3/16": [["b270018", "18"], ["b271418", "14/18"]],
    "28-13/16": [["b280018", "18"], ["b281418", "14/18"]],
    "30-9/16": [["b300018", "18"], ["b301418", "14/18"]],
    "32-7/8": [["b320018", "18"], ["b321418", "14/18"]],
    "35-3/8": [["b350018", "18"], ["b351418", "14/18"]],
    "44-7/8": [["279sb0024", "24"], ["279sb0018", "18"], ["279vp1418", "14/18"], ["279sb0014", "14"], ["279vp1014", "10/14"]],
  };
  var BAND_LENGTHS = ["27-3/16", "28-13/16", "30-9/16", "32-7/8", "35-3/8", "44-7/8"];

  function bandLenLabel(len) {
    return len + " in.";
  }

  function mountBandSawFinder(root) {
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--band-saw-finder");
    root.innerHTML =
      '<div class="et-tool__finder">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">Band saw blade finder</div>' +
      '<span class="et-tool__meta">Portable band saws &middot; made in the USA by ITM</span></div>' +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-brand">1. Pick your saw brand</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-brand" data-brands></div></div>' +
      '<div hidden data-saw-wrap><div class="et-tool__label" id="' + uid + '-saw">2. Pick your saw</div>' +
      '<div class="et-tool__saws" role="group" aria-labelledby="' + uid + '-saw" data-saws></div></div>' +
      '<div class="et-tool__result" aria-live="polite" data-result hidden></div>' +
      "</div></div>";

    var brandsEl = root.querySelector("[data-brands]");
    var sawWrap = root.querySelector("[data-saw-wrap]");
    var sawsEl = root.querySelector("[data-saws]");
    var resultEl = root.querySelector("[data-result]");
    var state = { brand: null, saw: null };

    BAND_BRANDS.forEach(function (b) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "et-tool__chip";
      btn.textContent = b;
      btn.setAttribute("aria-pressed", "false");
      btn.addEventListener("click", function () {
        state.brand = b;
        state.saw = null;
        render();
      });
      brandsEl.appendChild(btn);
    });

    function addPack(listEl, handle, tpi, len) {
      var a = document.createElement("a");
      a.className = "et-tool__pick";
      a.href = "/products/" + handle;
      a.innerHTML =
        '<span><span class="et-tool__pick-sku">' + esc(handle.toUpperCase()) + "</span> &middot; " +
        esc(bandLenLabel(len)) + " " + esc(tpi) + " TPI bi-metal band saw blades</span>" +
        '<span class="et-tool__pick-price" hidden data-price></span>';
      listEl.appendChild(a);
      var priceEl = a.querySelector("[data-price]");
      fetchProduct(handle).then(function (product) {
        var v = product && product.variants && product.variants[0];
        if (!v || typeof v.price !== "number") return; // hide price, keep SKU + link
        // variant.price covers the WHOLE box of 3.
        priceEl.innerHTML =
          "Box of " + BAND_PACK_SIZE + " &mdash; " + usd(v.price) + " (" + usd(v.price / BAND_PACK_SIZE) +
          " per blade)" + (v.available ? "" : " &middot; backorder");
        priceEl.hidden = false;
      });
    }

    function render() {
      Array.prototype.forEach.call(brandsEl.querySelectorAll(".et-tool__chip"), function (c, i) {
        c.setAttribute("aria-pressed", BAND_BRANDS[i] === state.brand ? "true" : "false");
      });
      sawsEl.innerHTML = "";
      resultEl.innerHTML = "";
      resultEl.hidden = true;
      sawWrap.hidden = true;
      if (!state.brand) return;

      if (state.brand === "Other / not listed") {
        resultEl.hidden = false;
        resultEl.innerHTML =
          '<p class="et-tool__why">Measure the old blade, or read the length printed on the saw\'s label or in its manual. ' +
          "Band saw blades are welded into a loop, so the length is fixed and has to match exactly. " +
          "These are the lengths we stock (all 1/2 in. wide x .020 in. thick, bi-metal):</p>" +
          '<ul class="et-tool__lenlist" data-lens></ul>';
        var lens = resultEl.querySelector("[data-lens]");
        BAND_LENGTHS.forEach(function (L) {
          var li = document.createElement("li");
          li.innerHTML = "<b>" + esc(bandLenLabel(L)) + "</b> ";
          BAND_PACKS[L].forEach(function (p, i) {
            var a = document.createElement("a");
            a.href = "/products/" + p[0];
            a.textContent = p[1] + " TPI";
            if (i) li.appendChild(document.createTextNode(" \u00b7 "));
            li.appendChild(a);
          });
          lens.appendChild(li);
        });
        return;
      }

      sawWrap.hidden = false;
      SAW_FIT.filter(function (s) { return s.brand === state.brand; }).forEach(function (s) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "et-tool__saw";
        btn.setAttribute("aria-pressed", s === state.saw ? "true" : "false");
        btn.innerHTML =
          '<span class="et-tool__saw-name">' + esc(s.name) + "</span>" +
          '<span class="et-tool__saw-meta">' + esc(s.models) + " &middot; " + esc(s.cls) + "</span>";
        btn.addEventListener("click", function () {
          state.saw = s;
          render();
        });
        sawsEl.appendChild(btn);
      });

      var saw = state.saw;
      if (!saw) return;
      resultEl.hidden = false;
      resultEl.innerHTML =
        '<div class="et-tool__bigrec"><small>Blade length</small><b>' + esc(bandLenLabel(saw.len)) + "</b>" +
        "<span>1/2 in. wide x .020 in. thick, bi-metal</span></div>" +
        (saw.pending
          ? '<p class="et-tool__hint">Confirm length on your saw\'s label. Milwaukee lists this saw at 27 in.</p>'
          : "") +
        '<p class="et-tool__why">Pick the tooth count for what you cut: 24 TPI for thin conduit and EMT; 18 TPI for strut and thin-wall tube; ' +
        "14/18 for all-round work; 14 or 10/14 for heavy-wall pipe, pier pipe and solid bar.</p>" +
        '<div class="et-tool__picks" data-picks></div>';
      var picksEl = resultEl.querySelector("[data-picks]");
      BAND_PACKS[saw.len].forEach(function (p) { addPack(picksEl, p[0], p[1], saw.len); });
    }

    render();
  }

  // ---------------------------------------------------------------------
  // SDS shank chooser
  // ---------------------------------------------------------------------

  // sku -> [handle, short title, minimum order qty, pieces per priced unit]. Handles are the real
  // Shopify handles (never derived from the SKU: they differ in case and, for some lines, in
  // form). Minimums come from PIM and are NOT in the storefront JSON, so they live here; prices do
  // not: they come from fetchProduct(). Every row is priced per piece except RL-63264-B, a 25-pack
  // priced per pack (pieces = 25). See docs/blog/ARTICLE-TOOLS.md.
  var SHANK_PRODUCTS = {
    "715S61664": ["715s61664", "1/4\" x 6-1/4\" SDS-Plus", 10, 1],
    "STP-617": ["stp-617", "3/8\" SDS-Plus Stop Drill Bit for 1/4\" Drop-In Anchors", 3, 1],
    "STP-813": ["stp-813", "1/2\" SDS-Plus Stop Drill Bit for 3/8\" Short Drop-In Anchors", 2, 1],
    "STP-1019": ["stp-1019", "5/8\" SDS-Plus Stop Drill Bit for 1/2\" Short Drop-In Anchors", 2, 1],
    "715S63264": ["715s63264", "1/2\" x 6-1/4\" SDS-Plus", 5, 1],
    "EVL63264": ["evl63264", "1/2\" x 6-1/4\" SDS-Plus X-Cutter", 4, 1],
    "EVL84064": ["evl84064", "5/8\" x 8\" SDS-Plus X-Cutter", 2, 1],
    "715S84864": ["715s84864", "3/4\" x 8\" SDS-Plus", 2, 1],
    "EVL10034": ["evl10034", "3/4\" x 10\" SDS-Plus X-Cutter", 2, 1],
    "EVL10001": ["evl10001", "1\" x 10\" SDS-Plus X-Cutter", 1, 1],
    "EVL10118": ["evl10118", "1-1/8\" x 10\" SDS-Plus X-Cutter", 1, 1],
    "EVL18114": ["evl18114", "1-1/4\" x 18\" SDS-Plus X-Cutter", 1, 1],
    "715FL3632": ["715fl3632", "1/2\" x 36\" SDS-Plus", 1, 1],
    "715FL3634": ["715fl3634", "3/4\" x 36\" SDS-Plus", 1, 1],
    "SMTE1312": ["smte1312", "1/2\" x 8\" x 13\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE1358": ["smte1358", "5/8\" x 8\" x 13\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE1334": ["smte1334", "3/4\" x 8\" x 13\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE1701": ["smte1701", "1\" x 12\" x 17\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE1718": ["smte1718", "1-1/8\" x 12\" x 17\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE2214": ["smte2214", "1-1/4\" x 18\" x 23\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE2202": ["smte2202", "2\" x 18\" x 23\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE3634": ["smte3634", "3/4\" x 31\" x 36\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE3678": ["smte3678", "7/8\" x 31\" x 36\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE3658": ["smte3658", "5/8\" x 31\" x 36\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE5201": ["smte5201", "1\" x 47\" x 52\" X-Cutter SDS-Max Drill", 1, 1],
    "725MAX001": ["725max001", "SDS-Max to SDS-Plus Adapter", 1, 1],
    "SDS-HD1": ["sds-hd1", "1/4\" x 9\" SDS-Plus Nail-In Anchor Driver", 1, 1],
    "3100BP": ["3100bp", "10\" SDS-Plus Bull Point", 2, 1],
    "3110FC": ["3110fc", "3/4\" x 10\" SDS-Plus Flat Chisel", 2, 1],
    "3175TC": ["3175tc", "3\" x 6-1/2\" SDS-Plus Tile Chisel", 1, 1],
    "7000BP": ["7000bp", "12\" SDS-Max Bull Point", 2, 1],
    "7024FC": ["7024fc", "1\" x 24\" SDS-Max Flat Chisel", 1, 1],
    "7025SC": ["7025sc", "2\" x 12\" SDS-Max Scaling Chisel", 2, 1],
    "880SL1055": ["880sl1055", "2-3/4\" x 12\" 1-Pc SDS-Max Core Bit", 1, 1],
    "880SL1115": ["880sl1115", "4\" x 12\" 1-Pc SDS-Max Core Bit", 1, 1],
    "XT6702": ["xt6702", "30\" SDS-Max Extension", 1, 1],
    "RT-10": ["rt-10", "5/8\" x 12\" SDS-Plus Rebar Puncher", 1, 1],
    "RMX-10": ["rmx-10", "5/8\" x 12\" SDS-Max Rebar Puncher", 1, 1],
    "715FL3638": ["715fl3638", "3/8\" x 36\" SDS-Plus", 1, 1],
    "715FL3658": ["715fl3658", "5/8\" x 36\" SDS-Plus", 1, 1],
    "SMTE5258": ["smte5258", "5/8\" x 47\" x 52\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE5234": ["smte5234", "3/4\" x 47\" x 52\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE5278": ["smte5278", "7/8\" x 47\" x 52\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE5214": ["smte5214", "1-1/4\" x 47\" x 52\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE5238": ["smte5238", "1-3/8\" x 47\" x 52\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE5212": ["smte5212", "1-1/2\" x 47\" x 52\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE3601": ["smte3601", "1\" x 31\" x 36\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE2201": ["smte2201", "1\" x 16\" x 21\" X-Cutter SDS-Max Drill", 1, 1],
    "SMTE2278": ["smte2278", "7/8\" x 16\" x 21\" X-Cutter SDS-Max Drill", 1, 1],
    "EVL18001": ["evl18001", "1\" x 18\" SDS-Plus X-Cutter", 1, 1],
    "EVL18078": ["evl18078", "7/8\" x 18\" SDS-Plus X-Cutter", 1, 1],
    "STP-1033": ["stp-1033", "5/8\" SDS-Plus Stop Drill Bit for 1/2\" Drop-In Anchors", 2, 1],
    "RL-63264-B": ["rl-63264-b", "1/2\" X 6-1/4\" SDS-Plus 2-Cutter (25 pack)", 1, 25],
  };

  var SHANK_HOLES = [
    ["u38", "Up to 3/8 in."], ["h12", "1/2 in."], ["h58", "5/8 in."], ["h34", "3/4 in."],
    ["h78", "7/8 to 1 in."], ["h118", "1-1/8 to 2 in."], ["core", "Core 2 to 6 in."], ["chisel", "Chisel or break"],
  ];
  var SHANK_DEPTHS = [["d0", "Under 8 in."], ["d1", "8 to 18 in."], ["d2", "19 to 31 in."], ["d3", "32 to 47 in."]];
  var SHANK_JOBS = [
    ["overhead", "Anchors overhead"], ["slab", "Anchors in a slab or footing"],
    ["through", "Through a wall or a sleeve"], ["rebar", "Rebar in the hole"],
  ];
  var SHANK_OWN = [["none", "None yet"], ["plus", "SDS-Plus"], ["max", "SDS-Max"], ["both", "Both"]];
  var SHANK_VERDICT = {
    PLUS: "SDS-Plus",
    MAX: "SDS-Max",
    MAX_ADAPTER: "SDS-Max with the adapter",
    EITHER: "Either shank",
  };

  var SHANK_ADAPTER_WHY =
    "Your SDS-Max hammer runs these through the SDS-Max to SDS-Plus adapter; keep to 5/8 in. and under and expect faster bit wear.";
  var SHANK_NEED_MAX_HINT = "An SDS-Plus hammer does not take SDS-Max bits; this job needs an SDS-Max hammer.";

  /**
   * The chooser's rules, as a pure function of the four chip choices. These match the article's
   * static table (rule T6), so change both together. `s` = {hole, depth, job, own}; depth and job
   * fall back to "Under 8 in." and "Anchors in a slab or footing". Returns
   * {verdict, reason, hints: [string], picks: [{sku, note?}]}.
   */
  function shankAnswer(s) {
    var hole = s.hole;
    var depth = s.depth || "d0";
    var job = s.job || "slab";
    var own = s.own;
    var ownMaxOnly = own === "max";
    var ownPlus = own === "plus";
    var hints = [];
    var picks = [];
    function pick(sku, note) {
      picks.push(note ? { sku: sku, note: note } : { sku: sku });
    }
    function out(verdict, reason) {
      // A pick can be offered by two rules (e.g. EVL10034); show it once, in first position.
      var seen = {};
      var uniq = picks.filter(function (p) {
        if (seen[p.sku]) return false;
        seen[p.sku] = true;
        return true;
      });
      if (verdict === "MAX" && ownPlus && !hints.length) hints.push(SHANK_NEED_MAX_HINT);
      return { verdict: verdict, reason: reason, hints: hints, picks: uniq };
    }

    // Chiseling and breaking.
    if (hole === "chisel") {
      if (ownPlus) {
        ["3100BP", "3110FC", "3175TC"].forEach(function (k) { pick(k); });
        return out("PLUS", "An SDS-Plus hammer chips tile, cuts chases and knocks off high spots; it will not break out a slab.");
      }
      ["7000BP", "7024FC", "7025SC"].forEach(function (k) { pick(k); });
      if (own === "none") hints.push("Buy the SDS-Max hammer for breaking; no adapter chisels safely.");
      return out("MAX", "Chiseling is impact energy; an SDS-Max hammer in hammer-only mode moves concrete.");
    }

    // Core drilling is SDS-Max work at any depth.
    if (hole === "core") {
      pick("880SL1055");
      pick("880SL1115");
      if (depth === "d2") {
        pick("XT6702");
        hints.push("The X-Tendr adapter must pass through the hole, so the core must be 1-1/4 in. or larger.");
      } else if (depth === "d3") {
        hints.push("A core bit that deep is not made; use a diamond core rig.");
      }
      return out("MAX", "Core bits this size need the power and torque of an SDS-Max hammer.");
    }

    // Depth 32 to 47 in. Catalog reach by diameter: SDS-Plus 36 in. bits drill 34 in. deep;
    // SDS-Max reaches 47 in. only at 5/8 in. and up. So the smallest holes stay SDS-Plus.
    if (depth === "d3") {
      if (hole === "u38" || hole === "h12") {
        if (ownMaxOnly) pick("725MAX001");
        pick(hole === "u38" ? "715FL3638" : "715FL3632");
        hints.push("The 36 in. bit reaches 34 in.; nothing in either shank reaches 47 in. at this size.");
        return out(
          ownMaxOnly ? "MAX_ADAPTER" : "PLUS",
          ownMaxOnly ? SHANK_ADAPTER_WHY : "In holes this small the longest bit we sell is an SDS-Plus 36 in. bit."
        );
      }
      if (hole === "h58") pick("SMTE5258");
      else if (hole === "h34") pick("SMTE5234");
      else if (hole === "h78") {
        pick("SMTE5278");
        pick("SMTE5201");
      } else {
        pick("SMTE5214");
        pick("SMTE5238");
        pick("SMTE5212");
      }
      if (hole === "h58" || hole === "h34") hints.push("The SDS-Plus 36 in. bit stops at 34 in.");
      return out("MAX", "Only SDS-Max bits reach 47 in. deep.");
    }

    if (hole === "h118") {
      pick("SMTE1718");
      pick("SMTE2214");
      pick("SMTE2202");
      if (ownPlus) {
        hints.push("SDS-Plus reaches 1-1/4 in. at the limit (EVL18114, EVL10118) but buy SDS-Max for weekly work.");
        pick("EVL18114");
      }
      return out("MAX", "Holes over 1 in. are SDS-Max work: the bits and the hammer are built for the load.");
    }

    if (hole === "h78") {
      if (depth === "d2" || job === "through") {
        pick("SMTE3678");
        pick(depth === "d2" ? "SMTE3601" : "SMTE1701");
        return out("MAX", "Deep holes and holes through a wall at 7/8 to 1 in. want the length of an SDS-Max bit and the power of an SDS-Max hammer.");
      }
      var deep16 = depth === "d1";
      var plusBits = deep16 ? ["EVL18001", "EVL18078"] : ["EVL10001"];
      var maxBits = deep16 ? ["SMTE2201", "SMTE2278"] : ["SMTE1701"];
      if (ownPlus) {
        plusBits.forEach(function (k) { pick(k); });
        return out("PLUS", "Your SDS-Plus hammer drills 7/8 to 1 in. well; stay with it unless the holes run deep.");
      }
      if (ownMaxOnly) {
        maxBits.forEach(function (k) { pick(k); });
        return out("MAX", deep16
          ? "Your SDS-Max hammer takes the SDS-Max bit; no adapter needed."
          : "Your SDS-Max hammer takes the 1 in. SDS-Max bit, which costs about 1.4x the Plus X-Cutter and drills 4 in. deeper.");
      }
      plusBits.concat(maxBits).forEach(function (k) { pick(k); });
      return out("EITHER", deep16
        ? "Both shanks drill 7/8 to 1 in. to 16 in.; the SDS-Max hammer finishes faster and does not slow in rebar."
        : "1 in. is the last size SDS-Plus does well; the SDS-Max bit costs about 1.4x the Plus X-Cutter and drills 4 in. deeper.");
    }

    if (hole === "h34") {
      if (ownMaxOnly) {
        if (depth === "d2") pick("SMTE3634");
        else pick("SMTE1334");
        return out("MAX", "Your SDS-Max hammer drills 3/4 in. with a true SDS-Max bit; no adapter needed.");
      }
      if (depth === "d2") {
        pick("SMTE3634");
        if (ownPlus) {
          pick("715FL3634");
          hints.push("715FL3634 is slow and wanders in a 1 in. hammer.");
        }
        return out("MAX", "At 19 to 31 in. deep, 3/4 in. is an SDS-Max bit.");
      }
      if (job === "overhead") {
        if (depth === "d0") pick("715S84864");
        pick("EVL10034");
        return out("PLUS", "Overhead the SDS-Plus hammer wins: it weighs about half as much as an SDS-Max hammer.");
      }
      if (depth === "d1") {
        pick("EVL10034");
        pick("SMTE1334");
        return out("EITHER", "Both shanks drill 3/4 in.; the SDS-Max hammer finishes faster and does not slow in rebar.");
      }
      pick("715S84864");
      pick("EVL10034");
      hints.push("SDS-Max (SMTE1334) when 3/4 in. is the daily size.");
      return out("PLUS", "A short 3/4 in. hole is comfortable SDS-Plus work, and the hammer is lighter to hold.");
    }

    if (hole === "h12" || hole === "h58") {
      var is12 = hole === "h12";
      var rebar = job === "rebar";
      var plusWhy = "Holes to 5/8 in. are SDS-Plus work and the hammer weighs half as much overhead.";
      if (depth === "d2") {
        // 19 to 31 in. deep: 1/2 in. is the 36 in. SDS-Plus bit only; 5/8 in. is SDS-Plus 715FL3658
        // unless the reader owns only SDS-Max (SMTE3658).
        if (is12 || !ownMaxOnly) {
          if (is12 && ownMaxOnly) pick("725MAX001");
          pick(is12 ? "715FL3632" : "715FL3658");
          if (rebar) {
            pick(is12 && ownMaxOnly ? "RMX-10" : "RT-10");
            hints.push("See the rebar drill bit guide.");
          }
          if (is12 && ownMaxOnly) return out("MAX_ADAPTER", SHANK_ADAPTER_WHY);
          return out("PLUS", "A 36 in. SDS-Plus bit reaches 19 to 31 in. in " + (is12 ? "1/2" : "5/8") + " in., and the hammer is half the weight.");
        }
        pick("SMTE3658");
        if (rebar) {
          pick("RMX-10");
          hints.push("See the rebar drill bit guide.");
        }
        return out("MAX", "Your SDS-Max hammer takes the 36 in. SDS-Max bit at 5/8 in.; no adapter needed.");
      }

      var plusPicks = is12 ? ["715S63264", "EVL63264"] : ["EVL84064"];
      var stop = is12 ? "STP-813" : "STP-1019";
      var trueMax = is12 ? "SMTE1312" : "SMTE1358";
      if (ownMaxOnly) {
        pick("725MAX001");
        if (job === "overhead") pick(stop);
        plusPicks.forEach(function (k) { pick(k); });
        if (job === "slab") pick(trueMax, "true SDS-Max bit, no adapter");
        if (rebar) {
          pick("RMX-10");
          hints.push("See the rebar drill bit guide.");
        }
        return out("MAX_ADAPTER", SHANK_ADAPTER_WHY);
      }
      if (job === "overhead") pick(stop);
      plusPicks.forEach(function (k) { pick(k); });
      if (rebar) {
        pick("RT-10");
        hints.push("See the rebar drill bit guide.");
      }
      if (is12) pick("RL-63264-B", "cheapest 1/2 in. hole we sell, 25-pack");
      return out("PLUS", plusWhy);
    }

    // Up to 3/8 in.
    if (depth === "d2") {
      if (ownMaxOnly) pick("725MAX001");
      pick("715FL3638");
      return out(ownMaxOnly ? "MAX_ADAPTER" : "PLUS", ownMaxOnly ? SHANK_ADAPTER_WHY : "Holes to 3/8 in. are SDS-Plus work, and a 36 in. SDS-Plus bit reaches this deep.");
    }
    if (ownMaxOnly) {
      pick("725MAX001");
      if (job === "overhead") pick("STP-617");
      pick("715S61664");
      return out("MAX_ADAPTER", SHANK_ADAPTER_WHY);
    }
    if (job === "overhead") pick("STP-617");
    pick("715S61664");
    if (job === "overhead") pick("SDS-HD1", "sets 1/4 in. nail-in anchors");
    return out("PLUS", "Holes to 3/8 in. are SDS-Plus work: the hammer is light and the bits are cheap.");
  }

  function shankPriceText(sku, v) {
    var pieces = SHANK_PRODUCTS[sku][3];
    var min = SHANK_PRODUCTS[sku][2];
    var text;
    if (pieces > 1) {
      // Priced per pack: variant.price covers the whole pack.
      text = usd(v.price) + " per pack of " + pieces + ", " + usd(v.price / pieces) + " per bit";
    } else {
      text = usd(v.price) + " each" + (min > 1 ? " (min. " + min + ")" : "");
    }
    return text + (v.available ? "" : " · backorder");
  }

  function mountShankChooser(root) {
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--shank-chooser");
    root.innerHTML =
      '<div class="et-tool__finder">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">SDS shank chooser</div>' +
      '<span class="et-tool__meta">SDS-Plus or SDS-Max, for the hole you need</span></div>' +
      '<div class="et-tool__body">' +
      '<div data-wrap="hole"><div class="et-tool__label" id="' + uid + '-hole">1. Hole size</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-hole" data-chips="hole"></div></div>' +
      '<div data-wrap="depth"><div class="et-tool__label" id="' + uid + '-depth">2. Depth</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-depth" data-chips="depth"></div></div>' +
      '<div data-wrap="job"><div class="et-tool__label" id="' + uid + '-job">3. The job</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-job" data-chips="job"></div></div>' +
      '<div data-wrap="own"><div class="et-tool__label" id="' + uid + '-own">4. The hammer you own</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-own" data-chips="own"></div></div>' +
      '<div class="et-tool__result" aria-live="polite" data-result hidden></div>' +
      "</div></div>";

    var resultEl = root.querySelector("[data-result]");
    var state = { hole: null, depth: null, job: null, own: null };
    var GROUPS = { hole: SHANK_HOLES, depth: SHANK_DEPTHS, job: SHANK_JOBS, own: SHANK_OWN };

    Object.keys(GROUPS).forEach(function (key) {
      var box = root.querySelector('[data-chips="' + key + '"]');
      GROUPS[key].forEach(function (opt) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "et-tool__chip";
        btn.textContent = opt[1];
        btn.setAttribute("data-value", opt[0]);
        btn.setAttribute("aria-pressed", "false");
        btn.addEventListener("click", function () {
          state[key] = opt[0];
          render();
        });
        box.appendChild(btn);
      });
    });

    function addPick(listEl, p) {
      var item = SHANK_PRODUCTS[p.sku];
      var a = document.createElement("a");
      a.className = "et-tool__pick";
      a.href = "/products/" + item[0];
      a.innerHTML =
        '<span><span class="et-tool__pick-sku">' + esc(p.sku) + "</span> &middot; " + esc(item[1]) +
        (p.note ? ' <span class="et-tool__pick-note">&mdash; ' + esc(p.note) + "</span>" : "") + "</span>" +
        '<span class="et-tool__pick-price" hidden data-price></span>';
      listEl.appendChild(a);
      var priceEl = a.querySelector("[data-price]");
      fetchProduct(item[0]).then(function (product) {
        var v = product && product.variants && product.variants[0];
        if (!v || typeof v.price !== "number") return; // hide price, keep SKU + link
        priceEl.textContent = shankPriceText(p.sku, v);
        priceEl.hidden = false;
      });
    }

    function render() {
      var chisel = state.hole === "chisel";
      var core = state.hole === "core";
      root.querySelector('[data-wrap="depth"]').hidden = chisel;
      root.querySelector('[data-wrap="job"]').hidden = chisel || core;
      Array.prototype.forEach.call(root.querySelectorAll(".et-tool__chip"), function (c) {
        var key = c.parentNode.getAttribute("data-chips");
        c.setAttribute("aria-pressed", state[key] === c.getAttribute("data-value") ? "true" : "false");
      });

      resultEl.innerHTML = "";
      if (!state.hole || !state.own) {
        resultEl.hidden = true;
        return;
      }
      var a = shankAnswer(state);
      resultEl.hidden = false;
      resultEl.innerHTML =
        '<div class="et-tool__bigrec"><small>Shank</small><b>' + esc(SHANK_VERDICT[a.verdict]) + "</b></div>" +
        '<p class="et-tool__why">' + esc(a.reason) + "</p>" +
        a.hints.map(function (h) { return '<p class="et-tool__hint">' + esc(h) + "</p>"; }).join("") +
        '<div class="et-tool__picks" data-picks></div>';
      var picksEl = resultEl.querySelector("[data-picks]");
      a.picks.forEach(function (p) { addPick(picksEl, p); });
    }

    render();
  }

  // ---------------------------------------------------------------------
  // Versus card (versus-card): a side-by-side card drawn from the article's own comparison table
  // ---------------------------------------------------------------------

  function vsEl(tag, cls, text) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text != null) el.textContent = text;
    return el;
  }

  function vsText(node) {
    return String((node && node.textContent) || "").replace(/\s+/g, " ").trim();
  }

  /** Fills `el` from a table cell. Plain text uses textContent; a cell that holds a link is cloned
   * node by node (unwrapping a lone <p>, which is how the editor re-serializes cells) so the link
   * keeps working. The card is aria-hidden (the visually hidden source table is what assistive
   * tech reads), so cloned links leave the tab order instead of being focusable-but-hidden. */
  function vsFill(el, cell) {
    if (!cell.querySelector("a")) {
      el.textContent = vsText(cell);
      return;
    }
    var from = cell.children.length === 1 && cell.firstElementChild.tagName === "P" ? cell.firstElementChild : cell;
    Array.prototype.forEach.call(from.childNodes, function (n) {
      el.appendChild(n.cloneNode(true));
    });
    Array.prototype.forEach.call(el.querySelectorAll("[id]"), function (n) { n.removeAttribute("id"); });
    Array.prototype.forEach.call(el.querySelectorAll("a"), function (a) { a.setAttribute("tabindex", "-1"); });
  }

  function mountVersusCard(root) {
    root.hidden = true; // stays hidden unless a card is actually drawn

    // Walk back through the siblings: the first one that is (or wraps, as the theme's .et-table
    // frame does) a <table> is the source; a "Verdict:" paragraph met on the way is the verdict.
    var srcBlock = null;
    var table = null;
    var verdictP = null;
    for (var el = root.previousElementSibling; el; el = el.previousElementSibling) {
      if (el.classList.contains("et-tool")) break; // another tool's territory
      if (el.tagName === "TABLE") { srcBlock = el; table = el; break; }
      var inner = el.querySelector ? el.querySelector("table") : null;
      if (inner) { srcBlock = el; table = inner; break; }
      if (!verdictP && el.tagName === "P" && /^\s*verdict\s*:/i.test(el.textContent || "")) verdictP = el;
    }
    if (!table) return;

    var rows = Array.prototype.slice.call(table.rows);
    if (rows.length < 2 || rows[0].cells.length !== 3) return;
    var nameA = vsText(rows[0].cells[1]) || "Option A";
    var nameB = vsText(rows[0].cells[2]) || "Option B";
    var data = rows.slice(1).filter(function (r) { return r.cells.length === 3; });
    if (!data.length) return;

    var title = nameA + " vs " + nameB + " at a glance";
    var card = vsEl("div", "et-tool__vs");
    card.setAttribute("role", "group");
    card.setAttribute("aria-label", title);
    card.setAttribute("aria-hidden", "true");

    card.appendChild(vsEl("div", "et-tool__vs-title", title));

    var head = vsEl("div", "et-tool__vs-head");
    head.appendChild(vsEl("span", "et-tool__vs-gutter"));
    head.appendChild(vsEl("span", "et-tool__vs-pill et-tool__vs-pill--a", nameA));
    head.appendChild(vsEl("span", "et-tool__vs-pill et-tool__vs-pill--b", nameB));
    card.appendChild(head);

    data.forEach(function (r, i) {
      var row = vsEl("div", "et-tool__vs-row" + (i % 2 ? " is-alt" : ""));
      var trait = vsEl("div", "et-tool__vs-trait");
      vsFill(trait, r.cells[0]);
      row.appendChild(trait);
      [["a", nameA, r.cells[1]], ["b", nameB, r.cells[2]]].forEach(function (c) {
        var cell = vsEl("div", "et-tool__vs-cell et-tool__vs-cell--" + c[0]);
        cell.appendChild(vsEl("span", "et-tool__vs-eyebrow", c[1]));
        var body = vsEl("div", "et-tool__vs-text");
        vsFill(body, c[2]);
        cell.appendChild(body);
        row.appendChild(cell);
      });
      card.appendChild(row);
    });

    var verdict = verdictP ? vsText(verdictP).replace(/^\s*verdict\s*:\s*/i, "") : "";
    if (verdict) {
      var band = vsEl("div", "et-tool__vs-verdict");
      band.appendChild(vsEl("div", "et-tool__vs-verdict-label", "VERDICT"));
      band.appendChild(vsEl("p", "et-tool__vs-verdict-text", verdict));
      card.appendChild(band);
    }

    root.classList.add("et-tool--versus-card");
    root.appendChild(card);
    root.hidden = false;
    // Only now, after a good render, take the plain table out of sight. It stays in the HTML for
    // crawlers and screen readers (visually hidden, not display:none); if anything above had
    // thrown, it would still be on the page as the article's normal table.
    srcBlock.classList.add("et-tool__src-table");
  }

  // ---------------------------------------------------------------------
  // Hole explorer (hole-explorer): the seat a counterbore or an 82 degree countersink cuts for a
  // given screw, in a plate of a given thickness. Drawn to scale (1 in. = 220 SVG units).
  // The numbers below are mirrored in docs/blog/ARTICLE-TOOLS.md and in the guide's static tables
  // (rule T6); change all three together.
  // ---------------------------------------------------------------------

  // [numeric inches, display text]. Fractions where the article uses them, decimals elsewhere.
  function hn(v, d) {
    return { n: v, d: d };
  }
  // screw size, screw diameter, close-fit hole (the Weldon -2 pilot), pocket diameter, pocket depth
  // (= cap screw head height), flat head diameter (ASME B18.6.3 max), cone depth in a close-fit
  // hole = (head - hole) / 2 / tan 41 deg, then picks: counterbore, long series, piloted
  // countersink, pilotless flat-head countersink, deburr pick, hex-shank twin (null = none).
  var HOLE_SIZES = [
    { key: "#4", dia: 0.112, fit: hn(0.125, "0.125"), pocket: hn(0.199, "0.199"), depth: hn(0.112, "0.112"), head: hn(0.225, "0.225"), cone: hn(0.058, "0.058"), cb: "LGN4-2", lg: null, pil: null, pless: "CS-5S", deb: "CS-5S", hex: "CS-3H" },
    { key: "#6", dia: 0.138, fit: hn(0.15, "0.150"), pocket: hn(0.242, "0.242"), depth: hn(0.138, "0.138"), head: hn(0.279, "0.279"), cone: hn(0.074, "0.074"), cb: "LGN6-2", lg: null, pil: null, pless: "CS-6S", deb: "CS-6S", hex: "CS-8H" },
    { key: "#8", dia: 0.164, fit: hn(0.178, "0.178"), pocket: hn(0.29, "0.290"), depth: hn(0.164, "0.164"), head: hn(0.332, "0.332"), cone: hn(0.089, "0.089"), cb: "LGN8-2", lg: null, pil: null, pless: "CS-8S", deb: "CS-8S", hex: "CS-8H" },
    { key: "#10", dia: 0.19, fit: hn(0.204, "0.204"), pocket: hn(0.332, "0.332"), depth: hn(0.19, "0.190"), head: hn(0.385, "0.385"), cone: hn(0.104, "0.104"), cb: "LGN10-2", lg: null, pil: null, pless: "CS-10S", deb: "CS-10S", hex: "CS-18H" },
    { key: "1/4", dia: 0.25, fit: hn(0.2656, "17/64"), pocket: hn(0.4063, "13/32"), depth: hn(0.25, "0.250"), head: hn(0.507, "0.507"), cone: hn(0.139, "0.139"), cb: "LGS8-2", lg: "LGL8-2", pil: "CS8-2", pless: "CS-18", deb: "CS-18", hex: "CS-18H" },
    { key: "5/16", dia: 0.3125, fit: hn(0.3281, "21/64"), pocket: hn(0.5, "1/2"), depth: hn(0.312, "0.312"), head: hn(0.635, "0.635"), cone: hn(0.177, "0.177"), cb: "LGS10-2", lg: "LGL10-2", pil: "CS10-2", pless: "CS-26", deb: "CS-18", hex: "CS-18H" },
    { key: "3/8", dia: 0.375, fit: hn(0.3906, "25/64"), pocket: hn(0.5938, "19/32"), depth: hn(0.375, "0.375"), head: hn(0.762, "0.762"), cone: hn(0.214, "0.214"), cb: "LGS12-2", lg: "LGL12-2", pil: "CS12-2", pless: "CS-26", deb: "CS-18", hex: "CS-18H" },
    { key: "7/16", dia: 0.4375, fit: hn(0.4531, "29/64"), pocket: hn(0.6875, "11/16"), depth: hn(0.438, "0.438"), head: hn(0.812, "0.812"), cone: hn(0.206, "0.206"), cb: "LGS14-2", lg: "LGL14-2", pil: "CS14-2", pless: "CS-36", deb: "CS-18", hex: "CS-18H" },
    { key: "1/2", dia: 0.5, fit: hn(0.5156, "33/64"), pocket: hn(0.7813, "25/32"), depth: hn(0.5, "0.500"), head: hn(0.875, "0.875"), cone: hn(0.207, "0.207"), cb: "LGS16-2", lg: "LGL16-2", pil: "CS16-2", pless: "CS-48", deb: "CS-36", hex: null },
  ];

  // sku -> [handle, short title]. Handles are stored, never derived from the SKU. Every item is
  // sold individually (minimum 1), so the price is "$X.XX each" straight from variants[0].price.
  var HOLE_PRODUCTS = {
    "LGN4-2": ["lgn4-2", "Counterbore for #4 screw, .125 pilot"],
    "LGN6-2": ["lgn6-2", "Counterbore for #6 screw, .150 pilot"],
    "LGN8-2": ["lgn8-2", "Counterbore for #8 screw, .178 pilot"],
    "LGN10-2": ["lgn10-2", "Counterbore for #10 screw, .204 pilot"],
    "LGS8-2": ["lgs8-2", "Counterbore for 1/4 in. screw, 17/64 pilot"],
    "LGS10-2": ["lgs10-2", "Counterbore for 5/16 in. screw, 21/64 pilot"],
    "LGS12-2": ["lgs12-2", "Counterbore for 3/8 in. screw, 25/64 pilot"],
    "LGS14-2": ["lgs14-2", "Counterbore for 7/16 in. screw, 29/64 pilot"],
    "LGS16-2": ["lgs16-2", "Counterbore for 1/2 in. screw, 33/64 pilot"],
    "LGL8-2": ["lgl8-2", "Long-series counterbore, 1/4 in. screw"],
    "LGL10-2": ["lgl10-2", "Long-series counterbore, 5/16 in. screw"],
    "LGL12-2": ["lgl12-2", "Long-series counterbore, 3/8 in. screw"],
    "LGL14-2": ["lgl14-2", "Long-series counterbore, 7/16 in. screw"],
    "LGL16-2": ["lgl16-2", "Long-series counterbore, 1/2 in. screw"],
    "CS8-2": ["cs8-2", "82° piloted countersink, 1/4 in. screw"],
    "CS10-2": ["cs10-2", "82° piloted countersink, 5/16 in. screw"],
    "CS12-2": ["cs12-2", "82° piloted countersink, 3/8 in. screw"],
    "CS14-2": ["cs14-2", "82° piloted countersink, 7/16 in. screw"],
    "CS16-2": ["cs16-2", "82° piloted countersink, 1/2 in. screw"],
    "CS-5S": ["cs-5s", "82° pilotless countersink, #4 and #5 screws"],
    "CS-6S": ["cs-6s", "82° pilotless countersink, #6 screws"],
    "CS-8S": ["cs-8s", "82° pilotless countersink, #8 screws"],
    "CS-10S": ["cs-10s", "82° pilotless countersink, #10 screws"],
    "CS-18": ["cs-18", "82° pilotless countersink, 3/16 to 17/32 in."],
    "CS-26": ["cs-26", "82° pilotless countersink, .307 to 25/32 in."],
    "CS-36": ["cs-36", "82° pilotless countersink, .431 to 7/8 in."],
    "CS-48": ["cs-48", "82° pilotless countersink, 1/2 to 1-15/32 in."],
    "CS-3H": ["cs-3h", "82° pilotless countersink, hex shank, 5/64 to 15/64 in."],
    "CS-8H": ["cs-8h", "82° pilotless countersink, hex shank, 1/8 to 11/32 in."],
    "CS-18H": ["cs-18h", "82° pilotless countersink, hex shank, 3/16 to 17/32 in."],
  };

  var HOLE_FASTENERS = [["cap", "Socket head cap screw"], ["flat", "82° flat-head screw"], ["deburr", "Deburr only"]];
  var HOLE_STYLES = [["piloted", "Piloted"], ["pilotless", "Pilotless"]];
  var HOLE_SCALE = 220; // SVG units per inch
  var HOLE_TAN41 = Math.tan((41 * Math.PI) / 180);

  function holeSize(key) {
    for (var i = 0; i < HOLE_SIZES.length; i++) {
      if (HOLE_SIZES[i].key === key) return HOLE_SIZES[i];
    }
    return HOLE_SIZES[4];
  }

  function holeIn(x) {
    return (Math.round(x * 1000) / 1000).toFixed(3);
  }

  /**
   * The rules. Pure function of the four controls; no DOM, no fetch.
   * Returns { verdict, reason, hints: [{text, level}], picks: [{sku, note}] } where level is
   * "warn" or "bad" (omitted for a plain hint).
   */
  function holeAnswer(fastener, size, style, thickness) {
    var sz = holeSize(size);
    var hints = [];
    var picks = [];
    var verdict, reason;
    if (fastener === "cap") {
      verdict = "Counterbore";
      reason =
        "A cap screw head is flat underneath, so it needs a flat shelf: a " + sz.pocket.d + " in. pocket, " +
        sz.depth.d + " in. deep (the head height), with a " + sz.fit.d + " in. pilot for a close-fit hole.";
      var floor = Math.round((thickness - sz.depth.n) * 1000000) / 1000000;
      if (thickness <= sz.depth.n) {
        hints.push({
          level: "bad",
          text: "Your plate is thinner than the head is tall, so the pocket cuts straight through. Use a button-head or low-head cap screw instead.",
        });
      } else if (floor < sz.dia / 2) {
        hints.push({
          level: "warn",
          text: "Only " + holeIn(floor) + " in. of metal is left under the pocket. Keep at least half the screw diameter of solid floor, or step down a size.",
        });
      }
      picks.push({ sku: sz.cb });
      if (sz.lg) picks.push({ sku: sz.lg, note: "long series, for a pocket at the bottom of a recess" });
    } else if (fastener === "flat") {
      verdict = "82° countersink";
      reason =
        "A flat head is 82° underneath; the cone matches it so the head sits flush. The cone is " + sz.head.d +
        " in. across and " + sz.cone.d + " in. deep in a close-fit hole. " +
        (style === "piloted"
          ? "The pilot rides in the hole, so the cone comes out centered."
          : "The cone centers itself on the hole edge, and one tool covers a range of holes.");
      if (sz.cone.n >= thickness) {
        hints.push({
          level: "bad",
          text: "The cone would break through the back of the plate. The plate is too thin for a flush flat head at this size; use a smaller screw or a thicker plate.",
        });
      } else if (thickness < sz.cone.n + 0.03) {
        hints.push({ level: "warn", text: "Only a knife edge of metal is left under the head." });
      }
      if (style === "piloted" && sz.pil) {
        picks.push({ sku: sz.pil });
      } else {
        if (style === "piloted") hints.push({ text: "Piloted countersinks start at 1/4 in.; for #4 to #10 use the pilotless tool." });
        picks.push({ sku: sz.pless });
      }
    } else {
      verdict = "Pilotless countersink";
      reason =
        "A few turns in the hole edge knock the burr off and leave an even chamfer; the tool just has to cover the hole size.";
      hints.push({ text: "Hex-shank versions drop into a quick-change chuck or an impact driver for field work." });
      picks.push({ sku: sz.deb });
      if (sz.hex) picks.push({ sku: sz.hex, note: "hex-shank twin" });
    }
    return { verdict: verdict, reason: reason, hints: hints, picks: picks };
  }

  // Tool silhouettes for the small inset at the top left of the drawing. Local box is 120 x 80;
  // the cutter points down. kind: "counterbore" | "piloted" | "pilotless".
  function holeInset(kind, c) {
    var g = '<g transform="translate(8 8)">';
    g += '<rect x="0" y="0" width="120" height="80" rx="4" fill="' + c.paper + '" stroke="' + c.line + '" stroke-width="1.5"></rect>';
    var fill = 'fill="' + c.muted + '" stroke="' + c.ink + '" stroke-width="1.5" stroke-linejoin="round"';
    var name;
    if (kind === "counterbore") {
      name = "Counterbore";
      g += '<rect x="52" y="4" width="16" height="10" ' + fill + "></rect>"; // shank
      g += '<rect x="34" y="14" width="52" height="26" ' + fill + "></rect>"; // body
      g += '<g stroke="' + c.ink + '" stroke-width="1.5"><line x1="46" y1="38" x2="52" y2="16"></line><line x1="60" y1="38" x2="66" y2="16"></line><line x1="74" y1="38" x2="80" y2="16"></line></g>';
      g += '<rect x="51" y="40" width="18" height="16" ' + fill + "></rect>"; // solid pilot
    } else {
      g += '<rect x="52" y="4" width="16" height="10" ' + fill + "></rect>"; // shank
      if (kind === "piloted") {
        name = "Piloted";
        g += '<polygon points="36,14 84,14 66,40 54,40" ' + fill + "></polygon>";
        g += '<rect x="55" y="40" width="10" height="16" ' + fill + "></rect>";
      } else {
        name = "Pilotless";
        g += '<polygon points="36,14 84,14 66,44 54,44" ' + fill + "></polygon>";
        g += '<circle cx="60" cy="28" r="4.5" fill="' + c.paper + '" stroke="' + c.ink + '" stroke-width="1.5"></circle>'; // cross-hole
      }
    }
    g += '<text x="60" y="73" text-anchor="middle" font-family="Montserrat, sans-serif" font-size="16" fill="' + c.ink + '">' + name + "</text>";
    return g + "</g>";
  }

  function mountHoleExplorer(root) {
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--hole-explorer");
    root.innerHTML =
      '<div class="et-tool__finder">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">Hole explorer</div>' +
      '<span class="et-tool__meta">The seat for your screw, in your plate</span></div>' +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-fast">Fastener</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-fast" data-chips="fastener"></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-size">Screw size (hole size for deburring)</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-size" data-chips="size"></div></div>' +
      '<div data-wrap="style"><div class="et-tool__label" id="' + uid + '-style">Countersink style</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-style" data-chips="style"></div></div>' +
      '<div class="et-tool__sliders et-tool__hole-sliders">' +
      '<label class="et-tool__slider-label">Plate thickness <output data-out>0.375 in.</output>' +
      '<input type="range" data-thick min="0.0625" max="1" step="0.015625" value="0.375" ' +
      'aria-label="Plate thickness in inches"></label></div>' +
      '<svg class="et-tool__svg et-tool__hole-svg" viewBox="0 0 640 300" role="img" aria-label="" data-svg></svg>' +
      '<div class="et-tool__result" aria-live="polite" data-result>' +
      '<div class="et-tool__bigrec"><small>Cut this</small><b data-verdict></b></div>' +
      '<p class="et-tool__why" data-reason></p>' +
      '<div class="et-tool__hole-hints" data-hints></div>' +
      '<div class="et-tool__picks" data-picks></div>' +
      "</div></div></div>";

    var svg = root.querySelector("[data-svg]");
    var thickInput = root.querySelector("[data-thick]");
    var outEl = root.querySelector("[data-out]");
    var verdictEl = root.querySelector("[data-verdict]");
    var reasonEl = root.querySelector("[data-reason]");
    var hintsEl = root.querySelector("[data-hints]");
    var picksEl = root.querySelector("[data-picks]");
    var styleWrap = root.querySelector('[data-wrap="style"]');
    var state = { fastener: "cap", size: "1/4", style: "piloted" };
    var lastHints = null;
    var lastPicks = null;

    function cssVar(name) {
      return getComputedStyle(root).getPropertyValue(name).trim();
    }

    function makeChips(key, opts) {
      var box = root.querySelector('[data-chips="' + key + '"]');
      opts.forEach(function (opt) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "et-tool__chip";
        btn.textContent = opt[1];
        btn.setAttribute("data-value", opt[0]);
        btn.setAttribute("aria-pressed", "false");
        btn.addEventListener("click", function () {
          state[key] = opt[0];
          render();
        });
        box.appendChild(btn);
      });
    }
    makeChips("fastener", HOLE_FASTENERS);
    makeChips(
      "size",
      HOLE_SIZES.map(function (s) {
        return [s.key, s.key];
      })
    );
    makeChips("style", HOLE_STYLES);

    function addPick(sku, note) {
      var item = HOLE_PRODUCTS[sku];
      if (!item) return;
      var a = document.createElement("a");
      a.className = "et-tool__pick";
      a.href = "/products/" + item[0];
      a.innerHTML =
        '<span><span class="et-tool__pick-sku">' + esc(sku) + "</span> &middot; " + esc(item[1]) +
        (note ? ' <span class="et-tool__pick-note">&mdash; ' + esc(note) + "</span>" : "") + "</span>" +
        '<span class="et-tool__pick-price" hidden data-price></span>';
      picksEl.appendChild(a);
      var priceEl = a.querySelector("[data-price]");
      try {
        fetchProduct(item[0]).then(function (product) {
          var v = product && product.variants && product.variants[0];
          if (!v || typeof v.price !== "number") return; // hide price, keep SKU + link
          priceEl.textContent = usd(v.price) + " each" + (v.available === false ? " · backorder" : "");
          priceEl.hidden = false;
        });
      } catch (e) {
        /* no fetch available: the price just stays hidden */
      }
    }

    function draw(th, a, sz) {
      var S = HOLE_SCALE;
      var cx = 320, x0 = 40, bot = 270;
      var ink = cssVar("--ett-ink") || "#161A20";
      var line = cssVar("--ett-line") || "#E4DCCB";
      var paper = cssVar("--ett-cream") || "#F4F4F4";
      var blue = cssVar("--ett-blue") || "#2E6FB0";
      var muted = cssVar("--ett-muted") || "#5B6573";
      var thPx = th * S;
      var top = bot - thPx;
      var halfHole = (sz.fit.n * S) / 2;
      var f = state.fastener;
      var font = 'font-family="Spline Sans Mono, monospace" font-size="16" fill="' + ink + '"';

      // Left half of the plate, outer top corner to outer bottom corner. The right half is the
      // mirror image about the centre line. cut = the points that are cut surfaces.
      var pts, seatW, seatText, floorY, headPoly, headRect, shankTop;
      var pdPx = sz.depth.n * S;
      var cdPx = sz.cone.n * S;
      var halfSeat;
      if (f === "cap") {
        halfSeat = (sz.pocket.n * S) / 2;
        var breakthrough = pdPx >= thPx;
        floorY = top + Math.min(pdPx, thPx);
        pts = breakthrough
          ? [[x0, top], [cx - halfSeat, top], [cx - halfSeat, bot], [x0, bot]]
          : [[x0, top], [cx - halfSeat, top], [cx - halfSeat, floorY], [cx - halfHole, floorY], [cx - halfHole, bot], [x0, bot]];
        seatW = halfSeat * 2;
        seatText = "pocket " + sz.pocket.d + " in. wide, " + sz.depth.d + " in. deep";
        // The head rests on the pocket floor with its top 0.02 in. below the surface.
        var headTop = top + 0.02 * S;
        headRect = { x: cx - ((sz.pocket.n - 0.03) * S) / 2, y: headTop, w: (sz.pocket.n - 0.03) * S, h: Math.max(2, floorY - headTop) };
        shankTop = floorY;
      } else if (f === "flat") {
        halfSeat = (sz.head.n * S) / 2;
        var yB = top + Math.min(cdPx, thPx);
        var wB = halfHole + (halfSeat - halfHole) * (1 - (yB - top) / cdPx);
        pts = cdPx < thPx
          ? [[x0, top], [cx - halfSeat, top], [cx - halfHole, yB], [cx - halfHole, bot], [x0, bot]]
          : [[x0, top], [cx - halfSeat, top], [cx - wB, bot], [x0, bot]];
        seatW = halfSeat * 2;
        seatText = "cone " + sz.head.d + " in. across, " + sz.cone.d + " in. deep";
        headPoly = [[cx - halfSeat, top], [cx + halfSeat, top], [cx + wB, yB], [cx - wB, yB]];
        shankTop = yB;
      } else {
        var c = 0.03 * S;
        halfSeat = halfHole + c * HOLE_TAN41;
        pts = [[x0, top], [cx - halfSeat, top], [cx - halfHole, top + c], [cx - halfHole, bot], [x0, bot]];
        seatW = halfSeat * 2;
        seatText = "0.03 in. chamfer each side";
      }

      function poly(arr, attrs) {
        return '<polygon points="' + arr.map(function (p) { return p[0].toFixed(1) + "," + p[1].toFixed(1); }).join(" ") + '" ' + attrs + "></polygon>";
      }
      function mirror(arr) {
        return arr.map(function (p) { return [2 * cx - p[0], p[1]]; }).reverse();
      }
      var s = "";
      var plateAttrs = 'fill="' + paper + '" stroke="' + line + '" stroke-width="2" stroke-linejoin="round"';
      var hatchId = uid + "-hatch";
      s += '<defs><pattern id="' + hatchId + '" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">' +
        '<line x1="0" y1="0" x2="0" y2="10" stroke="' + line + '" stroke-width="1.5"></line></pattern></defs>';
      s += poly(pts, plateAttrs) + poly(mirror(pts), plateAttrs);
      var hatchAttrs = 'fill="url(#' + hatchId + ')" stroke="none"';
      s += poly(pts, hatchAttrs) + poly(mirror(pts), hatchAttrs);
      // cut surfaces in ink: the points between the two outer corners, both sides
      var cutL = pts.slice(1, pts.length - 1);
      function polyline(arr) {
        return '<polyline points="' + arr.map(function (p) { return p[0].toFixed(1) + "," + p[1].toFixed(1); }).join(" ") +
          '" fill="none" stroke="' + ink + '" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"></polyline>';
      }
      s += polyline(cutL) + polyline(cutL.map(function (p) { return [2 * cx - p[0], p[1]]; }));

      var screwAttrs = 'fill="' + blue + '" fill-opacity="0.85" stroke="' + ink + '" stroke-width="1.5" stroke-linejoin="round"';
      var shankW = sz.dia * S;
      if (f !== "deburr") {
        if (f === "cap") {
          s += '<rect x="' + (cx - shankW / 2).toFixed(1) + '" y="' + shankTop.toFixed(1) + '" width="' + shankW.toFixed(1) +
            '" height="' + (bot + 0.15 * S - shankTop).toFixed(1) + '" ' + screwAttrs + "></rect>";
          s += '<rect x="' + headRect.x.toFixed(1) + '" y="' + headRect.y.toFixed(1) + '" width="' + headRect.w.toFixed(1) +
            '" height="' + headRect.h.toFixed(1) + '" rx="2" ' + screwAttrs + "></rect>";
        } else {
          s += '<rect x="' + (cx - shankW / 2).toFixed(1) + '" y="' + shankTop.toFixed(1) + '" width="' + shankW.toFixed(1) +
            '" height="' + (bot + 0.15 * S - shankTop).toFixed(1) + '" ' + screwAttrs + "></rect>";
          s += poly(headPoly, screwAttrs);
        }
      }

      // Plate thickness dimension (left) and its label under the plate.
      var dx = 66;
      s += '<g stroke="' + ink + '" stroke-width="1.5" fill="none"><line x1="' + dx + '" y1="' + top.toFixed(1) + '" x2="' + dx + '" y2="' + bot + '"></line>' +
        '<line x1="' + (dx - 6) + '" y1="' + top.toFixed(1) + '" x2="' + (dx + 6) + '" y2="' + top.toFixed(1) + '"></line>' +
        '<line x1="' + (dx - 6) + '" y1="' + bot + '" x2="' + (dx + 6) + '" y2="' + bot + '"></line></g>';
      s += '<text x="48" y="292" ' + font + ">plate " + holeIn(th) + " in.</text>";
      s += '<text x="' + (cx + 68) + '" y="292" ' + font + ">hole " + esc(sz.fit.d) + " in.</text>";
      // Seat dimension (above the plate).
      var dy = top - 14;
      s += '<g stroke="' + ink + '" stroke-width="1.5" fill="none"><line x1="' + (cx - seatW / 2).toFixed(1) + '" y1="' + dy.toFixed(1) +
        '" x2="' + (cx + seatW / 2).toFixed(1) + '" y2="' + dy.toFixed(1) + '"></line>' +
        '<line x1="' + (cx - seatW / 2).toFixed(1) + '" y1="' + (dy - 6).toFixed(1) + '" x2="' + (cx - seatW / 2).toFixed(1) + '" y2="' + (dy + 6).toFixed(1) + '"></line>' +
        '<line x1="' + (cx + seatW / 2).toFixed(1) + '" y1="' + (dy - 6).toFixed(1) + '" x2="' + (cx + seatW / 2).toFixed(1) + '" y2="' + (dy + 6).toFixed(1) + '"></line></g>';
      s += '<text x="' + cx + '" y="' + (dy - 10).toFixed(1) + '" text-anchor="middle" ' + font + ">" + esc(seatText) + "</text>";

      var kind = f === "cap" ? "counterbore" : f === "flat" && state.style === "piloted" ? "piloted" : "pilotless";
      if (f === "flat" && state.style === "piloted" && !sz.pil) kind = "pilotless";
      s += holeInset(kind, { ink: ink, line: line, paper: paper, muted: muted });
      svg.innerHTML = s;

      // The label states the seat in words, including what is left under it.
      var label;
      var left = Math.round((th - (f === "cap" ? sz.depth.n : f === "flat" ? sz.cone.n : 0)) * 1000) / 1000;
      if (f === "cap") {
        label = "Counterbore pocket for a " + sz.key + " socket head cap screw: " + sz.pocket.d + " inch wide and " + sz.depth.d +
          " inch deep, over a " + sz.fit.d + " inch hole, in a plate " + holeIn(th) + " inch thick. " +
          (left > 0 ? holeIn(left) + " inch of metal is left under the pocket." : "The pocket cuts straight through the plate.");
      } else if (f === "flat") {
        label = (state.style === "piloted" && sz.pil ? "Piloted" : "Pilotless") + " 82 degree countersink for a " + sz.key +
          " flat-head screw: a cone " + sz.head.d + " inch across and " + sz.cone.d + " inch deep, over a " + sz.fit.d +
          " inch hole, in a plate " + holeIn(th) + " inch thick. " +
          (left > 0 ? holeIn(left) + " inch of metal is left under the cone." : "The cone breaks through the back of the plate.");
      } else {
        label = "A " + sz.fit.d + " inch hole in a plate " + holeIn(th) + " inch thick, with a 0.03 inch deep 82 degree chamfer on the top edge.";
      }
      svg.setAttribute("aria-label", label);
    }

    function render() {
      var th = parseFloat(thickInput.value);
      if (!(th > 0)) th = 0.375;
      var sz = holeSize(state.size);
      styleWrap.hidden = state.fastener !== "flat";
      Array.prototype.forEach.call(root.querySelectorAll(".et-tool__chip"), function (c) {
        var key = c.parentNode.getAttribute("data-chips");
        c.setAttribute("aria-pressed", state[key] === c.getAttribute("data-value") ? "true" : "false");
      });
      outEl.textContent = th.toFixed(3) + " in.";
      var a = holeAnswer(state.fastener, state.size, state.style, th);
      draw(th, a, sz);

      if (verdictEl.textContent !== a.verdict) verdictEl.textContent = a.verdict;
      if (reasonEl.textContent !== a.reason) reasonEl.textContent = a.reason;
      var hintsKey = a.hints.map(function (h) { return (h.level || "") + ":" + h.text; }).join("|");
      if (hintsKey !== lastHints) {
        lastHints = hintsKey;
        hintsEl.innerHTML = a.hints
          .map(function (h) {
            return '<p class="et-tool__hint' + (h.level ? " et-tool__hint--" + h.level : "") + '">' + esc(h.text) + "</p>";
          })
          .join("");
        hintsEl.hidden = a.hints.length === 0;
      }
      // Picks only rebuild (and refetch prices, which fetchProduct caches per handle) when the
      // list actually changes, so dragging the slider does not flicker the product rows.
      var picksKey = a.picks.map(function (p) { return p.sku + ":" + (p.note || ""); }).join("|");
      if (picksKey !== lastPicks) {
        lastPicks = picksKey;
        picksEl.innerHTML = "";
        a.picks.forEach(function (p) { addPick(p.sku, p.note); });
      }
    }

    thickInput.addEventListener("input", render);
    thickInput.addEventListener("change", render);
    render();
  }

  // ---------------------------------------------------------------------
  // Speed fit chart (speed-fit): a bar chart of every row's rated maximum RPM against the reader's
  // grinder speed, drawn from the article's own table. No data of its own and no fetch; the table
  // stays visible (the chart does not reproduce every column).
  // ---------------------------------------------------------------------

  var SF_DEFAULT_RPM = 11000;
  var SF_PRESETS = [
    { label: '4-1/2" grinder', rpm: 11000 },
    { label: '5" grinder', rpm: 10000 },
    { label: '7" grinder', rpm: 8500 },
    { label: '9" grinder', rpm: 6600 },
  ];
  var SF_SVG_NS = "http://www.w3.org/2000/svg";
  var SF_TOP = 34;
  var SF_ROW_H = 40;
  var SF_BAR_H = 18;
  var SF_GUTTER_L = 150;
  var SF_GUTTER_R = 150;
  var SF_W = 720;

  function sfText(node) {
    return String((node && node.textContent) || "").replace(/\s+/g, " ").trim();
  }

  function sfFmt(n) {
    return Number(n).toLocaleString("en-US");
  }

  function sfSvg(tag, attrs) {
    var el = document.createElementNS(SF_SVG_NS, tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) { el.setAttribute(k, attrs[k]); });
    }
    return el;
  }

  function sfLabel(x, y, str, attrs) {
    var t = sfSvg("text", attrs);
    t.setAttribute("x", String(x));
    t.setAttribute("y", String(y));
    t.textContent = str;
    return t;
  }

  /** The first element that is, or contains, a table: forward through the next siblings, then back
   * through the previous ones. A direction stops at another tool marker or an H2/H3 heading. */
  function sfFindTable(root) {
    var dirs = ["nextElementSibling", "previousElementSibling"];
    for (var d = 0; d < dirs.length; d++) {
      for (var el = root[dirs[d]]; el; el = el[dirs[d]]) {
        if (el.classList.contains("et-tool") || /^H[23]$/.test(el.tagName)) break;
        if (el.tagName === "TABLE") return el;
        var inner = el.querySelector ? el.querySelector("table") : null;
        if (inner) return inner;
      }
    }
    return null;
  }

  /** Reads the table into { noun, rows: [{ name, sub, rpm, sku }] }, or null when it is unusable. */
  function sfParse(table) {
    var all = Array.prototype.slice.call(table.rows);
    if (all.length < 2) return null;
    var head = all[0].cells;
    var rpmCol = -1;
    for (var i = 0; i < head.length; i++) {
      if (/rpm/i.test(sfText(head[i]))) { rpmCol = i; break; }
    }
    if (rpmCol < 0) return null;
    var subCol = head.length >= 4 && rpmCol !== 1 ? 1 : -1;
    var last = head.length - 1;
    var skuCol = last > 0 && last !== rpmCol && last !== subCol && /sku|part|product|model/i.test(sfText(head[last])) ? last : -1;

    var rows = [];
    all.slice(1).forEach(function (r) {
      if (r.cells.length < 2 || rpmCol >= r.cells.length) return;
      var rpm = parseInt(sfText(r.cells[rpmCol]).replace(/\D/g, ""), 10);
      if (!(rpm > 0)) return;
      rows.push({
        name: sfText(r.cells[0]),
        sub: subCol >= 0 && subCol < r.cells.length ? sfText(r.cells[subCol]) : "",
        rpm: rpm,
        sku: skuCol >= 0 && skuCol < r.cells.length ? sfText(r.cells[skuCol]) : "",
      });
    });
    if (!rows.length) return null;
    return { noun: sfText(head[0]).toLowerCase() || "item", rows: rows };
  }

  function mountSpeedFit(root) {
    root.hidden = true; // stays hidden unless a chart is actually drawn

    var table = sfFindTable(root);
    if (!table) return;
    var data = sfParse(table);
    if (!data) return;
    var rows = data.rows;
    var total = rows.length;
    var nouns = total === 1 ? data.noun : data.noun + "s";

    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--speed-fit");
    root.innerHTML =
      '<div class="et-tool__finder">' +
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">Speed check</div>' +
      '<span class="et-tool__meta">Which sizes can your grinder spin?</span></div>' +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-g">Your grinder</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-g" data-chips></div></div>' +
      '<div class="et-tool__row"><label>Nameplate speed (RPM)' +
      '<input type="number" inputmode="numeric" min="1000" max="30000" step="100" value="' + SF_DEFAULT_RPM + '" data-rpm></label>' +
      '<span class="et-tool__sf-hint">Typical for that size; the speed on your grinder\'s label wins.</span></div>' +
      '<p class="et-tool__sf-verdict" aria-live="polite" data-verdict></p>' +
      '<div class="et-tool__sf-chart"><svg role="img" data-chart></svg></div>' +
      '<div class="et-tool__sf-legend" data-legend></div>' +
      "</div></div>";

    var chipsEl = root.querySelector("[data-chips]");
    var input = root.querySelector("[data-rpm]");
    var verdictEl = root.querySelector("[data-verdict]");
    var svg = root.querySelector("[data-chart]");
    var legendEl = root.querySelector("[data-legend]");
    var state = { rpm: SF_DEFAULT_RPM };

    function readInput() {
      var v = parseInt(input.value, 10);
      return v > 0 ? v : SF_DEFAULT_RPM;
    }

    function syncChips() {
      Array.prototype.forEach.call(chipsEl.querySelectorAll(".et-tool__chip"), function (c, i) {
        c.setAttribute("aria-pressed", SF_PRESETS[i].rpm === state.rpm ? "true" : "false");
      });
    }

    SF_PRESETS.forEach(function (p) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "et-tool__chip";
      btn.textContent = p.label;
      btn.setAttribute("aria-pressed", "false");
      btn.addEventListener("click", function () {
        input.value = String(p.rpm);
        render();
      });
      chipsEl.appendChild(btn);
    });

    [
      ["--ett-good", "Rated at or above your grinder"],
      ["--ett-bad", "Rated below it: do not use"],
      ["--ett-blue", "Your grinder's speed"],
    ].forEach(function (l) {
      var s = document.createElement("span");
      s.style.setProperty("--c", "var(" + l[0] + ")");
      s.textContent = l[1];
      legendEl.appendChild(s);
    });

    function drawVerdict(g) {
      var fit = rows.filter(function (r) { return r.rpm >= g; });
      var slow = rows.filter(function (r) { return r.rpm < g; });
      var names = function (list) {
        return list.map(function (r) { return esc(r.name); }).join(", ");
      };
      var html = "<strong>" + fit.length + " of " + total + "</strong> " + esc(nouns) + " are rated for " + sfFmt(g) + " RPM";
      if (!fit.length) {
        html += "; none of these fit.";
      } else {
        html += ". Fits: " + names(fit) + ".";
        if (slow.length) html += " Too slow-rated: " + names(slow) + ".";
      }
      verdictEl.innerHTML = html;
    }

    function drawChart(g) {
      var maxRpm = rows.reduce(function (m, r) { return Math.max(m, r.rpm); }, g);
      var axisMax = Math.max(10000, Math.ceil((maxRpm * 1.05) / 5000) * 5000);
      var plotW = SF_W - SF_GUTTER_L - SF_GUTTER_R;
      var bottom = SF_TOP + rows.length * SF_ROW_H;
      var h = bottom + 28;
      function x(v) { return SF_GUTTER_L + (v / axisMax) * plotW; }

      svg.setAttribute("viewBox", "0 0 " + SF_W + " " + h);
      svg.setAttribute("aria-label", "Max RPM of each " + data.noun + " against your grinder speed of " + sfFmt(g) + " RPM");
      while (svg.firstChild) svg.removeChild(svg.firstChild);

      for (var t = 0; t <= axisMax; t += 5000) {
        svg.appendChild(sfSvg("line", { x1: x(t), x2: x(t), y1: SF_TOP, y2: bottom, stroke: "var(--ett-line)", "stroke-width": 1 }));
        svg.appendChild(sfLabel(x(t), bottom + 17, t === 0 ? "0" : t / 1000 + "k", { "text-anchor": "middle", "font-size": 11.5, fill: "var(--ett-muted)" }));
      }

      var gx = x(g);
      svg.appendChild(sfSvg("rect", { x: gx - 4, y: SF_TOP - 8, width: 8, height: bottom - SF_TOP + 8, fill: "var(--ett-blue)", "fill-opacity": 0.18 }));

      rows.forEach(function (r, i) {
        var y0 = SF_TOP + i * SF_ROW_H;
        var ok = r.rpm >= g;
        var colour = ok ? "var(--ett-good)" : "var(--ett-bad)";
        var barEnd = x(r.rpm);

        svg.appendChild(sfLabel(SF_GUTTER_L - 10, y0 + (r.sub ? 18 : 25), r.name, { "text-anchor": "end", "font-size": 14, "font-weight": 700, fill: "var(--ett-ink)" }));
        if (r.sub) {
          svg.appendChild(sfLabel(SF_GUTTER_L - 10, y0 + 33, r.sub, { "text-anchor": "end", "font-size": 11.5, fill: "var(--ett-muted)" }));
        }

        var bar = sfSvg("rect", {
          x: SF_GUTTER_L, y: y0 + (SF_ROW_H - SF_BAR_H) / 2, width: Math.max(2, barEnd - SF_GUTTER_L), height: SF_BAR_H,
          rx: 3, fill: colour, "fill-opacity": 0.85,
        });
        var title = sfSvg("title");
        title.textContent = r.name + (r.sku ? " (" + r.sku + ")" : "") + ": rated " + sfFmt(r.rpm) + " RPM max";
        bar.appendChild(title);
        svg.appendChild(bar);

        var tx = Math.max(barEnd, gx) + 10;
        svg.appendChild(sfLabel(tx, y0 + (r.sku ? 18 : 25), (ok ? "Fits" : "Too slow-rated") + " · " + sfFmt(r.rpm), { "font-size": 13.5, "font-weight": 700, fill: colour }));
        if (r.sku) {
          svg.appendChild(sfLabel(tx, y0 + 33, r.sku, { "class": "et-tool__sf-mono", "font-size": 11.5, fill: "var(--ett-muted)" }));
        }
      });

      svg.appendChild(sfSvg("line", { x1: gx, x2: gx, y1: SF_TOP - 8, y2: bottom, stroke: "var(--ett-blue)", "stroke-width": 2 }));
      svg.appendChild(sfLabel(gx, SF_TOP - 14, "Your grinder " + sfFmt(g), { "text-anchor": "middle", "font-size": 13, "font-weight": 700, fill: "var(--ett-blue)" }));
    }

    function render() {
      state.rpm = readInput();
      syncChips();
      drawVerdict(state.rpm);
      drawChart(state.rpm);
    }

    input.addEventListener("input", render);
    input.addEventListener("change", render);
    render();
    root.hidden = false;
  }

  // ---------------------------------------------------------------------
  // Silver & Deming tools (sd-bolt-finder, sd-price-ladder, sd-speed-dial, sd-kit-builder), built
  // 2026-10-29 for the guide "Silver and Deming Drill Bits: Big Holes From a 1/2 in. Chuck". Like the
  // speed fit chart they have NO data of their own, no attributes and no fetch: they read the
  // article's own visible tables (found by the exact text of a header cell, within the article body)
  // and the size table is the only price source. There are no stock flags: the store sells on
  // continue-selling inventory, so a hardcoded "out of stock" list would only go stale.
  // ---------------------------------------------------------------------

  var SD_SVG_W = 720;
  var SD_SVG_H = 300;
  var SD_PL = 46;
  var SD_PR = 14;
  var SD_PT = 14;
  var SD_PB = 34;
  var SD_PRESETS = [
    { label: "8-piece, 9/16 to 1 in.", sizes: ["9/16", "5/8", "11/16", "3/4", "13/16", "7/8", "15/16", "1"] },
    { label: "Structural bolt holes", sizes: ["9/16", "11/16", "13/16", "15/16", "1-1/16"] },
    { label: "Big tap drills", sizes: ["17/32", "37/64", "21/32", "11/16", "49/64", "13/16", "7/8"] },
    { label: "Pipe tap drills", sizes: ["37/64", "23/32", "59/64", "1-5/32", "1-1/2"] },
  ];

  /** The article body this marker sits in; the tools only ever read tables from here. */
  function sdScope(root) {
    return root.closest(".et-art__prose") || root.closest("article") || document;
  }

  function sdText(node) {
    return String((node && node.textContent) || "").replace(/ /g, " ").replace(/\s+/g, " ").trim();
  }

  /** "1-1/16" -> 1.0625, "11/16 in." -> 0.6875, "0.75" -> 0.75. NaN when unreadable. */
  function sdFrac(s) {
    s = String(s).trim().replace(/\s*in\.?$/, "");
    var m = s.match(/^(\d+)-(\d+)\/(\d+)$/);
    if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
    m = s.match(/^(\d+)\/(\d+)$/);
    if (m) return Number(m[1]) / Number(m[2]);
    return parseFloat(s);
  }

  /** The first table in scope whose FIRST row has a cell with exactly this text. The header row is
   * rows[0] whether the table has a <thead> (on the store) or not (in the portal), and a header cell
   * can be a th or a td with its text in a <p>. */
  function sdTableByHeader(scope, text) {
    var tables = scope.querySelectorAll("table");
    for (var i = 0; i < tables.length; i++) {
      var head = tables[i].rows[0];
      if (!head) continue;
      for (var c = 0; c < head.cells.length; c++) {
        if (sdText(head.cells[c]) === text) return tables[i];
      }
    }
    return null;
  }

  /** Only same-site paths and http(s) links survive; anything else becomes "#". */
  function sdHref(a) {
    var h = a.getAttribute("href") || "";
    return /^(\/(?!\/)|https?:\/\/)/.test(h) ? h : "#";
  }

  function sdMoney(n) {
    var parts = Number(n).toFixed(2).split(".");
    return "$" + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + parts[1];
  }

  /** Reads "Every Silver and Deming size we stock" (the table whose header has "Decimal"):
   * { series: [{ key, name, colour }], rows: [{ s, d, by }], bySize }. A series cell only counts when it
   * holds a link; "—" cells are absent from `by`. Null when the table is missing or unusable. */
  function sdParseSizes(scope) {
    var table = sdTableByHeader(scope, "Decimal");
    if (!table || table.rows.length < 2) return null;
    var head = table.rows[0].cells;
    var series = [];
    for (var i = 2; i < head.length; i++) {
      series.push({ key: "s" + (i - 1), name: sdText(head[i]), colour: "var(--ett-s" + (((i - 2) % 5) + 1) + ")" });
    }
    if (!series.length) return null;
    var rows = [];
    var bySize = {};
    for (var r = 1; r < table.rows.length; r++) {
      var cells = table.rows[r].cells;
      if (cells.length < 3) continue;
      var d = parseFloat(sdText(cells[1]));
      var s = sdText(cells[0]).replace(/\s*in\.?$/, "");
      if (!s || !(d > 0)) continue;
      var row = { s: s, d: d, by: {} };
      for (var k = 0; k < series.length; k++) {
        var cell = cells[k + 2];
        var a = cell ? cell.querySelector("a") : null;
        if (!a) continue;
        var text = sdText(cell);
        var price = text.match(/\$\s*([\d,]+(?:\.\d+)?)/);
        if (!price) continue;
        var min = text.match(/min\.\s*(\d+)/);
        row.by[series[k].key] = {
          p: parseFloat(price[1].replace(/,/g, "")),
          sku: sdText(a),
          href: sdHref(a),
          min: min ? parseInt(min[1], 10) : 1,
        };
      }
      rows.push(row);
      bySize[s] = row;
    }
    if (!rows.length) return null;
    return { series: series, rows: rows, bySize: bySize };
  }

  /** One series card: name, price, SKU link, minimum. Its top border takes the series colour. */
  function sdCard(series, b) {
    return (
      '<div class="et-tool__sd-card" style="--c:' + esc(series.colour) + '">' +
      '<span class="et-tool__sd-meta">' + esc(series.name) + "</span>" +
      '<span class="et-tool__sd-price">' + sdMoney(b.p) + "</span>" +
      '<a href="' + esc(b.href) + '">' + esc(b.sku) + "</a>" +
      '<span class="et-tool__sd-meta">' + (b.min > 1 ? "Each, min. " + b.min : "Each, no minimum") + "</span></div>"
    );
  }

  function sdHead(title, meta) {
    return (
      '<div class="et-tool__head"><div class="et-tool__title" role="heading" aria-level="3">' + esc(title) + "</div>" +
      '<span class="et-tool__meta">' + esc(meta) + "</span></div>"
    );
  }

  function sdReadout(big, sub) {
    return '<span class="et-tool__sd-big">' + big + '</span><span class="et-tool__sd-sub">' + sub + "</span>";
  }

  function sdChip(html, pressed, onClick) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "et-tool__chip";
    btn.innerHTML = html;
    btn.setAttribute("aria-pressed", pressed ? "true" : "false");
    btn.addEventListener("click", onClick);
    return btn;
  }

  function sdSetPressed(btn, on) {
    btn.setAttribute("aria-pressed", on ? "true" : "false");
  }

  function sdSvgNum(n) {
    return String(Math.round(n * 10) / 10);
  }

  // ---- (a) Bolt-to-bit finder ------------------------------------------

  var SD_JOBS = [
    { key: "Standard hole", label: "Standard clearance" },
    { key: "Oversized hole", label: "Oversized clearance" },
    { key: "Tap drill, coarse", label: "Tap, coarse thread" },
    { key: "Tap drill, fine", label: "Tap, fine thread" },
    { key: "Base-plate hole", label: "Base plate, anchor rod" },
    { key: "pipe", label: "Pipe tap (NPT)" },
  ];

  /** The "Our pipe tap" cell as escaped HTML: its link kept, the rest (the price) as text. */
  function sdCellHtml(cell) {
    var text = sdText(cell);
    var a = cell.querySelector("a");
    if (!a) return esc(text);
    var linkText = sdText(a);
    var at = text.indexOf(linkText);
    if (at < 0) return esc(text);
    return esc(text.slice(0, at)) + '<a href="' + esc(sdHref(a)) + '">' + esc(linkText) + "</a>" + esc(text.slice(at + linkText.length));
  }

  function mountSdBoltFinder(root) {
    root.hidden = true;
    var scope = sdScope(root);
    var boltTable = sdTableByHeader(scope, "Standard hole");
    var pipeTable = sdTableByHeader(scope, "Pipe thread");
    var sizes = sdParseSizes(scope);
    if (!boltTable || !pipeTable || !sizes) return;

    var cols = [];
    for (var c = 0; c < boltTable.rows[0].cells.length; c++) cols.push(sdText(boltTable.rows[0].cells[c]));
    var bolts = [];
    for (var i = 1; i < boltTable.rows.length; i++) {
      var bc = boltTable.rows[i].cells;
      if (bc.length < 2) continue;
      var texts = [];
      for (var j = 0; j < bc.length; j++) texts.push(sdText(bc[j]));
      bolts.push({ bolt: texts[0].replace(/\s*in\.?$/, ""), cells: texts });
    }
    var pipes = [];
    for (var p = 1; p < pipeTable.rows.length; p++) {
      var pc = pipeTable.rows[p].cells;
      if (pc.length < 3) continue;
      var lead = sdText(pc[1]).match(/^[\d\-\/]+/);
      if (!lead) continue;
      pipes.push({ thread: sdText(pc[0]), hole: lead[0], tap: sdCellHtml(pc[2]) });
    }
    if (!bolts.length || !pipes.length) return;

    var jobs = SD_JOBS.filter(function (j) { return j.key === "pipe" || cols.indexOf(j.key) >= 0; });
    var hasBolt = function (s) { return bolts.some(function (b) { return b.bolt === s; }); };
    var state = { job: "Standard hole", pick: hasBolt("3/4") ? "3/4" : bolts[0].bolt };
    var builtFor = "";

    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--sd-bolt-finder");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Bolt-to-bit finder", "Reads the two tables above") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-j">Job</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-j" data-jobs></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-s" data-size-label>Bolt or rod size</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-s" data-sizes></div></div>' +
      '<div class="et-tool__sd-readout" aria-live="polite" data-out></div>' +
      '<div class="et-tool__sd-cards" data-cards></div>' +
      "</div></div>";

    var jobsEl = root.querySelector("[data-jobs]");
    var sizesEl = root.querySelector("[data-sizes]");
    var sizeLabel = root.querySelector("[data-size-label]");
    var outEl = root.querySelector("[data-out]");
    var cardsEl = root.querySelector("[data-cards]");

    jobs.forEach(function (j) {
      var btn = sdChip(esc(j.label), j.key === state.job, function () {
        state.job = j.key;
        if (j.key === "pipe") state.pick = pipes[Math.min(2, pipes.length - 1)].thread;
        else if (!hasBolt(state.pick)) state.pick = hasBolt("3/4") ? "3/4" : bolts[0].bolt;
        render();
      });
      btn.setAttribute("data-job", j.key);
      jobsEl.appendChild(btn);
    });

    /** Rebuilds the size chips only when the list switches between bolts and pipe threads, so a
     * keyboard user keeps focus on the chip they just pressed. */
    function drawSizes() {
      var pipe = state.job === "pipe";
      var kind = pipe ? "pipe" : "bolt";
      if (builtFor !== kind) {
        builtFor = kind;
        sizeLabel.textContent = pipe ? "Pipe size" : "Bolt or rod size";
        sizesEl.innerHTML = "";
        var list = pipe ? pipes.map(function (x) { return x.thread; }) : bolts.map(function (b) { return b.bolt; });
        list.forEach(function (s) {
          var btn = sdChip(esc(pipe ? s : s + " in."), false, function () {
            state.pick = s;
            render();
          });
          btn.setAttribute("data-pick", s);
          sizesEl.appendChild(btn);
        });
      }
      Array.prototype.forEach.call(sizesEl.children, function (btn) {
        sdSetPressed(btn, btn.getAttribute("data-pick") === state.pick);
      });
    }

    function render() {
      Array.prototype.forEach.call(jobsEl.children, function (btn) {
        sdSetPressed(btn, btn.getAttribute("data-job") === state.job);
      });
      drawSizes();

      var pipe = state.job === "pipe";
      var hole = null;
      var thread = "";
      var tap = "";
      if (pipe) {
        var pr = pipes.filter(function (x) { return x.thread === state.pick; })[0];
        if (pr) {
          hole = pr.hole;
          thread = pr.thread;
          tap = pr.tap;
        }
      } else {
        var br = bolts.filter(function (b) { return b.bolt === state.pick; })[0];
        var cell = br ? br.cells[cols.indexOf(state.job)] : "";
        var m = cell ? cell.match(/(?:([\d\-\/]+-\d+):\s*)?([\d\-\/]+) in\./) : null;
        if (m) {
          thread = m[1] || "";
          hole = m[2];
        }
      }

      if (!hole) {
        outEl.innerHTML = '<span class="et-tool__sd-sub">' +
          (state.job === "Base-plate hole"
            ? "No base-plate size listed for this rod. Small rods go through standard holes."
            : "No size listed for this one.") + "</span>";
        cardsEl.innerHTML = "";
        return;
      }

      var d = sdFrac(hole);
      var what;
      if (pipe || state.job.indexOf("Tap") === 0) {
        what = "Tap drill for " + esc(thread || state.pick + " in.");
      } else {
        what = esc(state.job.replace(" hole", "")) + " hole for a " + esc(state.pick) + " in. " +
          (state.job === "Base-plate hole" ? "anchor rod" : "bolt");
      }
      outEl.innerHTML = sdReadout(esc(hole) + " in.", what + " &middot; " + d.toFixed(4) + " in." + (pipe ? " Tap: " + tap : ""));

      var row = sizes.bySize[hole];
      if (row) {
        var html = "";
        sizes.series.forEach(function (s) {
          if (row.by[s.key]) html += sdCard(s, row.by[s.key]);
        });
        cardsEl.innerHTML = html;
      } else if (d < 0.515) {
        cardsEl.innerHTML = '<p class="et-tool__sd-note">Under 33/64 in.: a jobber-length bit fits your chuck. See our ' +
          '<a href="/blogs/guides/drill-bits-for-metal">guide to drill bits for metal</a>.</p>';
      } else {
        cardsEl.innerHTML = '<p class="et-tool__sd-note">Over 1-1/2 in.: use a bi-metal hole saw, or an annular cutter on a mag drill.</p>';
      }
    }

    render();
    root.hidden = false;
  }

  // ---- (b) Price ladder ------------------------------------------------

  function mountSdPriceLadder(root) {
    root.hidden = true;
    var sizes = sdParseSizes(sdScope(root));
    if (!sizes) return;
    var series = sizes.series;
    var rows = sizes.rows;

    var on = series.map(function (s, i) { return i === 0 || i === 2 || i === 3; });
    var idx = 0;
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].s === "3/4") idx = i;
    }
    if (!sizes.bySize["3/4"]) idx = Math.floor(rows.length / 2);

    var top = 0;
    rows.forEach(function (r) {
      series.forEach(function (s) {
        if (r.by[s.key] && r.by[s.key].p > top) top = r.by[s.key].p;
      });
    });
    var yMax = Math.max(120, Math.ceil(top / 30) * 30);
    var plotW = SD_SVG_W - SD_PL - SD_PR;
    var plotH = SD_SVG_H - SD_PT - SD_PB;
    function X(d) { return SD_PL + ((d - 0.5) / 1) * plotW; }
    function Y(p) { return SD_PT + (1 - p / yMax) * plotH; }

    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--sd-price-ladder");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Price ladder", "Price per bit vs size, from the size table") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-g">Series</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-g" data-series></div></div>' +
      '<div class="et-tool__sd-chart"><div data-chart></div></div>' +
      '<label class="et-tool__slider-label">Size <output data-size-out></output>' +
      '<input type="range" data-size min="0" max="' + (rows.length - 1) + '" step="1" value="' + idx + '" aria-label="Bit size"></label>' +
      '<div class="et-tool__sd-readout" aria-live="polite" data-out></div>' +
      '<div class="et-tool__sd-cards" data-cards></div>' +
      "</div></div>";

    var seriesEl = root.querySelector("[data-series]");
    var chartEl = root.querySelector("[data-chart]");
    var slider = root.querySelector("[data-size]");
    var sizeOut = root.querySelector("[data-size-out]");
    var outEl = root.querySelector("[data-out]");
    var cardsEl = root.querySelector("[data-cards]");

    series.forEach(function (s, i) {
      var btn = sdChip('<span class="et-tool__sd-swatch" style="--c:' + esc(s.colour) + '"></span>' + esc(s.name), on[i], function () {
        on[i] = !on[i];
        sdSetPressed(btn, on[i]);
        draw();
      });
      seriesEl.appendChild(btn);
    });

    function draw() {
      var r = rows[idx];
      var g = "";
      for (var v = 0; v <= yMax; v += 30) {
        g += '<line x1="' + SD_PL + '" x2="' + (SD_SVG_W - SD_PR) + '" y1="' + sdSvgNum(Y(v)) + '" y2="' + sdSvgNum(Y(v)) +
          '" stroke="var(--ett-line)" stroke-width="1"/><text x="' + (SD_PL - 6) + '" y="' + sdSvgNum(Y(v) + 4) +
          '" text-anchor="end" font-size="12" fill="var(--ett-muted)">$' + v + "</text>";
      }
      [["1/2", 0.5], ["3/4", 0.75], ["1", 1], ["1-1/4", 1.25], ["1-1/2", 1.5]].forEach(function (t) {
        g += '<text x="' + sdSvgNum(X(t[1])) + '" y="' + (SD_SVG_H - 12) + '" text-anchor="middle" font-size="12" fill="var(--ett-muted)">' + t[0] + " in.</text>";
      });
      g += '<line x1="' + sdSvgNum(X(r.d)) + '" x2="' + sdSvgNum(X(r.d)) + '" y1="' + SD_PT + '" y2="' + (SD_SVG_H - SD_PB) +
        '" stroke="var(--ett-rust)" stroke-width="1.5" stroke-dasharray="4 3"/>';
      var names = [];
      series.forEach(function (s, i) {
        if (!on[i]) return;
        names.push(s.name);
        var pts = [];
        rows.forEach(function (z) {
          if (z.by[s.key]) pts.push(sdSvgNum(X(z.d)) + "," + sdSvgNum(Y(z.by[s.key].p)));
        });
        g += '<polyline fill="none" stroke="' + esc(s.colour) + '" stroke-width="2.2" stroke-linejoin="round" points="' + pts.join(" ") + '"/>';
        var b = r.by[s.key];
        if (b) {
          g += '<circle cx="' + sdSvgNum(X(r.d)) + '" cy="' + sdSvgNum(Y(b.p)) + '" r="5" fill="' + esc(s.colour) +
            '" stroke="var(--ett-cream)" stroke-width="2"/>';
        }
      });
      chartEl.innerHTML = '<svg viewBox="0 0 ' + SD_SVG_W + " " + SD_SVG_H + '" role="img" aria-label="' +
        esc("Price per bit by size, 1/2 to 1-1/2 in., for " + (names.length ? names.join(", ") : "no series selected")) + '">' + g + "</svg>";

      sizeOut.textContent = r.s + " in.";
      var count = 0;
      var cards = "";
      series.forEach(function (s, i) {
        if (!r.by[s.key]) return;
        count++;
        if (on[i]) cards += sdCard(s, r.by[s.key]);
      });
      outEl.innerHTML = sdReadout(esc(r.s) + " in.", r.d.toFixed(4) + " in. &middot; " + count + " series stock this size");
      cardsEl.innerHTML = cards || '<p class="et-tool__sd-note">Turn on a series to see its price.</p>';
    }

    slider.addEventListener("input", function () {
      idx = parseInt(slider.value, 10) || 0;
      draw();
    });
    draw();
    root.hidden = false;
  }

  // ---- (c) Speed dial ----------------------------------------------------

  function sdGear(v) {
    if (v < 300) return "Cordless drill: low gear, trigger about half way or less.";
    if (v <= 550) return "Cordless drill: low gear, trigger most of the way.";
    if (v <= 1800) return "Cordless drill: high gear, part trigger.";
    return "High gear, full trigger.";
  }

  function mountSdSpeedDial(root) {
    root.hidden = true;
    var table = sdTableByHeader(sdScope(root), "Bit size");
    if (!table) return;
    var head = table.rows[0].cells;
    var mats = [];
    for (var m = 1; m < head.length; m++) mats.push(sdText(head[m]));
    var rows = [];
    for (var r = 1; r < table.rows.length; r++) {
      if (table.rows[r].cells.length >= 2) rows.push(table.rows[r]);
    }
    if (!mats.length || !rows.length) return;

    var mi = Math.min(2, mats.length - 1);
    var ri = Math.floor(rows.length / 2);
    for (var i = 0; i < rows.length; i++) {
      if (sdText(rows[i].cells[0]) === "3/4 in.") ri = i;
    }

    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--sd-speed-dial");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Speed dial", "Highlights the RPM table above") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-m">Material</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-m" data-mats></div></div>' +
      '<label class="et-tool__slider-label">Bit size <output data-size-out></output>' +
      '<input type="range" data-size min="0" max="' + (rows.length - 1) + '" step="1" value="' + ri + '" aria-label="Bit size"></label>' +
      '<div class="et-tool__sd-readout" aria-live="polite" data-out></div>' +
      "</div></div>";

    var matsEl = root.querySelector("[data-mats]");
    var slider = root.querySelector("[data-size]");
    var sizeOut = root.querySelector("[data-size-out]");
    var outEl = root.querySelector("[data-out]");

    mats.forEach(function (name, k) {
      matsEl.appendChild(sdChip(esc(name), k === mi, function () {
        mi = k;
        draw();
      }));
    });

    function draw() {
      Array.prototype.forEach.call(matsEl.children, function (btn, k) { sdSetPressed(btn, k === mi); });
      var marked = table.querySelectorAll(".et-tool-hl");
      for (var x = 0; x < marked.length; x++) marked[x].classList.remove("et-tool-hl");
      var tr = rows[ri];
      var cell = tr.cells[mi + 1];
      tr.classList.add("et-tool-hl");
      if (cell) cell.classList.add("et-tool-hl");
      var size = sdText(tr.cells[0]);
      var rpmText = cell ? sdText(cell) : "";
      var rpm = parseInt(rpmText.replace(/,/g, ""), 10);
      sizeOut.textContent = size;
      outEl.innerHTML = sdReadout(
        esc(rpmText) + " RPM",
        esc(size) + " bit in " + esc(mats[mi].toLowerCase()) + ". " + (rpm > 0 ? sdGear(rpm) + " " : "") +
          "Drill press: the belt step closest to, but below, this number." + (mi === mats.length - 1 ? " Use M42 cobalt." : "")
      );
    }

    slider.addEventListener("input", function () {
      ri = parseInt(slider.value, 10) || 0;
      draw();
    });
    draw();
    root.hidden = false;
  }

  // ---- (d) Kit builder ---------------------------------------------------

  function mountSdKitBuilder(root) {
    root.hidden = true;
    var sizes = sdParseSizes(sdScope(root));
    if (!sizes) return;
    var series = sizes.series;
    var rows = sizes.rows;

    var sel = {};
    function setSel(list) {
      sel = {};
      for (var i = 0; i < list.length; i++) sel[list[i]] = true;
    }
    setSel(SD_PRESETS[0].sizes);
    var mode = series[0].key;

    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    var options = '<option value="best">Lowest cost per size</option>';
    series.forEach(function (s) {
      options += '<option value="' + esc(s.key) + '">' + esc(s.name) + "</option>";
    });
    root.classList.add("et-tool--sd-kit-builder");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Kit builder", "Totals from the size table") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-p">Start from</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-p" data-presets></div></div>' +
      '<div class="et-tool__row"><label>Series <select data-series>' + options + "</select></label></div>" +
      '<div><div class="et-tool__label" id="' + uid + '-g">Sizes (tap to add or remove)</div>' +
      '<div class="et-tool__sd-grid" role="group" aria-labelledby="' + uid + '-g" data-grid></div></div>' +
      '<div class="et-tool__sd-total" aria-live="polite" data-total></div>' +
      '<div class="et-tool__sd-list" data-list></div>' +
      "</div></div>";

    var presetsEl = root.querySelector("[data-presets]");
    var select = root.querySelector("[data-series]");
    var gridEl = root.querySelector("[data-grid]");
    var totalEl = root.querySelector("[data-total]");
    var listEl = root.querySelector("[data-list]");
    select.value = mode;

    SD_PRESETS.forEach(function (p) {
      presetsEl.appendChild(sdChip(esc(p.label), false, function () {
        setSel(p.sizes);
        draw();
      }));
    });
    presetsEl.appendChild(sdChip("Clear", false, function () {
      setSel([]);
      draw();
    }));
    select.addEventListener("change", function () {
      mode = select.value;
      draw();
    });
    rows.forEach(function (r) {
      var btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = r.s;
      btn.setAttribute("data-s", r.s);
      btn.setAttribute("aria-pressed", "false");
      btn.addEventListener("click", function () {
        if (sel[r.s]) delete sel[r.s];
        else sel[r.s] = true;
        draw();
      });
      gridEl.appendChild(btn);
    });

    function presetMatches(list) {
      var count = 0;
      for (var k in sel) if (sel[k]) count++;
      if (count !== list.length) return false;
      for (var i = 0; i < list.length; i++) if (!sel[list[i]]) return false;
      return true;
    }

    function draw() {
      Array.prototype.forEach.call(gridEl.children, function (btn) {
        sdSetPressed(btn, !!sel[btn.getAttribute("data-s")]);
      });
      Array.prototype.forEach.call(presetsEl.children, function (btn, i) {
        sdSetPressed(btn, i < SD_PRESETS.length ? presetMatches(SD_PRESETS[i].sizes) : presetMatches([]));
      });

      var cents = 0;
      var bits = 0;
      var missing = [];
      var lines = "";
      var made = 0;
      rows.forEach(function (r) {
        if (!sel[r.s]) return;
        var key = mode;
        if (mode === "best") {
          key = null;
          var bestCost = 0;
          series.forEach(function (s) {
            var cand = r.by[s.key];
            if (!cand) return;
            var cost = Math.round(cand.p * 100) * cand.min;
            if (key === null || cost < bestCost) {
              key = s.key;
              bestCost = cost;
            }
          });
        }
        var b = key ? r.by[key] : null;
        if (!b) {
          missing.push(r.s);
          return;
        }
        var line = Math.round(b.p * 100) * b.min;
        cents += line;
        bits += b.min;
        made++;
        lines += "<div><span>" + esc(r.s) + ' in. &middot; <a href="' + esc(b.href) + '">' + esc(b.sku) + "</a>" +
          (b.min > 1 ? " &times;" + b.min : "") + "</span><span>" + sdMoney(line / 100) + "</span></div>";
      });

      totalEl.innerHTML = sdReadout(
        sdMoney(cents / 100),
        bits + (bits === 1 ? " bit" : " bits") + " in " + made + (made === 1 ? " size" : " sizes") +
          (bits ? " &middot; " + sdMoney(cents / 100 / bits) + " per bit" : "") +
          (missing.length ? " &middot; not made in this series: " + esc(missing.join(", ")) : "")
      );
      listEl.innerHTML = lines || '<span class="et-tool__sd-sub">Pick a starting set or tap sizes above.</span>';
    }

    draw();
    root.hidden = false;
  }

  // ---------------------------------------------------------------------
  // Chart from table (chart), built 2026-10-29 so an author can add a data visualization without a
  // code change. The marker carries nothing: the chart reads the table next to it (found the way the
  // speed fit chart finds its table). Row 0 is the header, column 0 the category or x value, and every
  // other column whose cells are mostly numbers is a series. The chart kind follows the data: numeric
  // x -> lines (points when a header cell says "scatter"); text categories -> grouped bars; one series
  // with 8 rows or fewer -> horizontal bars. The table stays visible (rule T6); no data of its own,
  // no attributes and no fetch.
  // ---------------------------------------------------------------------

  var CH_W = 720;
  var CH_PLOT_H = 300;
  var CH_PT = 18;
  var CH_PR = 18;
  var CH_ROW_H = 36;
  var CH_BAR_H = 18;
  var CH_SERIES_COLOURS = 5; // --ett-s1..s5; a sixth series starts over
  var CH_UNIT_RE = /\s*(?:%|in\.|in|inch|inches|"|″|mm|cm|ft|lbs?|lb\.|oz|rpm|sfm|ipm|fpm|hp|kg|kw|w|v|a|hrs?|hours?|min\.?|minutes?|sec\.?|pcs?|pieces?|each|ea\.?|x|×|\/\s*[a-z.]+)$/i;

  /** Strict number reader for one table cell: "1,700" -> 1700, "$12.50" -> 12.5, "1-1/16 in." -> 1.0625,
   * "11/16" -> 0.6875, "45%" -> 45, "(12)" -> -12. Words, ranges ("10,000-11,000") and dashes are NaN. */
  function chNum(raw) {
    var s = String(raw == null ? "" : raw).replace(/ /g, " ").replace(/\s+/g, " ").trim();
    if (!s) return NaN;
    var neg = false;
    var m = s.match(/^\((.*)\)$/);
    if (m) { neg = true; s = m[1].trim(); }
    if (/^[-−–]\s*\d/.test(s)) { neg = !neg; s = s.slice(1).trim(); }
    s = s.replace(/^(?:US\$|\$|USD|€|£)\s*/i, "");
    s = s.replace(CH_UNIT_RE, "").replace(CH_UNIT_RE, "").trim();
    var v;
    m = s.match(/^(\d+)[ -](\d+)\/(\d+)$/);
    if (m) v = Number(m[1]) + Number(m[2]) / Number(m[3]);
    else if ((m = s.match(/^(\d+)\/(\d+)$/))) v = Number(m[1]) / Number(m[2]);
    else if (/^(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.\d+)?$/.test(s) && /\d/.test(s)) v = Number(s.replace(/,/g, ""));
    else return NaN;
    if (!isFinite(v)) return NaN;
    return neg ? -v : v;
  }

  /** The unit a cell's own text declares: "$" (leading currency), "%" or "in" (trailing). */
  function chCellUnit(raw) {
    var s = String(raw == null ? "" : raw).trim();
    if (/^\(?\s*(?:US\$|\$|USD)/i.test(s)) return "$";
    if (/%\s*\)?$/.test(s)) return "%";
    if (/(?:\bin\.?|"|″|inch(?:es)?)\s*\)?$/i.test(s)) return "in";
    return "";
  }

  /** The unit a header cell implies ("Price", "Max RPM", "Bit size"). */
  function chHeaderUnit(h) {
    if (/\$|price|cost|usd|dollar/i.test(h)) return "$";
    if (/%|percent/i.test(h)) return "%";
    if (/\brpm\b/i.test(h)) return "rpm";
    if (/\bin\.|\binch|\bsize\b|\bdia|\bdiameter|\blength|\bwidth/i.test(h)) return "in";
    return "";
  }

  function chIsBlank(text) {
    return !text || /^[-–—−]+$/.test(text) || /^(?:n\/a|na|none)$/i.test(text);
  }

  /** Reads the table into { xName, xUnit, labels, xs (numbers, or null when column 0 is text), series:
   * [{ name, unit, values: [number|null] }], scatter }. Null when it holds no chartable column. */
  function chParse(table) {
    var all = Array.prototype.slice.call(table.rows);
    if (all.length < 3) return null;
    var head = Array.prototype.map.call(all[0].cells, sdText);
    if (head.length < 2) return null;
    var body = all.slice(1).filter(function (r) {
      return r.cells.length >= 2 && !/^(?:total|sum|average|avg|mean)\b/i.test(sdText(r.cells[0]));
    });
    if (body.length < 2) return null;

    var series = [];
    for (var j = 1; j < head.length; j++) {
      var filled = 0;
      var numeric = 0;
      var units = {};
      var values = body.map(function (r) {
        var text = r.cells[j] ? sdText(r.cells[j]) : "";
        if (chIsBlank(text)) return null;
        filled++;
        var v = chNum(text);
        if (!isFinite(v)) return null;
        numeric++;
        var u = chCellUnit(text);
        if (u) units[u] = (units[u] || 0) + 1;
        return v;
      });
      if (numeric < 2 || numeric * 2 < filled) continue;
      var unit = chHeaderUnit(head[j]);
      if (!unit) {
        Object.keys(units).forEach(function (u) { if (units[u] * 2 > numeric) unit = u; });
      }
      series.push({ name: head[j] || "Column " + (j + 1), unit: unit, values: values });
    }
    if (!series.length) return null;

    var labels = body.map(function (r) { return sdText(r.cells[0]); });
    var xs = labels.map(chNum);
    var distinct = {};
    xs.forEach(function (x) { if (isFinite(x)) distinct[x] = true; });
    var numericX = xs.every(isFinite) && Object.keys(distinct).length >= 2;
    var xUnit = chHeaderUnit(head[0]);
    if (!xUnit) {
      var inches = labels.filter(function (l) { return chCellUnit(l) === "in"; }).length;
      if (inches * 2 > labels.length) xUnit = "in";
    }
    if (xUnit === "in") {
      labels = labels.map(function (l) { return l.replace(/\s*(?:in\.?|inch(?:es)?)\s*$/i, ""); });
    }
    var data = { xName: head[0] || "Row", xUnit: xUnit, labels: labels, xs: numericX ? xs : null, series: series, scatter: head.some(function (h) { return /scatter/i.test(h); }) };
    if (numericX) {
      var order = [];
      for (var i = 0; i < xs.length; i++) order.push(i);
      order.sort(function (a, b) { return xs[a] - xs[b] || a - b; });
      data.labels = order.map(function (i) { return labels[i]; });
      data.xs = order.map(function (i) { return xs[i]; });
      data.series.forEach(function (s) { s.values = order.map(function (i) { return s.values[i]; }); });
    }
    return data;
  }

  /** `dec` is the most decimals shown; dollars always show exactly two. */
  function chFmt(v, unit, dec) {
    var str = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: unit === "$" ? dec : 0, maximumFractionDigits: dec });
    if (unit === "$") str = "$" + str;
    else if (unit === "%") str += "%";
    return (v < 0 ? "−" : "") + str;
  }

  /** A tick label: "$1,000", "45%", "0.75", or "12.5k" when the whole axis reads in thousands (RPM and
   * plain counts past 10,000, steps of 1,000 or more: every tick on that axis takes the k form). */
  function chTick(v, unit, axis) {
    if (axis.k) return v === 0 ? "0" : v / 1000 + "k";
    var step = axis.step;
    var dec = step % 1 === 0 ? 0 : step >= 0.1 ? 1 : step >= 0.01 ? 2 : 3;
    return chFmt(v, unit, dec);
  }

  /** A value as the reader would say it: "$12.50", "1,700 RPM", "0.688 in.", "45%". */
  function chValue(v, unit) {
    var dec = unit === "$" ? 2 : v % 1 === 0 ? 0 : Math.abs(v) < 1 ? 3 : 2;
    return chFmt(v, unit, dec) + (unit === "rpm" ? " RPM" : unit === "in" ? " in." : "");
  }

  /** "Bit size" -> "bit size", but "SFM" and "Speed (SFM)" keep their capitals. */
  function chLower(name) {
    return /^[A-Z][a-z]/.test(name) ? name.charAt(0).toLowerCase() + name.slice(1) : name;
  }

  function chUnitWords(unit) {
    return unit === "$" ? "dollars" : unit === "%" ? "percent" : unit === "rpm" ? "RPM" : unit === "in" ? "inches" : "";
  }

  /** 1, 2, 2.5, 5 or 10 times a power of ten, so that about `n` steps cover `span`. */
  function chNiceStep(span, n) {
    var raw = span / n;
    if (!(raw > 0)) return 1;
    var p = Math.pow(10, Math.floor(Math.log(raw) / Math.LN10));
    var f = raw / p;
    return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
  }

  /** The value axis: { lo, hi, step, ticks, k } from the lowest and highest values, always including 0. */
  function chAxis(lo, hi, unit) {
    lo = Math.min(0, lo);
    hi = Math.max(0, hi);
    if (hi === lo) hi = lo + 1;
    var step = chNiceStep(hi - lo, 5);
    var top = Math.ceil(hi / step - 1e-9) * step;
    var bottom = Math.floor(lo / step + 1e-9) * step;
    var ticks = [];
    for (var t = bottom; t <= top + step / 2; t += step) ticks.push(Math.round(t / step) * step);
    var k = unit !== "$" && unit !== "%" && Math.max(Math.abs(top), Math.abs(bottom)) >= 10000 && step >= 1000;
    return { lo: bottom, hi: top, step: step, ticks: ticks, k: k };
  }

  function chJoin(names) {
    if (names.length <= 1) return names.join("");
    return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
  }

  function mountChart(root) {
    root.hidden = true;

    var table = sfFindTable(root);
    if (!table) return;
    var data = chParse(table);
    if (!data) return;

    var series = data.series.map(function (s, i) {
      s.colour = "var(--ett-s" + ((i % CH_SERIES_COLOURS) + 1) + ")";
      s.on = true;
      return s;
    });
    var multi = series.length > 1;
    var n = data.labels.length;
    var kinds;
    var kind;
    if (data.xs) {
      kinds = ["bar", "line", "points"];
      kind = data.scatter ? "points" : "line";
    } else if (!multi && n <= 8) {
      kinds = [];
      kind = "hbar";
    } else {
      kinds = ["bar", "line"];
      kind = "bar";
    }
    var tableAfter = !!(root.compareDocumentPosition(table) & Node.DOCUMENT_POSITION_FOLLOWING);
    var KIND_LABELS = { bar: "Bars", line: "Lines", points: "Points" };
    var KIND_NAMES = { bar: "Bar chart", hbar: "Bar chart", line: "Line chart", points: "Scatter chart" };

    root.classList.add("et-tool--chart");
    var names = series.map(function (s) { return s.name; });
    root.innerHTML =
      '<div class="et-tool__finder">' +
      sdHead(multi ? data.xName : names[0], multi ? names.join(", ") : "by " + chLower(data.xName)) +
      '<div class="et-tool__body">' +
      '<div class="et-tool__ch-controls">' +
      (multi ? '<div class="et-tool__chips" role="group" aria-label="Series shown" data-series></div>' : "") +
      (kinds.length ? '<div class="et-tool__chips et-tool__ch-kinds" role="group" aria-label="Show as" data-kinds></div>' : "") +
      "</div>" +
      '<div class="et-tool__ch-chart"><svg role="img" data-chart></svg></div>' +
      '<div class="et-tool__ch-legend" data-legend></div>' +
      "</div></div>";

    var svg = root.querySelector("[data-chart]");
    var legendEl = root.querySelector("[data-legend]");
    var seriesEl = root.querySelector("[data-series]");
    var kindsEl = root.querySelector("[data-kinds]");

    if (seriesEl) {
      series.forEach(function (s) {
        var chip = sdChip('<span class="et-tool__ch-swatch" style="--c:' + esc(s.colour) + '"></span>' + esc(s.name), true, function () {
          if (s.on && series.filter(function (o) { return o.on; }).length === 1) return; // keep one
          s.on = !s.on;
          sdSetPressed(chip, s.on);
          render();
        });
        seriesEl.appendChild(chip);
      });
    }
    if (kindsEl) {
      kinds.forEach(function (k) {
        var chip = sdChip(esc(KIND_LABELS[k]), k === kind, function () {
          kind = k;
          Array.prototype.forEach.call(kindsEl.children, function (c, i) { sdSetPressed(c, kinds[i] === kind); });
          render();
        });
        kindsEl.appendChild(chip);
      });
    }

    function active() {
      return series.filter(function (s) { return s.on; });
    }

    function sharedUnit(list) {
      var u = list[0].unit;
      return list.every(function (s) { return s.unit === u; }) ? u : "";
    }

    function clear() {
      while (svg.firstChild) svg.removeChild(svg.firstChild);
    }

    function labelWidth(strs) {
      var longest = strs.reduce(function (m, s) { return Math.max(m, String(s).length); }, 0);
      return Math.round(longest * 7.2) + 16;
    }

    function cellTitle(label, s, v) {
      return label + (data.xUnit === "in" ? " in." : "") + " · " + s.name + ": " + chValue(v, s.unit);
    }

    function drawLegend(list) {
      legendEl.innerHTML = "";
      list.forEach(function (s) {
        var span = document.createElement("span");
        span.style.setProperty("--c", s.colour);
        span.textContent = s.name + (s.unit && !/\$|%/.test(s.unit) ? " (" + chUnitWords(s.unit) + ")" : "");
        legendEl.appendChild(span);
      });
    }

    function describe(list, unit) {
      var what = KIND_NAMES[kind] + " of " + chJoin(list.map(function (s) { return s.name; }));
      var words = chUnitWords(unit);
      var named = list.some(function (s) { return words && s.name.toLowerCase().indexOf(words.toLowerCase()) >= 0; });
      if (words && !named) what += " in " + words;
      what += " by " + chLower(data.xName) + ": " + n + " rows from the table " + (tableAfter ? "below" : "above") + ".";
      return what;
    }

    /** Horizontal bars: one series, a label per row on the left and the value at the bar's end. */
    function drawHorizontal(list) {
      var s = list[0];
      var vals = s.values.filter(function (v) { return v !== null; });
      var axis = chAxis(Math.min.apply(null, vals), Math.max.apply(null, vals), s.unit);
      var gutterL = Math.min(240, Math.max(90, labelWidth(data.labels)));
      var gutterR = Math.max(60, labelWidth(vals.map(function (v) { return chValue(v, s.unit); })));
      var plotW = CH_W - gutterL - gutterR;
      var top = CH_PT;
      var bottom = top + n * CH_ROW_H;
      var h = bottom + 30;
      function x(v) { return gutterL + ((v - axis.lo) / (axis.hi - axis.lo)) * plotW; }

      svg.setAttribute("viewBox", "0 0 " + CH_W + " " + h);
      axis.ticks.forEach(function (t) {
        svg.appendChild(sfSvg("line", { x1: x(t), x2: x(t), y1: top, y2: bottom, stroke: "var(--ett-line)", "stroke-width": 1 }));
        svg.appendChild(sfLabel(x(t), bottom + 18, chTick(t, s.unit, axis), { "text-anchor": "middle", "font-size": 11.5, fill: "var(--ett-muted)" }));
      });
      data.labels.forEach(function (label, i) {
        var y0 = top + i * CH_ROW_H;
        svg.appendChild(sfLabel(gutterL - 10, y0 + CH_ROW_H / 2 + 5, label, { "text-anchor": "end", "font-size": 13.5, "font-weight": 700, fill: "var(--ett-ink)" }));
        var v = s.values[i];
        if (v === null) return;
        var x0 = x(Math.min(0, v));
        var x1 = x(Math.max(0, v));
        var bar = sfSvg("rect", { x: x0, y: y0 + (CH_ROW_H - CH_BAR_H) / 2, width: Math.max(2, x1 - x0), height: CH_BAR_H, rx: 3, fill: s.colour, "fill-opacity": 0.85 });
        var title = sfSvg("title");
        title.textContent = cellTitle(label, s, v);
        bar.appendChild(title);
        svg.appendChild(bar);
        svg.appendChild(sfLabel(x1 + 8, y0 + CH_ROW_H / 2 + 5, chValue(v, s.unit), { "font-size": 13, "font-weight": 700, fill: "var(--ett-ink)" }));
      });
      svg.appendChild(sfSvg("line", { x1: x(0), x2: x(0), y1: top, y2: bottom, stroke: "var(--ett-ink)", "stroke-width": 1.5 }));
    }

    /** Vertical bars, lines or points over a shared x axis (categories, or numeric x). */
    function drawVertical(list) {
      var unit = sharedUnit(list);
      var vals = [];
      list.forEach(function (s) { s.values.forEach(function (v) { if (v !== null) vals.push(v); }); });
      var axis = chAxis(Math.min.apply(null, vals), Math.max.apply(null, vals), unit);
      var gutterL = Math.max(44, labelWidth(axis.ticks.map(function (t) { return chTick(t, unit, axis); })));
      var rotate = !data.xs && (n > 8 || data.labels.some(function (l) { return l.length > 12; }));
      var gutterB = rotate ? 30 + Math.min(110, Math.round(labelWidth(data.labels) * 0.6)) : 46;
      var plotW = CH_W - gutterL - CH_PR;
      var top = CH_PT;
      var bottom = top + CH_PLOT_H;
      var h = bottom + gutterB;
      var banded = kind === "bar" || !data.xs;
      var xmin = data.xs ? data.xs[0] : 0;
      var xmax = data.xs ? data.xs[n - 1] : 0;
      var pad = data.xs ? (xmax - xmin || 1) * 0.06 : 0;

      function y(v) { return bottom - ((v - axis.lo) / (axis.hi - axis.lo)) * CH_PLOT_H; }
      function xc(i) {
        if (banded) return gutterL + (plotW / n) * (i + 0.5);
        return gutterL + ((data.xs[i] - (xmin - pad)) / (xmax - xmin + 2 * pad)) * plotW;
      }

      svg.setAttribute("viewBox", "0 0 " + CH_W + " " + h);
      axis.ticks.forEach(function (t) {
        svg.appendChild(sfSvg("line", { x1: gutterL, x2: CH_W - CH_PR, y1: y(t), y2: y(t), stroke: t === 0 ? "var(--ett-ink)" : "var(--ett-line)", "stroke-width": t === 0 ? 1.5 : 1 }));
        svg.appendChild(sfLabel(gutterL - 8, y(t) + 4, chTick(t, unit, axis), { "text-anchor": "end", "font-size": 11.5, fill: "var(--ett-muted)" }));
      });

      var every = banded ? Math.max(1, Math.ceil(n / 16)) : 1;
      data.labels.forEach(function (label, i) {
        if (i % every) return;
        var lx = xc(i);
        var attrs = { "text-anchor": rotate ? "end" : "middle", "font-size": 12, fill: "var(--ett-muted)" };
        if (rotate) attrs.transform = "rotate(-35 " + lx + " " + (bottom + 16) + ")";
        svg.appendChild(sfLabel(lx, bottom + 16, label, attrs));
      });
      if (data.xUnit) {
        svg.appendChild(sfLabel(CH_W - CH_PR, h - 4, data.xName + (data.xUnit === "in" ? " (in.)" : ""), { "text-anchor": "end", "font-size": 11.5, fill: "var(--ett-muted)" }));
      }

      if (kind === "bar") {
        var band = (plotW / n) * 0.72;
        var bw = band / list.length;
        list.forEach(function (s, k) {
          s.values.forEach(function (v, i) {
            if (v === null) return;
            var bx = xc(i) - band / 2 + k * bw;
            var y0 = y(Math.max(0, v));
            var y1 = y(Math.min(0, v));
            var bar = sfSvg("rect", { x: bx, y: y0, width: Math.max(1, bw - 1), height: Math.max(2, y1 - y0), rx: 2, fill: s.colour, "fill-opacity": 0.85 });
            var title = sfSvg("title");
            title.textContent = cellTitle(data.labels[i], s, v);
            bar.appendChild(title);
            svg.appendChild(bar);
          });
        });
        return;
      }

      list.forEach(function (s) {
        if (kind === "line") {
          var d = "";
          var pen = false;
          s.values.forEach(function (v, i) {
            if (v === null) { pen = false; return; }
            d += (pen ? " L " : " M ") + sdSvgNum(xc(i)) + " " + sdSvgNum(y(v));
            pen = true;
          });
          if (d) svg.appendChild(sfSvg("path", { d: d.trim(), fill: "none", stroke: s.colour, "stroke-width": 2.5, "stroke-linejoin": "round", "stroke-linecap": "round" }));
        }
        s.values.forEach(function (v, i) {
          if (v === null) return;
          var dot = sfSvg("circle", { cx: xc(i), cy: y(v), r: kind === "points" ? 5 : 3.5, fill: s.colour, stroke: "#fff", "stroke-width": 1.5 });
          var title = sfSvg("title");
          title.textContent = cellTitle(data.labels[i], s, v);
          dot.appendChild(title);
          svg.appendChild(dot);
        });
      });
    }

    function render() {
      var list = active();
      clear();
      if (kind === "hbar") drawHorizontal(list);
      else drawVertical(list);
      svg.setAttribute("aria-label", describe(list, sharedUnit(list)));
      drawLegend(list);
    }

    render();
    root.hidden = false;
  }

  // ---------------------------------------------------------------------
  // Bell hanger and flex bit tools (bh-route-planner, bh-bit-rail, bh-plate-check), for the guide
  // "Bell Hanger Bit vs Flex Bit: Which One Gets the Wire Through". Same contract as the S&D tools:
  // no data of their own, no fetch, no stock flags. The route planner and the bit rail read three of
  // the article's tables by header text:
  //   - the stock table (a header cell "Diameter x length"): column 0 "3/8 x 18 in.", one column per
  //     bit style; a cell holds one or more SKU links, each followed by "$7.04, min. 4" and "USA" when
  //     made in the USA. The only price source.
  //   - the route table (a header cell "Length at least"): Route | Starting point | Drill through |
  //     Reach to the plate | Bit | Length at least.
  //   - the pull table (a header cell "What you pull"): column 0 the cable or pipe, "Bit diameter"
  //     the smallest hole for it.
  // The plate check is pure math (NEC 300.4(A)(1) and IRC R602.6.1), stated in the article's text.
  // ---------------------------------------------------------------------

  /** Reads the stock table: { styles: [{ key, name, colour }], items: [{ style, s, d, len, sku, href,
   * p, min, usa }] }. Null when the table is missing or holds no linked SKU. */
  function bhParseStock(scope) {
    var table = sdTableByHeader(scope, "Diameter x length");
    if (!table || table.rows.length < 2) return null;
    var head = table.rows[0].cells;
    var styles = [];
    for (var i = 1; i < head.length; i++) {
      styles.push({ key: "s" + i, name: sdText(head[i]), colour: "var(--ett-s" + (((i - 1) % 5) + 1) + ")" });
    }
    if (!styles.length) return null;
    var items = [];
    for (var r = 1; r < table.rows.length; r++) {
      var cells = table.rows[r].cells;
      var m = sdText(cells[0]).match(/^([\d\-\/]+)\s*x\s*(\d+)/);
      if (!m) continue;
      var d = sdFrac(m[1]);
      var len = parseInt(m[2], 10);
      if (!(d > 0) || !(len > 0)) continue;
      for (var k = 0; k < styles.length; k++) {
        var cell = cells[k + 1];
        if (!cell) continue;
        var text = sdText(cell);
        var links = cell.querySelectorAll("a");
        var from = 0;
        for (var a = 0; a < links.length; a++) {
          var sku = sdText(links[a]);
          var at = text.indexOf(sku, from);
          if (!sku || at < 0) continue;
          var next = a + 1 < links.length ? text.indexOf(sdText(links[a + 1]), at + sku.length) : -1;
          var seg = text.slice(at + sku.length, next < 0 ? text.length : next);
          from = at + sku.length;
          var price = seg.match(/\$\s*([\d,]+(?:\.\d+)?)/);
          if (!price) continue;
          var min = seg.match(/min\.\s*(\d+)/);
          items.push({
            style: styles[k],
            s: m[1],
            d: d,
            len: len,
            sku: sku,
            href: sdHref(links[a]),
            p: parseFloat(price[1].replace(/,/g, "")),
            min: min ? parseInt(min[1], 10) : 1,
            usa: /\bUSA\b/.test(seg),
          });
        }
      }
    }
    if (!items.length) return null;
    return { styles: styles, items: items };
  }

  /** Column index of the header cell with exactly this text, or -1. */
  function bhCol(table, text) {
    var head = table.rows[0].cells;
    for (var c = 0; c < head.length; c++) {
      if (sdText(head[c]) === text) return c;
    }
    return -1;
  }

  /** The bit styles a route row's "Bit" text allows: carbide-tipped, flex, or the plain stiff ones. */
  function bhStylesFor(bitText, styles) {
    var want = /carbide/i.test(bitText) ? /carbide/i : /flex/i.test(bitText) ? /flex/i : null;
    return styles.filter(function (s) {
      return want ? want.test(s.name) : !/carbide|flex/i.test(s.name);
    });
  }

  /** A pick card: style, size, price, SKU link, minimum, and "Made in USA" when it is. */
  function bhCard(it) {
    return (
      '<div class="et-tool__sd-card" style="--c:' + esc(it.style.colour) + '">' +
      '<span class="et-tool__sd-meta">' + esc(it.style.name) + " &middot; " + esc(it.s) + " x " + it.len + " in.</span>" +
      '<span class="et-tool__sd-price">' + sdMoney(it.p) + "</span>" +
      '<a href="' + esc(it.href) + '">' + esc(it.sku) + "</a>" +
      '<span class="et-tool__sd-meta">' + (it.min > 1 ? "Each, min. " + it.min : "Each, no minimum") +
      (it.usa ? " &middot; Made in USA" : "") + "</span></div>"
    );
  }

  function bhSort(a, b) {
    return a.d - b.d || a.len - b.len || (b.usa ? 1 : 0) - (a.usa ? 1 : 0) || a.p - b.p;
  }

  // ---- (a) Route planner -------------------------------------------------

  function mountBhRoutePlanner(root) {
    root.hidden = true;
    var scope = sdScope(root);
    var stock = bhParseStock(scope);
    var routeTable = sdTableByHeader(scope, "Length at least");
    var pullTable = sdTableByHeader(scope, "What you pull");
    if (!stock || !routeTable || !pullTable) return;

    var cRoute = bhCol(routeTable, "Route");
    var cStart = bhCol(routeTable, "Starting point");
    var cThrough = bhCol(routeTable, "Drill through");
    var cReach = bhCol(routeTable, "Reach to the plate");
    var cBit = bhCol(routeTable, "Bit");
    var cLen = bhCol(routeTable, "Length at least");
    var cHole = bhCol(pullTable, "Bit diameter");
    if (cRoute < 0 || cStart < 0 || cBit < 0 || cLen < 0 || cHole < 0) return;

    var routes = [];
    var byName = {};
    for (var r = 1; r < routeTable.rows.length; r++) {
      var rc = routeTable.rows[r].cells;
      var need = parseInt((sdText(rc[cLen]).match(/\d+/) || [""])[0], 10);
      var name = sdText(rc[cRoute]);
      if (!name || !(need > 0)) continue;
      if (!byName[name]) {
        byName[name] = { name: name, starts: [] };
        routes.push(byName[name]);
      }
      byName[name].starts.push({
        label: sdText(rc[cStart]),
        through: cThrough >= 0 ? sdText(rc[cThrough]) : "",
        reach: cReach >= 0 ? sdText(rc[cReach]) : "",
        bit: sdText(rc[cBit]),
        need: need,
      });
    }
    var pulls = [];
    for (var p = 1; p < pullTable.rows.length; p++) {
      var pc = pullTable.rows[p].cells;
      var hole = (sdText(pc[cHole]).match(/^[\d\-\/]+/) || [""])[0];
      if (!hole || !(sdFrac(hole) > 0)) continue;
      pulls.push({ name: sdText(pc[0]), hole: hole, d: sdFrac(hole) });
    }
    if (!routes.length || !pulls.length) return;

    var state = { route: 0, start: 0, pull: Math.min(1, pulls.length - 1) };
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--bh-route-planner");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Route planner", "Where is the wire going?") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-r">Route</div>' +
      '<div class="et-tool__chips et-tool__bh-rail" role="group" aria-labelledby="' + uid + '-r" data-routes></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-w">Starting point</div>' +
      '<div class="et-tool__chips et-tool__bh-rail" role="group" aria-labelledby="' + uid + '-w" data-starts></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-p">What you pull</div>' +
      '<div class="et-tool__chips et-tool__bh-rail" role="group" aria-labelledby="' + uid + '-p" data-pulls></div></div>' +
      '<div class="et-tool__sd-readout" aria-live="polite" data-out></div>' +
      '<div class="et-tool__sd-cards" data-cards></div>' +
      '<div class="et-tool__bh-alts" data-alts></div>' +
      "</div></div>";

    var routesEl = root.querySelector("[data-routes]");
    var startsEl = root.querySelector("[data-starts]");
    var pullsEl = root.querySelector("[data-pulls]");
    var outEl = root.querySelector("[data-out]");
    var cardsEl = root.querySelector("[data-cards]");
    var altsEl = root.querySelector("[data-alts]");

    routes.forEach(function (rt, i) {
      routesEl.appendChild(sdChip(esc(rt.name), i === 0, function () {
        state.route = i;
        state.start = 0;
        drawStarts();
        render();
      }));
    });
    pulls.forEach(function (pl, i) {
      pullsEl.appendChild(sdChip(esc(pl.name), i === state.pull, function () {
        state.pull = i;
        render();
      }));
    });

    function drawStarts() {
      startsEl.innerHTML = "";
      routes[state.route].starts.forEach(function (st, i) {
        startsEl.appendChild(sdChip(esc(st.label), i === state.start, function () {
          state.start = i;
          render();
        }));
      });
    }

    function render() {
      Array.prototype.forEach.call(routesEl.children, function (b, i) { sdSetPressed(b, i === state.route); });
      Array.prototype.forEach.call(startsEl.children, function (b, i) { sdSetPressed(b, i === state.start); });
      Array.prototype.forEach.call(pullsEl.children, function (b, i) { sdSetPressed(b, i === state.pull); });

      var st = routes[state.route].starts[state.start];
      var pl = pulls[state.pull];
      var allowed = bhStylesFor(st.bit, stock.styles);
      var inStyle = stock.items.filter(function (it) { return allowed.indexOf(it.style) >= 0; });
      var fits = inStyle.filter(function (it) { return it.d >= pl.d - 1e-6 && it.len >= st.need; }).sort(bhSort);
      var why = esc(st.bit) + " &middot; at least " + esc(pl.hole) + " in. across for " + esc(pl.name) +
        " &middot; at least " + st.need + " in. long" +
        (st.reach && !/^[-–—]+$/.test(st.reach) ? " (" + esc(st.reach) + " of reach to the plate)" : "");

      if (!fits.length) {
        var longest = inStyle.reduce(function (m, it) { return Math.max(m, it.len); }, 0);
        outEl.innerHTML = sdReadout("No single bit", why);
        cardsEl.innerHTML = '<p class="et-tool__sd-note">' +
          (st.need > longest
            ? "That is longer than any bit of this kind we stock (" + longest + " in.). Drill from the other side, or start from an opening closer to the plate."
            : "Nothing of this kind is that wide. Pull one cable per hole, or drill from the other side.") + "</p>";
        altsEl.innerHTML = "";
        return;
      }

      var best = fits[0];
      var edge = (3.5 - best.d) / 2;
      outEl.innerHTML = sdReadout(esc(best.s) + " x " + best.len + " in.", why + ". " +
        (edge >= 1.25
          ? "Centered in a 2x4 plate, the hole leaves " + edge.toFixed(2) + " in. each side: no nail plate needed."
          : "In a 2x4 plate the hole edge comes within 1-1/4 in. of the face: add a steel nail plate."));
      cardsEl.innerHTML = bhCard(best);
      var alts = fits.slice(1, 4);
      altsEl.innerHTML = alts.length
        ? '<div class="et-tool__label">Also fits</div><div class="et-tool__sd-list">' + alts.map(function (it) {
          return '<div><span><a href="' + esc(it.href) + '">' + esc(it.sku) + "</a> " + esc(it.s) + " x " + it.len + " in. " +
            esc(it.style.name.toLowerCase()) + "</span><span>" + sdMoney(it.p) + (it.min > 1 ? " &times;" + it.min + " min." : "") + "</span></div>";
        }).join("") + "</div>"
        : "";
    }

    drawStarts();
    render();
    root.hidden = false;
  }

  // ---- (b) Bit rail ------------------------------------------------------

  function mountBhBitRail(root) {
    root.hidden = true;
    var stock = bhParseStock(sdScope(root));
    if (!stock) return;
    var styles = stock.styles;
    var items = stock.items;
    var on = styles.map(function () { return true; });
    var diams = [];
    var lens = [];
    items.forEach(function (it) {
      if (!diams.some(function (x) { return x.s === it.s; })) diams.push({ s: it.s, d: it.d });
      if (lens.indexOf(it.len) < 0) lens.push(it.len);
    });
    diams.sort(function (a, b) { return a.d - b.d; });
    lens.sort(function (a, b) { return a - b; });
    var sel = { s: null, len: null };

    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--bh-bit-rail");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Bit rail", items.length + " bits, every size and length we stock") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-st">Style</div>' +
      '<div class="et-tool__chips et-tool__bh-rail" role="group" aria-labelledby="' + uid + '-st" data-styles></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-d">Diameter</div>' +
      '<div class="et-tool__chips et-tool__bh-rail" role="group" aria-labelledby="' + uid + '-d" data-diams></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-l">Length</div>' +
      '<div class="et-tool__chips et-tool__bh-rail" role="group" aria-labelledby="' + uid + '-l" data-lens></div></div>' +
      '<div class="et-tool__bh-matrix" data-matrix></div>' +
      '<div class="et-tool__hint">Each square is the lowest price per bit at that size in the styles that are on. Tap one to list its bits.</div>' +
      '<div aria-live="polite" data-list></div>' +
      "</div></div>";

    var stylesEl = root.querySelector("[data-styles]");
    var diamsEl = root.querySelector("[data-diams]");
    var lensEl = root.querySelector("[data-lens]");
    var matrixEl = root.querySelector("[data-matrix]");
    var listEl = root.querySelector("[data-list]");

    styles.forEach(function (s, i) {
      var count = items.filter(function (it) { return it.style === s; }).length;
      var btn = sdChip('<span class="et-tool__sd-swatch" style="--c:' + esc(s.colour) + '"></span>' + esc(s.name) +
        ' <span class="et-tool__bh-count">' + count + "</span>", true, function () {
        if (on[i] && on.filter(Boolean).length === 1) return;
        on[i] = !on[i];
        sdSetPressed(btn, on[i]);
        draw();
      });
      stylesEl.appendChild(btn);
    });
    function railChip(el, label, value, key) {
      var btn = sdChip(esc(label), value === null, function () {
        sel[key] = value;
        draw();
      });
      btn.setAttribute("data-v", value === null ? "" : String(value));
      el.appendChild(btn);
    }
    railChip(diamsEl, "All", null, "s");
    diams.forEach(function (x) { railChip(diamsEl, x.s + " in.", x.s, "s"); });
    railChip(lensEl, "All", null, "len");
    lens.forEach(function (l) { railChip(lensEl, l + " in.", l, "len"); });

    function draw() {
      Array.prototype.forEach.call(diamsEl.children, function (b) {
        sdSetPressed(b, b.getAttribute("data-v") === (sel.s === null ? "" : sel.s));
      });
      Array.prototype.forEach.call(lensEl.children, function (b) {
        sdSetPressed(b, b.getAttribute("data-v") === (sel.len === null ? "" : String(sel.len)));
      });
      var live = items.filter(function (it) { return on[styles.indexOf(it.style)]; });

      var h = '<table><thead><tr><th scope="col">Dia. / length</th>';
      lens.forEach(function (l) { h += '<th scope="col"' + (sel.len === l ? ' class="is-sel"' : "") + ">" + l + "&quot;</th>"; });
      h += "</tr></thead><tbody>";
      diams.forEach(function (x) {
        h += "<tr><th scope=\"row\"" + (sel.s === x.s ? ' class="is-sel"' : "") + ">" + esc(x.s) + "&quot;</th>";
        lens.forEach(function (l) {
          var g = live.filter(function (it) { return it.s === x.s && it.len === l; });
          if (!g.length) {
            h += "<td></td>";
            return;
          }
          var lo = Math.min.apply(null, g.map(function (it) { return it.p; }));
          var exact = sel.s === x.s && sel.len === l;
          var dim = (sel.s !== null && sel.s !== x.s) || (sel.len !== null && sel.len !== l);
          h += '<td><button type="button" data-s="' + esc(x.s) + '" data-l="' + l + '" aria-pressed="' + (exact ? "true" : "false") + '"' +
            (dim ? ' class="is-dim"' : "") + ' aria-label="' + esc(x.s + " by " + l + " in., from " + sdMoney(lo) + ", " + g.length + " bits") + '">' +
            sdMoney(lo) + "<small>" + g.length + "</small></button></td>";
        });
        h += "</tr>";
      });
      matrixEl.innerHTML = h + "</tbody></table>";
      Array.prototype.forEach.call(matrixEl.querySelectorAll("button"), function (b) {
        b.addEventListener("click", function () {
          sel.s = b.getAttribute("data-s");
          sel.len = parseInt(b.getAttribute("data-l"), 10);
          draw();
          var again = matrixEl.querySelector('button[data-s="' + sel.s + '"][data-l="' + sel.len + '"]');
          if (again) again.focus();
        });
      });

      var list = live.filter(function (it) {
        return (sel.s === null || it.s === sel.s) && (sel.len === null || it.len === sel.len);
      }).sort(bhSort);
      var shown = list.slice(0, 40);
      listEl.innerHTML =
        '<div class="et-tool__label">' + list.length + " bit" + (list.length === 1 ? "" : "s") +
        (sel.s !== null ? " &middot; " + esc(sel.s) + " in." : "") + (sel.len !== null ? " &middot; " + sel.len + " in. long" : "") + "</div>" +
        (shown.length
          ? '<div class="et-tool__sd-list">' + shown.map(function (it) {
            return '<div><span><a href="' + esc(it.href) + '">' + esc(it.sku) + "</a> " + esc(it.s) + " x " + it.len + " in. " +
              esc(it.style.name.toLowerCase()) + (it.usa ? ", USA" : "") + "</span><span>" + sdMoney(it.p) +
              (it.min > 1 ? " min. " + it.min : "") + "</span></div>";
          }).join("") + "</div>"
          : '<p class="et-tool__sd-note">None of the styles that are on come in this size.</p>') +
        (list.length > shown.length ? '<p class="et-tool__sd-note">Pick a diameter or a length to see the rest.</p>' : "");
    }

    draw();
    root.hidden = false;
  }

  // ---- (c) Plate check ---------------------------------------------------

  /** 6 -> "3/8", 16 -> "1": a count of sixteenths of an inch as the fraction a tape reads. */
  function bhSixteenths(n) {
    var den = 16;
    while (n % 2 === 0 && den > 1) {
      n /= 2;
      den /= 2;
    }
    return den === 1 ? String(n) : n + "/" + den;
  }

  var BH_HOLES = ["3/16", "1/4", "5/16", "3/8", "7/16", "1/2", "5/8", "11/16", "3/4", "7/8", "1", "1-3/8", "1-3/4"];
  var BH_PLATES = [{ label: "2x4 plate", w: 3.5, txt: "3-1/2" }, { label: "2x6 plate", w: 5.5, txt: "5-1/2" }];

  function mountBhPlateCheck(root) {
    var state = { plate: 0, hole: 6, off: 0 };
    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--bh-plate-check");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Plate check", "Nail plate? Steel strap?") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-pl">Plate</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-pl" data-plates></div></div>' +
      '<label class="et-tool__slider-label">Hole diameter <output data-hole-out></output>' +
      '<input type="range" data-hole min="0" max="' + (BH_HOLES.length - 1) + '" step="1" value="' + state.hole + '" aria-label="Hole diameter"></label>' +
      '<label class="et-tool__slider-label">Off center (flex bits wander) <output data-off-out></output>' +
      '<input type="range" data-off min="0" max="16" step="1" value="0" aria-label="How far the hole is off center, in sixteenths of an inch"></label>' +
      '<div class="et-tool__sd-chart"><div data-chart></div></div>' +
      '<div class="et-tool__bh-verdicts" aria-live="polite" data-out></div>' +
      "</div></div>";

    var platesEl = root.querySelector("[data-plates]");
    var holeIn = root.querySelector("[data-hole]");
    var offIn = root.querySelector("[data-off]");
    var holeOut = root.querySelector("[data-hole-out]");
    var offOut = root.querySelector("[data-off-out]");
    var chartEl = root.querySelector("[data-chart]");
    var outEl = root.querySelector("[data-out]");

    BH_PLATES.forEach(function (pl, i) {
      platesEl.appendChild(sdChip(esc(pl.label + " (" + pl.txt + " in.)"), i === 0, function () {
        state.plate = i;
        render();
      }));
    });

    function render() {
      Array.prototype.forEach.call(platesEl.children, function (b, i) { sdSetPressed(b, i === state.plate); });
      var pl = BH_PLATES[state.plate];
      var W = pl.w;
      var holeTxt = BH_HOLES[state.hole];
      var d = sdFrac(holeTxt);
      var off = state.off / 16;
      holeOut.textContent = holeTxt + " in." + (d > 1 ? " (wider than any bit in this guide)" : "");
      offOut.textContent = state.off === 0 ? "centered" : bhSixteenths(state.off) + " in.";

      var edge = Math.min(W / 2 + off - d / 2, W / 2 - off - d / 2);
      var broken = edge <= 0;
      var nec = !broken && edge >= 1.25 - 1e-9;
      var strap = d / W > 0.5;

      var s = 600 / 5.5;
      var x0 = 60;
      var y0 = 46;
      var w = W * s;
      var h = 1.5 * s;
      var zone = 1.25 * s;
      var cx = x0 + (W / 2 + off) * s;
      var cy = y0 + h / 2;
      var g = "";
      g += '<rect x="' + x0 + '" y="' + y0 + '" width="' + sdSvgNum(w) + '" height="' + sdSvgNum(h) + '" fill="var(--ett-chip)" stroke="var(--ett-ink)" stroke-width="2"/>';
      g += '<rect x="' + x0 + '" y="' + y0 + '" width="' + sdSvgNum(zone) + '" height="' + sdSvgNum(h) + '" fill="var(--ett-bad)" fill-opacity="0.12"/>';
      g += '<rect x="' + sdSvgNum(x0 + w - zone) + '" y="' + y0 + '" width="' + sdSvgNum(zone) + '" height="' + sdSvgNum(h) + '" fill="var(--ett-bad)" fill-opacity="0.12"/>';
      g += '<text x="' + (x0 + 4) + '" y="' + (y0 - 8) + '" font-size="13" fill="var(--ett-bad)">1-1/4 in.</text>';
      g += '<text x="' + sdSvgNum(x0 + w - 4) + '" y="' + (y0 - 8) + '" font-size="13" text-anchor="end" fill="var(--ett-bad)">1-1/4 in.</text>';
      g += '<line x1="' + sdSvgNum(x0 + w / 2) + '" x2="' + sdSvgNum(x0 + w / 2) + '" y1="' + (y0 - 4) + '" y2="' + sdSvgNum(y0 + h + 4) + '" stroke="var(--ett-muted)" stroke-dasharray="4 3"/>';
      g += '<circle cx="' + sdSvgNum(cx) + '" cy="' + sdSvgNum(cy) + '" r="' + sdSvgNum(Math.max(d / 2 * s, 2)) + '" fill="var(--ett-cream)" stroke="' +
        (broken ? "var(--ett-bad)" : nec ? "var(--ett-good)" : "var(--ett-warn)") + '" stroke-width="3"><title>' + esc(holeTxt) + " in. hole</title></circle>";
      var dy = y0 + h + 26;
      g += '<line x1="' + x0 + '" x2="' + sdSvgNum(x0 + w) + '" y1="' + sdSvgNum(dy) + '" y2="' + sdSvgNum(dy) + '" stroke="var(--ett-ink)"/>';
      g += '<line x1="' + x0 + '" x2="' + x0 + '" y1="' + sdSvgNum(dy - 6) + '" y2="' + sdSvgNum(dy + 6) + '" stroke="var(--ett-ink)"/>';
      g += '<line x1="' + sdSvgNum(x0 + w) + '" x2="' + sdSvgNum(x0 + w) + '" y1="' + sdSvgNum(dy - 6) + '" y2="' + sdSvgNum(dy + 6) + '" stroke="var(--ett-ink)"/>';
      g += '<text x="' + sdSvgNum(x0 + w / 2) + '" y="' + sdSvgNum(dy + 20) + '" font-size="14" text-anchor="middle" fill="var(--ett-ink)">' + pl.txt + " in. plate, seen end-on</text>";
      var label = esc(pl.label + " with a " + holeTxt + " in. hole" + (off ? " " + offOut.textContent + " off center" : ", centered") + ": " +
        (broken ? "the hole breaks out the side" : "closest edge " + edge.toFixed(2) + " in. from the face") + ", " + Math.round((d / W) * 100) + "% of the width removed");
      chartEl.innerHTML = '<svg viewBox="0 0 720 ' + sdSvgNum(dy + 34) + '" role="img" aria-label="' + label + '">' + g + "</svg>";

      var v = "";
      v += broken
        ? '<p><span class="et-tool__bh-pill et-tool__bh-pill--bad">Breaks out</span> The hole leaves the side of the plate. Re-drill it closer to center.</p>'
        : nec
          ? '<p><span class="et-tool__bh-pill et-tool__bh-pill--good">No nail plate</span> The hole edge stays ' + edge.toFixed(2) + " in. from the face, at least the 1-1/4 in. NEC 300.4(A)(1) asks for.</p>"
          : '<p><span class="et-tool__bh-pill et-tool__bh-pill--warn">Nail plate</span> The hole edge is ' + edge.toFixed(2) + " in. from the face, under 1-1/4 in. Cover the cable with a steel plate at least 1/16 in. thick (NEC 300.4(A)(1)).</p>";
      v += strap
        ? '<p><span class="et-tool__bh-pill et-tool__bh-pill--bad">Steel strap</span> The hole takes ' + Math.round((d / W) * 100) + "% of the plate width, more than half. In an exterior or bearing wall, add a 16 ga. (0.054 in.) steel tie, 1-1/2 in. wide, 6 in. past each side (IRC R602.6.1).</p>"
        : '<p><span class="et-tool__bh-pill et-tool__bh-pill--good">No strap</span> The hole takes ' + Math.round((d / W) * 100) + "% of the plate width, half or less (IRC R602.6.1).</p>";
      outEl.innerHTML = v + '<p class="et-tool__hint">Check the code edition your inspector works from.</p>';
    }

    holeIn.addEventListener("input", function () {
      state.hole = parseInt(holeIn.value, 10) || 0;
      render();
    });
    offIn.addEventListener("input", function () {
      state.off = parseInt(offIn.value, 10) || 0;
      render();
    });
    render();
  }

  // ---------------------------------------------------------------------
  // Mount registry + init
  // ---------------------------------------------------------------------

  // To add a new tool: write a mount(root) function above, then add it here under its id. The id
  // must also be added to lib/blog/article-tools.ts or the sanitizer strips the marker before it
  // ever reaches this file.
  // ---------------------------------------------------------------------
  // Wheel fit finder (wheel-fit-finder), built 2026-11-02 for the guide "Angle Grinder Blades and Saw
  // Wheels: What Fits Which Tool" (roadmap row 19). Like the Silver & Deming tools it has NO data of
  // its own, no attributes and no fetch: it reads the article's fit chart (the table whose header row
  // has a cell reading exactly "Our wheels are rated") and nothing else. Columns 0-3 are Tool, Wheel
  // size, Arbor and the rating; every column after that is one material, and its cells are the picks.
  // A "—" cell means we stock nothing for that tool and material, and the chip is disabled.
  // ---------------------------------------------------------------------

  var WF_HEADER = "Our wheels are rated";
  var WF_RULE = 60000; // diameter (in.) x max RPM for a standard bonded wheel, ~80 m/s at the rim

  /** A picks cell split at ";" into items: [{ text, links: [{ sku, href }] }]. "Cut 11T042 $5.86; grind
   * 08H452 $6.98 (all box of 25)" -> two items, each with the link it holds. */
  function wfCellItems(cell) {
    var items = [{ text: "", links: [] }];
    (function walk(node) {
      Array.prototype.forEach.call(node.childNodes, function (n) {
        if (n.nodeType === 3) {
          var parts = n.nodeValue.split(";");
          parts.forEach(function (part, i) {
            if (i > 0) items.push({ text: "", links: [] });
            items[items.length - 1].text += part;
          });
        } else if (n.nodeType === 1 && n.tagName === "A") {
          var cur = items[items.length - 1];
          cur.text += sdText(n);
          cur.links.push({ sku: sdText(n), href: sdHref(n) });
        } else if (n.nodeType === 1) {
          walk(n);
        }
      });
    })(cell);
    return items
      .map(function (it) {
        var t = it.text.replace(/\s+/g, " ").trim();
        return { text: t.charAt(0).toUpperCase() + t.slice(1), links: it.links };
      })
      .filter(function (it) { return it.text; });
  }

  /** One item as a pick row: a link row when it names one product, plain text otherwise, with the
   * SKU set in the mono face like the other finders. */
  function wfPickHtml(it) {
    var html = esc(it.text);
    it.links.forEach(function (l) {
      html = html.replace(esc(l.sku), '<span class="et-tool__pick-sku">' + esc(l.sku) + "</span>");
    });
    if (it.links.length === 1) {
      return '<a class="et-tool__pick" href="' + esc(it.links[0].href) + '"><span>' + html + "</span></a>";
    }
    var inner = esc(it.text);
    it.links.forEach(function (l) {
      inner = inner.replace(esc(l.sku), '<a class="et-tool__pick-sku" href="' + esc(l.href) + '">' + esc(l.sku) + "</a>");
    });
    return '<div class="et-tool__pick"><span>' + inner + "</span></div>";
  }

  function wfBlank(text) {
    return !text || /^[-–—−]+$/.test(text);
  }

  /** "4-1/2 in." -> 4.5, "14 in." -> 14. NaN when the size cell does not start with a number. */
  function wfInches(text) {
    var m = String(text).match(/^\s*(\d+(?:-\d+\/\d+)?|\d+\/\d+)/);
    return m ? sdFrac(m[1]) : NaN;
  }

  /** The first RPM figure in the rating cell: "8,600 RPM (diamond 8,730)" -> 8600. */
  function wfRpm(text) {
    var m = String(text).replace(/,/g, "").match(/(\d{3,6})/);
    return m ? Number(m[1]) : NaN;
  }

  /** One plain sentence comparing the rating with the 60,000 rule for that diameter. */
  function wfRuleLine(dia, rpm) {
    if (!(dia > 0) || !(rpm > 0)) return "";
    var guess = Math.round(WF_RULE / dia / 10) * 10;
    var ratio = (dia * rpm) / WF_RULE;
    var head = "60,000 ÷ " + sfFmt(dia) + " ≈ " + sfFmt(guess) + " RPM. ";
    if (ratio >= 0.95 && ratio <= 1.05) return head + "Our wheels for this tool sit right on the rule.";
    if (ratio > 1.05) return head + "These wheels are built for a faster rim speed than standard, so trust the label, not the rule.";
    return head + "This is a low-speed blade. Never fit it to a tool that runs at the rule's speed.";
  }

  function mountWheelFitFinder(root) {
    root.hidden = true; // stays hidden unless the finder is actually drawn
    var table = sdTableByHeader(sdScope(root), WF_HEADER);
    if (!table || table.rows.length < 3) return;

    var head = Array.prototype.map.call(table.rows[0].cells, sdText);
    if (head.length < 5) return;
    var mats = head.slice(4).map(function (name, i) { return { key: i + 4, label: name }; });

    var tools = [];
    for (var r = 1; r < table.rows.length; r++) {
      var cells = table.rows[r].cells;
      if (cells.length < head.length) continue;
      var size = sdText(cells[1]);
      var rating = sdText(cells[3]);
      tools.push({
        name: sdText(cells[0]),
        size: size,
        arbor: sdText(cells[2]),
        rating: rating,
        dia: wfInches(size),
        rpm: wfRpm(rating),
        picks: mats.map(function (m) {
          return wfBlank(sdText(cells[m.key])) ? null : wfCellItems(cells[m.key]);
        }),
      });
    }
    if (!tools.length) return;

    var start = 0;
    tools.forEach(function (t, i) { if (/4-1\/2/.test(t.name) && !start) start = i; });
    var state = { tool: start, mat: 0 };

    var uid = "et-tool-" + Math.random().toString(36).slice(2, 8);
    root.classList.add("et-tool--wheel-fit-finder");
    root.innerHTML =
      '<div class="et-tool__finder">' + sdHead("Wheel fit finder", "Reads the fit chart in this guide") +
      '<div class="et-tool__body">' +
      '<div><div class="et-tool__label" id="' + uid + '-t">Your tool</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-t" data-tools></div></div>' +
      '<div><div class="et-tool__label" id="' + uid + '-m">What you are cutting</div>' +
      '<div class="et-tool__chips" role="group" aria-labelledby="' + uid + '-m" data-mats></div></div>' +
      '<div class="et-tool__sd-readout" aria-live="polite" data-out></div>' +
      '<div class="et-tool__result" data-result></div>' +
      "</div></div>";

    var toolsEl = root.querySelector("[data-tools]");
    var matsEl = root.querySelector("[data-mats]");
    var outEl = root.querySelector("[data-out]");
    var resultEl = root.querySelector("[data-result]");

    tools.forEach(function (t, i) {
      var btn = sdChip(esc(t.name), i === state.tool, function () {
        state.tool = i;
        if (!tools[i].picks[state.mat]) {
          state.mat = 0;
          for (var k = 0; k < tools[i].picks.length; k++) {
            if (tools[i].picks[k]) { state.mat = k; break; }
          }
        }
        render();
      });
      toolsEl.appendChild(btn);
    });
    mats.forEach(function (m, i) {
      var btn = sdChip(esc(m.label), i === state.mat, function () {
        state.mat = i;
        render();
      });
      matsEl.appendChild(btn);
    });

    function render() {
      var t = tools[state.tool];
      Array.prototype.forEach.call(toolsEl.children, function (btn, i) { sdSetPressed(btn, i === state.tool); });
      Array.prototype.forEach.call(matsEl.children, function (btn, i) {
        sdSetPressed(btn, i === state.mat);
        btn.disabled = !t.picks[i];
      });

      outEl.innerHTML = sdReadout(
        esc(t.size) + " wheel",
        esc(t.arbor) + " arbor · rated " + esc(t.rating) + " or faster than your tool"
      );

      var pick = t.picks[state.mat];
      resultEl.innerHTML =
        '<p class="et-tool__verdict">' + esc(mats[state.mat].label) + " on a " + esc(t.name.toLowerCase()) + "</p>" +
        (pick
          ? '<div class="et-tool__picks">' + pick.map(wfPickHtml).join("") + "</div>"
          : '<p class="et-tool__warn">We do not stock a wheel for this. Call (833) 4-ETOOLS and we will find one.</p>') +
        '<p class="et-tool__why">' + esc(wfRuleLine(t.dia, t.rpm)) + "</p>";
    }

    render();
    root.hidden = false;
  }

  var TOOLS = {
    "blade-finder": mountBladeFinder,
    "teeth-explorer": mountTeethExplorer,
    "bit-length-finder": mountBitLengthFinder,
    "speed-calculator": mountSpeedCalculator,
    "band-saw-finder": mountBandSawFinder,
    "shank-chooser": mountShankChooser,
    "versus-card": mountVersusCard,
    "hole-explorer": mountHoleExplorer,
    "speed-fit": mountSpeedFit,
    "sd-bolt-finder": mountSdBoltFinder,
    "sd-price-ladder": mountSdPriceLadder,
    "sd-speed-dial": mountSdSpeedDial,
    "sd-kit-builder": mountSdKitBuilder,
    chart: mountChart,
    "bh-route-planner": mountBhRoutePlanner,
    "bh-bit-rail": mountBhBitRail,
    "bh-plate-check": mountBhPlateCheck,
    "wheel-fit-finder": mountWheelFitFinder,
  };

  function mountAll() {
    var nodes = document.querySelectorAll(".et-tool[data-tool]");
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.dataset.etMounted) return;
      var id = el.getAttribute("data-tool");
      var mount = TOOLS[id];
      if (!mount) return; // unrecognized id — leave the placeholder empty, nothing to render
      el.dataset.etMounted = "true";
      try {
        mount(el);
      } catch (e) {
        console.warn("[et-article-tools] tool \"" + id + "\" failed to mount:", e);
        el.innerHTML = "";
        el.hidden = true;
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mountAll);
  } else {
    mountAll();
  }
})();
